import { useState, useEffect, useRef, useMemo, Children } from 'react';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  ListItemButton,
  Button,
  Card as MuiCard,
  CardContent,
  Stack,
  Avatar as MuiAvatar,
  SvgIcon,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faClock,
  faPlane,
  faBriefcase,
  faChartLine,
  faWallet,
  faFileAlt,
  faChartBar,
  faCog,
  faBell,
  faSearch,
  faChevronRight,
  faRobot,
  faPaperPlane,
  faTimes,
  faPlus,
  faCheck,
  faBuilding,
  faAward,
  faSignOutAlt,
  faBook,
  faShieldAlt,
  faSparkles,
  faArrowRight,
  faArrowLeft,
  faDownload,
  faUser,
  faStar,
  faMapPin,
  faEnvelope,
  faPhone,
  faPencil,
  faAlertTriangle,
  faHashtag,
  faCalendar,
  faGraduationCap,
  faUserPlus,
  faComment,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  Wallet,
  Users,
  ShieldCheck,
  Plus,
  Check,
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  GraduationCap,
  Clock,
  X,
  CalendarOff,
  CircleUser,
  Sparkles,
  TrendingUp,
  TrendingDown,
  LayoutDashboard,
  FileText,
  BarChart3,
  Home,
  BookOpen,
  UserPlus,
  AlertTriangle,
  Settings,
  Search,
  Bell,
  Send,
  Hash,
  Calendar,
  Pencil,
  CornerDownLeft,
  ChevronRight,
  MessageSquare,
  LogOut,
  Bot,
  Download,
  Star,
  PanelLeft,
  UserCog,
  SlidersHorizontal,
  Award,
  Menu as MenuIcon,
  Trash2,
  Upload,
  Activity,
  MoreVertical,
  Power,
} from 'lucide-react';
import { api } from './api';
import EmployeesDataGrid from './EmployeesDataGrid';
import { EntityGlyph } from './EntitySwitcher';
import ApprovalsInbox from './ApprovalsInbox';
import ConfigAdmin from './ConfigAdmin';
import TalentPool from './TalentPool';
import JobScreening from './JobScreening';
import InterviewEval from './InterviewEval';
import OnboardingSurveys from './OnboardingSurveys';
import Letters from './Letters';
import OrgChart from './OrgChart';
import SalaryStructure from './SalaryStructure';
import SettlementCard from './SettlementCard';
import OvertimeCard from './OvertimeCard';
import AdjustmentsCard from './AdjustmentsCard';
import TrainingAdmin from './TrainingAdmin';
import CompetencyAdmin from './CompetencyAdmin';
import Feedback360Admin from './Feedback360Admin';
import Recognition from './Recognition';
import Awards from './Awards';
import Pulse from './Pulse';
import Cases from './Cases';
import Insights from './Insights';
import Announcements from './Announcements';
import Safety from './Safety';
import Benefits from './Benefits';
import Offboarding from './Offboarding';
import Disciplinary from './Disciplinary';
import Succession from './Succession';
import Loans from './Loans';
import JdLibrary from './JdLibrary';
import PerformanceCycles from './PerformanceCycles';
import { IMAGE_ACCEPT, MAX_IMAGE_BYTES, downscaleSquareDataUrl } from './lib/image';
import AppDataGrid, {
  DataGridCard,
  DataGridPage,
  PersonCell,
  TextCell,
  GridStatusPill,
  useAttendanceColumns,
  useHistoryColumns,
} from './AppDataGrid';
import { useAuth } from './auth';
import { useAppSettings } from './SettingsContext';
import { buildPath, parsePath } from './routing';
import {
  StatusPill,
  UserAvatar,
  Header,
  SectionHeader,
  GridLayout,
  EmptyState,
} from './components';
import { FONT, AG, PIE_COLORS, STATUS_COLOR, statusTone } from './theme/tokens';
import {
  initials,
  empEmail,
  tenure,
  fmtPKR,
  fmtM,
  fmtMoney,
  greeting,
} from './lib/format';
import { STAGES, JOB_STAGES, jobTone, jobName } from './lib/ats';
import { printPayDoc, docRow, printReport } from './lib/print';
import {
  OKR_RATINGS,
  RATING_NUM,
  ratingTone,
  okrStatusTone,
  rollupStatusTone,
  okrTabBtn,
  okrLbl,
  weightedProgress,
  avgRatingLabel,
  okrsByEmployee,
} from './lib/okr';
import {
  Pill,
  Avatar,
  AvatarEditor,
  AccountButton,
  Card,
  DashboardWidget,
  WidgetGrid,
  KPI,
  CardSectionHeader,
  ChartCard,
  SectionTitle,
  ChartTip,
  CenterSpinner,
  PrimaryBtn,
  OutlineBtn,
  PageScroll,
  PTh,
  PTd,
  PerfTable,
  DrawerFormRow,
  DrawerSection,
  DrawerTextField,
  DRAWER_TABLE_SX,
} from './ui';

// Engineer-ladder titles, in the order HR requested. Used for both Job title
// and Grade / level.
const JOB_LEVELS = [
  'Principal Software Engineer',
  'Senior Software Engineer',
  'Software Engineer',
  'Analyst Software Engineer',
  'MTO',
];
// Group legal entities (OpCos) employees can belong to.
const LEGAL_ENTITIES = ['Fieldforce', 'GTA', 'Augment', 'DIH'];
// Salary currencies — code + readable label.
const SALARY_CURRENCIES = [
  ['PKR', 'PKR · Pakistani Rupee'],
  ['USD', 'USD · US Dollar'],
  ['EUR', 'EUR · Euro'],
  ['GBP', 'GBP · British Pound'],
  ['AED', 'AED · UAE Dirham'],
  ['SAR', 'SAR · Saudi Riyal'],
  ['INR', 'INR · Indian Rupee'],
  ['CAD', 'CAD · Canadian Dollar'],
  ['AUD', 'AUD · Australian Dollar'],
  ['CHF', 'CHF · Swiss Franc'],
  ['SGD', 'SGD · Singapore Dollar'],
  ['JPY', 'JPY · Japanese Yen'],
  ['CNY', 'CNY · Chinese Yuan'],
];

function EmployeeModal({
  mode = 'add',
  initial,
  employees = [],
  orgTree = [],
  activeEntity = 'all',
  onClose,
  onSubmit,
}) {
  const isEdit = mode === 'edit';
  const [f, setF] = useState({
    name: initial?.name || '',
    role: initial?.role || JOB_LEVELS[0],
    dept: initial?.dept || 'Engineering',
    managerId: initial?.managerId || '',
    type: initial?.type || 'Full-time',
    status: initial?.status || (isEdit ? 'Active' : 'Probation'),
    joined: initial?.joined || '',
    location: initial?.location || '',
    grade: initial?.grade || JOB_LEVELS[0],
    phone: initial?.phone || '',
    maritalStatus: initial?.maritalStatus || 'Single',
    nationalId: initial?.nationalId || '',
    personalEmail: initial?.personalEmail || '',
    workEmail: initial?.email || '',
    emergencyName: initial?.emergencyName || '',
    emergencyPhone: initial?.emergencyPhone || '',
    salary: initial?.salary != null ? String(initial.salary) : '',
    salaryCurrency: initial?.salaryCurrency || 'PKR',
    dateOfConfirmation: initial?.dateOfConfirmation || '',
    gender: initial?.gender || 'Male',
    dateOfBirth: initial?.dateOfBirth || '',
    marriageDate: initial?.marriageDate || '',
    qualification: initial?.qualification || '',
    currentAddress: initial?.currentAddress || '',
    permanentAddress: initial?.permanentAddress || '',
    pastExperience: initial?.pastExperience || '',
    guardianName: initial?.guardianName || '',
    bankAccount: initial?.bankAccount || '',
    legalEntity: initial?.legalEntity || LEGAL_ENTITIES[0],
    // Org-scope entity (Company → Group → Entity). Defaults to the active entity;
    // chosen on add, fixed on edit (the backend doesn't reassign entity here).
    entityId: initial?.entityId || (activeEntity && activeEntity !== 'all' ? activeEntity : ''),
    password: '', // HR never sets passwords on add (invite flow); edit = blank keeps current
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (ev) => setF((s) => ({ ...s, [k]: ev.target.value }));

  // Full required set on creation; relaxed on edit (existing records may lack fields).
  const required = isEdit
    ? ['name', 'role', 'dept', 'joined']
    : [
        'name',
        'role',
        'dept',
        'managerId',
        'type',
        'status',
        'joined',
        'dateOfConfirmation',
        'phone',
        'maritalStatus',
        'nationalId',
        'personalEmail',
        'workEmail',
        'gender',
        'dateOfBirth',
        'qualification',
        'salary',
        'entityId',
      ];
  // A wedding date is required once the employee is marked Married.
  if (f.maritalStatus === 'Married') required.push('marriageDate');
  const missing = required.filter((k) => !String(f[k]).trim());
  // Phone is optional, but if a real number is entered it must be valid.
  const phoneEntered = (f.phone || '').replace(/\D/g, '').length > 3;
  const phoneOk = !phoneEntered || matchIsValidTel(f.phone);
  const valid = missing.length === 0 && phoneOk;
  const deptOptions = Array.from(
    new Set([
      ...employees.map((e) => e.dept),
      'Engineering',
      'Product',
      'Design',
      'People',
      'Sales',
      'Finance',
      'Marketing',
      'Data',
      'Leadership',
    ]),
  );
  // Designated managers (explicit isManager flag) or anyone who already has a
  // direct report — excluding the employee being edited.
  const managerIds = new Set(employees.map((e) => e.managerId).filter(Boolean));
  const managers = employees.filter(
    (e) => (e.isManager || managerIds.has(e.id)) && e.id !== initial?.id,
  );

  const submit = async () => {
    if (!valid || busy) return;
    setErr('');
    setBusy(true);
    const payload = {
      name: f.name.trim(),
      role: f.role.trim(),
      dept: f.dept,
      managerId: f.managerId || null,
      type: f.type,
      status: f.status,
      joined: f.joined,
      location: f.location.trim(),
      grade: f.grade.trim(),
      phone: f.phone.trim(),
      maritalStatus: f.maritalStatus,
      nationalId: f.nationalId.trim(),
      personalEmail: f.personalEmail.trim(),
      email: f.workEmail.trim(),
      emergencyName: f.emergencyName.trim(),
      emergencyPhone: f.emergencyPhone.trim(),
      salary: f.salary,
      salaryCurrency: f.salaryCurrency,
      dateOfConfirmation: f.dateOfConfirmation,
      gender: f.gender,
      dateOfBirth: f.dateOfBirth,
      marriageDate: f.maritalStatus === 'Married' ? f.marriageDate : null,
      qualification: f.qualification.trim(),
      currentAddress: f.currentAddress.trim(),
      permanentAddress: f.permanentAddress.trim(),
      pastExperience: f.pastExperience.trim(),
      guardianName: f.guardianName.trim(),
      bankAccount: f.bankAccount.trim(),
      legalEntity: f.legalEntity.trim(),
      entityId: f.entityId || null, // org scope; ignored by the edit endpoint
    };
    if (f.password.trim()) payload.password = f.password.trim(); // blank on edit = keep current
    try {
      await onSubmit(payload); // add → addEmployee(payload); edit → saveEdits(payload)
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Something went wrong');
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={{ flexShrink: 0, px: 2, py: 2 }}
      >
        <DialogTitle sx={{ p: 0, flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Employees
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
          >
            {isEdit ? 'Edit employee' : 'Add a new employee'}
          </Typography>
        </DialogTitle>
        <DialogActions sx={{ p: 0, flexShrink: 0 }}>
          <Button onClick={onClose} variant="text" sx={{ color: 'inherit' }}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            disabled={!valid || busy}
            startIcon={isEdit ? <Check size={16} /> : <UserPlus size={16} />}
          >
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </Button>
        </DialogActions>
      </Stack>

      <DialogContent
        dividers
        sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2 }}
      >
        {err && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.light', color: 'error.dark', fontSize: 13, borderRadius: 2, fontWeight: 500, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            {err}
          </Box>
        )}
        <Table
          className="editable-tasks-drawer noRowBorders standardPadding"
          sx={DRAWER_TABLE_SX}
        >
          <TableBody>
            <DrawerFormRow label="Full name" req>
              <DrawerTextField
                value={f.name}
                onChange={set('name')}
                placeholder="e.g. Jordan Carter"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Job title / role" req>
              <DrawerTextField select native value={f.role} onChange={set('role')}>
                {JOB_LEVELS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Department" req>
              <DrawerTextField select native value={f.dept} onChange={set('dept')}>
                {deptOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Legal entity">
              <DrawerTextField select native value={f.legalEntity} onChange={set('legalEntity')}>
                {LEGAL_ENTITIES.map((le) => (
                  <option key={le} value={le}>
                    {le}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            {!isEdit && (
              <DrawerFormRow label="Entity" req>
                <DrawerTextField select native value={f.entityId} onChange={set('entityId')}>
                  <option value="">— Select an entity —</option>
                  {orgTree.flatMap((c) =>
                    (c.groups || []).map((g) => (
                      <optgroup key={g.id} label={g.name}>
                        {(g.entities || []).map((ent) => (
                          <option key={ent.id} value={ent.id}>
                            {ent.name}
                          </option>
                        ))}
                      </optgroup>
                    )),
                  )}
                </DrawerTextField>
              </DrawerFormRow>
            )}
            <DrawerFormRow label="Reporting manager" req={!isEdit}>
              <DrawerTextField
                select
                native
                value={f.managerId}
                onChange={set('managerId')}
              >
                <option value="">— None (reports to HR) —</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.role ? ` · ${m.role}` : ''}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Employment type" req={!isEdit}>
              <DrawerTextField select native value={f.type} onChange={set('type')}>
                {['Full-time', 'Part-time', 'Contract', 'Internship'].map(
                  (t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ),
                )}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Status" req={!isEdit}>
              <DrawerTextField select native value={f.status} onChange={set('status')}>
                {['Active', 'Probation', 'On Notice'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Date of joining" req>
              <DrawerTextField
                type="date"
                value={f.joined}
                onChange={set('joined')}
                InputLabelProps={{ shrink: true }}
              />
            </DrawerFormRow>
            <DrawerFormRow label="Date of confirmation" req={!isEdit}>
              <DrawerTextField
                type="date"
                value={f.dateOfConfirmation}
                onChange={set('dateOfConfirmation')}
                InputLabelProps={{ shrink: true }}
              />
            </DrawerFormRow>
            <DrawerFormRow label="Work location">
              <DrawerTextField
                value={f.location}
                onChange={set('location')}
                placeholder="e.g. New York / Remote"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Grade / level">
              <DrawerTextField select native value={f.grade} onChange={set('grade')}>
                {JOB_LEVELS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Phone" req={!isEdit}>
              <MuiTelInput
                value={f.phone}
                onChange={(value) => setF((s) => ({ ...s, phone: value }))}
                defaultCountry="US"
                fullWidth
                size="small"
                error={phoneEntered && !phoneOk}
                helperText={
                  phoneEntered && !phoneOk ? 'Enter a valid phone number' : ''
                }
              />
            </DrawerFormRow>
            <DrawerFormRow label="Marital status" req={!isEdit}>
              <DrawerTextField
                select
                native
                value={f.maritalStatus}
                onChange={set('maritalStatus')}
              >
                {['Single', 'Married', 'Divorced', 'Widowed', 'Other'].map(
                  (m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ),
                )}
              </DrawerTextField>
            </DrawerFormRow>
            {f.maritalStatus === 'Married' && (
              <DrawerFormRow label="Date of marriage" req={!isEdit}>
                <DrawerTextField
                  type="date"
                  value={f.marriageDate}
                  onChange={set('marriageDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </DrawerFormRow>
            )}
            <DrawerFormRow label="National ID" req={!isEdit}>
              <DrawerTextField
                value={f.nationalId}
                onChange={set('nationalId')}
                placeholder="e.g. SSN / National ID"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Gender" req={!isEdit}>
              <DrawerTextField
                select
                native
                value={f.gender}
                onChange={set('gender')}
              >
                {['Male', 'Female'].map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Date of birth" req={!isEdit}>
              <DrawerTextField
                type="date"
                value={f.dateOfBirth}
                onChange={set('dateOfBirth')}
                InputLabelProps={{ shrink: true }}
              />
            </DrawerFormRow>
            <DrawerFormRow label="Father / Husband name">
              <DrawerTextField
                value={f.guardianName}
                onChange={set('guardianName')}
                placeholder="e.g. Robert Carter"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Qualification" req={!isEdit}>
              <DrawerTextField
                value={f.qualification}
                onChange={set('qualification')}
                placeholder="e.g. BSc Computer Science"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Past experience">
              <DrawerTextField
                value={f.pastExperience}
                onChange={set('pastExperience')}
                placeholder="e.g. 3 yrs at Acme Corp"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Personal email" req={!isEdit}>
              <DrawerTextField
                type="email"
                value={f.personalEmail}
                onChange={set('personalEmail')}
              />
            </DrawerFormRow>
            <DrawerFormRow label="Work email" req={!isEdit}>
              <DrawerTextField
                type="email"
                value={f.workEmail}
                onChange={set('workEmail')}
              />
            </DrawerFormRow>
            <DrawerSection title="Address" />
            <DrawerFormRow label="Current address">
              <DrawerTextField
                value={f.currentAddress}
                onChange={set('currentAddress')}
                placeholder="House, street, city"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Permanent address">
              <DrawerTextField
                value={f.permanentAddress}
                onChange={set('permanentAddress')}
                placeholder="House, street, city"
              />
            </DrawerFormRow>
            <DrawerSection title="Emergency contact" />
            <DrawerFormRow label="Name">
              <DrawerTextField
                value={f.emergencyName}
                onChange={set('emergencyName')}
                placeholder="e.g. Jamie Carter"
              />
            </DrawerFormRow>
            <DrawerFormRow label="Contact number">
              <MuiTelInput
                value={f.emergencyPhone}
                onChange={(value) =>
                  setF((s) => ({ ...s, emergencyPhone: value }))
                }
                defaultCountry="US"
                fullWidth
                size="small"
              />
            </DrawerFormRow>
            <DrawerSection title="Compensation & access" />
            <DrawerFormRow label="Currency" req={!isEdit}>
              <DrawerTextField select native value={f.salaryCurrency} onChange={set('salaryCurrency')}>
                {SALARY_CURRENCIES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </DrawerTextField>
            </DrawerFormRow>
            <DrawerFormRow label="Monthly gross salary" req={!isEdit}>
              <DrawerTextField
                type="text"
                inputMode="numeric"
                value={f.salary}
                onChange={set('salary')}
                placeholder="e.g. 6500"
                inputProps={{ style: { MozAppearance: 'textfield' } }}
                sx={{ '& input[type=number]': { MozAppearance: 'textfield' }, '& input::-webkit-outer-spin-button,& input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
              />
            </DrawerFormRow>
            <DrawerFormRow label="Bank account">
              <DrawerTextField
                value={f.bankAccount}
                onChange={set('bankAccount')}
                placeholder="Account number / IBAN"
              />
            </DrawerFormRow>
            {/* Invite flow: HR doesn't set passwords on add. Kept on EDIT only as a
                manual fallback (e.g. before SES is live). */}
            {isEdit && (
              <DrawerFormRow label="Login password">
                <DrawerTextField
                  type="password"
                  value={f.password}
                  onChange={set('password')}
                  placeholder="Leave blank to keep current password"
                />
              </DrawerFormRow>
            )}
          </TableBody>
        </Table>
        {!isEdit && (
          <Box sx={{ mt: 2, fontSize: 12.5, lineHeight: 1.5, color: "text.secondary", bgcolor: "action.hover", borderRadius: 2, p: 1.5 }}>
            {"An invite to "}<b>{"set their own password"}</b>{" will be emailed to the employee — you don’t set or see their password."}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography
          variant="caption"
          color={valid ? 'success.main' : 'text.secondary'}
        >
          {valid
            ? 'Ready to save'
            : `${missing.length} required field${missing.length > 1 ? 's' : ''} left`}
        </Typography>
      </Box>
    </Dialog>
  );
}

// Additional concurrent positions (Phase 2) — the primary role stays on the
// employee record + org tree; these are extra appointments with FTE allocation.
function PositionsCard({ employeeId, isHR, employees = [], primaryRole, showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.positions(employeeId).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const blank = { title: '', dept: '', managerId: '', allocationPct: '20', startDate: new Date().toISOString().slice(0, 10), endDate: '', note: '' };
  const save = async () => {
    if (!form.title.trim() || !form.startDate || busy) return; setBusy(true);
    try {
      const payload = { ...form, managerId: form.managerId || null, endDate: form.endDate || null };
      if (form.id) await api.updatePosition(employeeId, form.id, payload); else await api.addPosition(employeeId, payload);
      showToast?.('Position saved'); setForm(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const end = async (p) => { try { await api.updatePosition(employeeId, p.id, { status: 'Ended', endDate: p.endDate || new Date().toISOString().slice(0, 10) }); load(); } catch (e) { showToast?.(e.message); } };
  const remove = async (p) => { try { await api.deletePosition(employeeId, p.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return null;
  // "Allocated now" mirrors the backend's active-today rule (status + date window),
  // so the indicator matches the cap the server enforces.
  const todayStr = new Date().toISOString().slice(0, 10);
  const isActiveNow = (p) => p.status === 'Active' && p.startDate <= todayStr && (!p.endDate || p.endDate >= todayStr);
  const activeNow = rows.filter(isActiveNow);
  const allocated = activeNow.reduce((a, p) => a + (p.allocationPct || 0), 0);
  return (
    <Card style={{ padding: 24 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: AG.n400 }}>Additional positions</p>
          <p style={{ fontSize: 12.5, color: AG.n500, marginTop: 4 }}>Primary: <b style={{ color: AG.text }}>{primaryRole}</b> · {rows.length} additional {rows.length === 1 ? 'position' : 'positions'}{activeNow.length ? ` · ${allocated}% allocated now` : ''}</p>
        </div>
        {isHR && <OutlineBtn onClick={() => setForm({ ...blank })}><Plus size={14} style={{ marginRight: 6 }} /> Add position</OutlineBtn>}
      </div>
      {rows.length === 0 ? (
        <p style={{ fontSize: 13, color: AG.n500 }}>No additional positions. The employee holds only their primary role.</p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((p) => (
            <div key={p.id} className="flex items-center justify-between" style={{ padding: '10px 12px', border: `1px solid ${AG.divider}`, borderRadius: 10, gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: AG.text }}>{p.title} <Pill tone={p.status === 'Active' ? 'success' : 'neutral'}>{p.status}</Pill> {p.allocationPct ? <Pill tone="info">{p.allocationPct}%</Pill> : null}</div>
                <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{p.dept || '—'}{p.managerName ? ` · reports to ${p.managerName}` : ''} · from {p.startDate}{p.endDate ? ` to ${p.endDate}` : ''}{p.note ? ` · ${p.note}` : ''}</div>
              </div>
              {isHR && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ ...p, managerId: p.managerId || '', dept: p.dept || '', endDate: p.endDate || '', note: p.note || '', allocationPct: String(p.allocationPct) })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Pencil size={15} /></button>
                  {p.status === 'Active' && <OutlineBtn onClick={() => end(p)}>End</OutlineBtn>}
                  <button onClick={() => remove(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit position' : 'Add position'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <TextField size="small" label="Title" value={form.title} onChange={(ev) => setForm({ ...form, title: ev.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <TextField size="small" label="Department" value={form.dept} onChange={(ev) => setForm({ ...form, dept: ev.target.value })} placeholder="(optional)" />
                <TextField size="small" type="number" label="Allocation %" value={form.allocationPct} onChange={(ev) => setForm({ ...form, allocationPct: ev.target.value })} inputProps={{ min: 0, max: 100 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Manager (optional)</label>
                <select className="ag-input" style={{ marginTop: 4 }} value={form.managerId} onChange={(ev) => setForm({ ...form, managerId: ev.target.value })}>
                  <option value="">— None —</option>
                  {employees.filter((x) => x.id !== employeeId).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(ev) => setForm({ ...form, startDate: ev.target.value })} />
                <TextField size="small" type="date" label="End (optional)" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(ev) => setForm({ ...form, endDate: ev.target.value })} />
              </div>
              <TextField size="small" label="Note (optional)" value={form.note} onChange={(ev) => setForm({ ...form, note: ev.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy || !form?.title?.trim()}>Save</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function EmployeeDetail({
  id,
  onBack,
  isHR,
  employees = [],
  updateEmployee,
  promoteToManager,
  removeAsManager,
  showToast,
}) {
  const [e, setE] = useState(null);
  const empDetailTabs = ['Overview', 'Documents', 'Attendance', 'Leave', 'Performance'];
  const [tab, setTab] = useState(() => tabFromHash(window.location.hash, empDetailTabs));
  const changeEmpTab = (t) => { setTab(t); window.history.replaceState(null, '', window.location.pathname + (t === empDetailTabs[0] ? '' : '#' + tabSlug(t))); };
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [mgrAction, setMgrAction] = useState(null); // null | 'promote' | 'remove'
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState('Other');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileInputRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [docMeta, setDocMeta] = useState(null);   // { doc, visibility, expiresAt } — metadata edit
  const [docHistory, setDocHistory] = useState(null); // { doc, versions }
  const [docBusy, setDocBusy] = useState(false);
  const historyColumns = useHistoryColumns();
  const attRows = useMemo(
    () => (e?.attendance || []).map((h, i) => ({ id: i, ...h })),
    [e],
  );
  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .employeeFull(id)
      .then((d) => {
        if (active) {
          setE(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  // Save edits through the parent handler, then refresh this detail's data.
  const saveEdits = async (data) => {
    await updateEmployee(id, data);
    const fresh = await api.employeeFull(id);
    setE(fresh);
  };
  const refresh = async () => setE(await api.employeeFull(id));
  const doPromote = async () => {
    await promoteToManager(id);
    await refresh();
    setMgrAction(null);
  };
  const doRemove = async (newManagerId) => {
    await removeAsManager(id, newManagerId);
    await refresh();
    setMgrAction(null);
  };
  // Direct reports + the pool of managers a team can be handed to (from the directory).
  const myReports = employees.filter((x) => x.managerId === id);
  const managerIds = new Set(employees.map((x) => x.managerId).filter(Boolean));
  const otherManagers = employees.filter(
    (x) =>
      x.id !== id &&
      x.status !== 'Inactive' &&
      (x.isManager || managerIds.has(x.id)),
  );
  const isMgr = !!(e?.isManager || myReports.length > 0);

  if (loading) return <CenterSpinner />;
  if (!e)
    return (
      <div className="fade-up">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5"
          style={{ color: AG.textSec, fontSize: 14, marginBottom: 16 }}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <Card>
          <EmptyState
            icon={Users}
            title="Employee not found"
            desc="This record is unavailable."
          />
        </Card>
      </div>
    );

  const tabs = empDetailTabs;
  const balances = e.balances || [];
  const mine = e.leaves || [];
  const annual = balances.find((b) => b.type === 'Annual Leave') || {
    entitled: 0,
    used: 0,
  };
  // Current versions only — superseded ones are reachable via the history viewer
  // (the /full payload, unlike the HR list route, isn't pre-filtered).
  const docs = (e.documents || []).filter((d) => !d.supersededAt);
  const att = e.attendance || [];

  const openUpload = () => { setUploadFiles([]); setUploadType('Other'); setUploadErr(''); setUploadOpen(true); };
  const handleUpload = async () => {
    if (!uploadFiles.length) { setUploadErr('Please select at least one file.'); return; }
    setUploading(true); setUploadErr('');
    try {
      const { uploaded, errors } = await api.uploadEmployeeDocumentsBulk(id, uploadFiles, uploadType);
      setUploadOpen(false);
      await refresh();
      if (errors.length === 0) showToast && showToast(`${uploaded.length} document${uploaded.length > 1 ? 's' : ''} uploaded`);
      else if (uploaded.length > 0) showToast && showToast(`${uploaded.length} uploaded, ${errors.length} failed`);
      else setUploadErr(errors.map((e) => e.name + ': ' + e.error).join('; '));
    } catch (err) { setUploadErr(err.message || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const handleDeleteDoc = (doc) => setDeleteTarget(doc);
  const confirmDeleteDoc = async () => {
    const doc = deleteTarget;
    setDeleteTarget(null);
    try { await api.deleteEmployeeDocument(id, doc.id); await refresh(); showToast && showToast('Document deleted'); }
    catch (err) { showToast && showToast('Delete failed: ' + err.message); }
  };
  const handleDownloadDoc = (doc) => {
    api.downloadEmployeeDocument(id, doc.id, doc.name).catch((err) => showToast && showToast('Download failed: ' + err.message));
  };
  // Document hardening (X.4): new version, metadata (visibility/expiry), history.
  const uploadNewVersion = async (doc, file) => {
    if (!file || docBusy) return; setDocBusy(true);
    try { await api.uploadEmployeeDocumentVersion(id, doc.id, file); await refresh(); showToast && showToast(`New version of "${doc.name}" uploaded`); }
    catch (err) { showToast && showToast('Version upload failed: ' + err.message); } finally { setDocBusy(false); }
  };
  const saveDocMeta = async () => {
    if (!docMeta || docBusy) return; setDocBusy(true);
    try { await api.updateEmployeeDocument(id, docMeta.doc.id, { visibility: docMeta.visibility, expiresAt: docMeta.expiresAt || null }); await refresh(); setDocMeta(null); showToast && showToast('Document updated'); }
    catch (err) { showToast && showToast('Update failed: ' + err.message); } finally { setDocBusy(false); }
  };
  const openDocHistory = async (doc) => {
    try { const versions = await api.employeeDocumentHistory(id, doc.id); setDocHistory({ doc, versions }); }
    catch (err) { showToast && showToast('Could not load history: ' + err.message); }
  };
  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const goals = e.goals || [];
  const Row = ({ icon: Icon, label, value }) => (
    <div
      className="flex items-center gap-3"
      style={{ padding: '10px 0', borderBottom: `1px solid ${AG.divider}` }}
    >
      <Icon size={16} style={{ color: AG.n400, flexShrink: 0 }} />
      <span
        style={{ color: AG.textSec, fontSize: 14, width: 128, flexShrink: 0 }}
      >
        {label}
      </span>
      <span style={{ color: AG.text, fontSize: 14, fontWeight: 500 }}>
        {value}
      </span>
    </div>
  );
  const gridTab = tab === 'Attendance';
  return (
    <div className="page-fill fade-up">
      <button
        onClick={onBack}
        className="page-shrink flex items-center gap-1.5 navlift"
        style={{ color: AG.textSec, fontSize: 14, fontWeight: 500 }}
      >
        <ArrowLeft size={16} />
        Back
      </button>
      <Card className="page-shrink" style={{ padding: 24 }}>
        <div className="flex items-start gap-5 flex-wrap">
          {isHR ? (
            <AvatarEditor
              name={e.name}
              avatar={e.avatar}
              size={80}
              onSave={(dataUrl) => saveEdits({ avatar: dataUrl })}
              onError={(m) => showToast && showToast(m, 'error')}
            />
          ) : (
            <Avatar name={e.name} size="xl" src={e.avatar} />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: AG.text,
                  letterSpacing: '-0.01em',
                }}
              >
                {e.name}
              </h2>
              <Pill tone={statusTone(e.status)} dot>
                {e.status}
              </Pill>
              {isMgr && (
                <Pill tone="primary" dot>
                  Manager
                </Pill>
              )}
            </div>
            <p style={{ color: AG.textSec, marginTop: 2 }}>
              {e.role} · {e.dept}
            </p>
            <div
              className="flex items-center gap-4 flex-wrap"
              style={{ marginTop: 8, color: AG.n400, fontSize: 13 }}
            >
              <span className="flex items-center gap-1">
                <Hash size={13} />
                {e.id}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={13} />
                Joined {e.joined}
              </span>
              <span className="flex items-center gap-1">
                <Users size={13} />
                Reports to {e.manager}
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isHR &&
              (isMgr ? (
                <OutlineBtn
                  icon={UserCog}
                  onClick={() => setMgrAction('remove')}
                >
                  Remove as manager
                </OutlineBtn>
              ) : (
                <OutlineBtn
                  icon={UserCog}
                  onClick={() => setMgrAction('promote')}
                >
                  Make manager
                </OutlineBtn>
              ))}
            {isHR && (
              <PrimaryBtn icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </PrimaryBtn>
            )}
          </div>
        </div>
      </Card>
      {editing && (
        <EmployeeModal
          mode="edit"
          initial={e}
          employees={employees}
          onClose={() => setEditing(false)}
          onSubmit={saveEdits}
        />
      )}
      {mgrAction === 'promote' && (
        <PromoteManagerModal
          employee={e}
          onClose={() => setMgrAction(null)}
          onConfirm={doPromote}
        />
      )}
      {mgrAction === 'remove' && (
        <RemoveManagerModal
          employee={e}
          reports={myReports}
          managers={otherManagers}
          onClose={() => setMgrAction(null)}
          onConfirm={doRemove}
        />
      )}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, fontSize: 14, color: AG.textSec }}>
            <b style={{ color: AG.text }}>{deleteTarget?.name}</b> will be permanently deleted and cannot be recovered.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDeleteDoc} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      <div className="tab-strip page-shrink">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => changeEmpTab(t)}
            className={`navlift tab-strip-btn${tab === t ? ' tab-strip-btn--active' : ''}`}
          >
            <span>{t}</span>
          </button>
        ))}
      </div>
      <div className={gridTab ? 'page-fill' : 'page-scroll'}>
        {tab === 'Overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2" style={{ padding: 24 }}>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: AG.n400,
                  marginBottom: 8,
                }}
              >
                Contact &amp; employment
              </p>
              <Row icon={Mail} label="Work email" value={e.email} />
              <Row icon={Phone} label="Phone" value={e.phone} />
              <Row icon={MapPin} label="Location" value={e.location} />
              <Row icon={Briefcase} label="Employment" value={e.type} />
              <Row icon={Building2} label="Department" value={e.dept} />
              <Row icon={GraduationCap} label="Grade" value={e.grade} />
            </Card>
            <div className="space-y-4">
              {[
                { l: 'Tenure', v: tenure(e.joined), c: AG.text },
                {
                  l: 'Annual leave left',
                  v: `${annual.entitled - annual.used} days`,
                  c: AG.text,
                },
                {
                  l: 'Attendance (recent)',
                  v: att.length
                    ? `${Math.round((att.filter((a) => a.status !== 'Absent').length / att.length) * 100)}%`
                    : '—',
                  c: AG.success,
                },
              ].map((s, i) => (
                <Card key={i} style={{ padding: 24 }}>
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      color: AG.n400,
                    }}
                  >
                    {s.l}
                  </p>
                  <p
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: s.c,
                      marginTop: 6,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {s.v}
                  </p>
                </Card>
              ))}
            </div>
            <div className="lg:col-span-3">
              <PositionsCard employeeId={id} isHR={isHR} employees={employees} primaryRole={`${e.role} · ${e.dept}`} showToast={showToast} />
            </div>
          </div>
        )}
        {tab === 'Documents' && (
          <div>
            {isHR && (
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={openUpload}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: AG.primary, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >
                  <Upload size={14} /> Upload Document
                </button>
              </div>
            )}
            {docs.length === 0 ? (
              <Card style={{ padding: 32, textAlign: 'center' }}>
                <FileText size={32} style={{ color: AG.n300, marginBottom: 8 }} />
                <p style={{ color: AG.textSec, fontSize: 14 }}>No documents yet</p>
                {isHR && <p style={{ color: AG.n400, fontSize: 12, marginTop: 4 }}>Upload the employee&apos;s contracts, IDs, and certificates above.</p>}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {docs.map((d) => {
                  const expired = d.expiresAt && d.expiresAt < new Date().toISOString().slice(0, 10);
                  const visTone = d.visibility === 'HR' ? 'warning' : d.visibility === 'Shared' ? 'info' : 'neutral';
                  const visLabel = d.visibility === 'HR' ? 'HR only' : d.visibility === 'Shared' ? 'Shared w/ manager' : 'Employee + HR';
                  return (
                  <Card key={d.id} hover style={{ padding: 16 }}>
                    <div className="flex items-center gap-3">
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: AG.primaryLightest, color: AG.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FileText size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate" style={{ fontWeight: 500, fontSize: 14, color: AG.text }}>{d.name}</p>
                        <p style={{ fontSize: 12, color: AG.n400 }}>{d.type} · {d.date}{d.size ? ` · ${fmtSize(d.size)}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 10 }}>
                      <Pill tone={visTone}>{visLabel}</Pill>
                      {d.version > 1 && <Pill tone="neutral">v{d.version}</Pill>}
                      {d.expiresAt && <Pill tone={expired ? 'error' : 'warning'}>{expired ? 'Expired' : 'Expires'} {d.expiresAt}</Pill>}
                    </div>
                    <div className="flex items-center" style={{ gap: 4, marginTop: 10, borderTop: `1px solid ${AG.divider}`, paddingTop: 8 }}>
                      <button onClick={() => handleDownloadDoc(d)} title="Download" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.n400, display: 'flex' }}><Download size={16} /></button>
                      {isHR && (
                        <>
                          <label title="Upload new version" style={{ cursor: docBusy ? 'default' : 'pointer', padding: 4, color: AG.n400, display: 'flex' }}>
                            <Upload size={16} />
                            <input type="file" hidden disabled={docBusy} onChange={(e) => { uploadNewVersion(d, e.target.files?.[0]); e.target.value = ''; }} />
                          </label>
                          <button onClick={() => setDocMeta({ doc: d, visibility: d.visibility || 'Employee', expiresAt: d.expiresAt || '' })} title="Visibility & expiry" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.n400, display: 'flex' }}><Pencil size={16} /></button>
                          {d.version > 1 && <button onClick={() => openDocHistory(d)} title="Version history" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.n400, display: 'flex' }}><Clock size={16} /></button>}
                          <button onClick={() => handleDeleteDoc(d)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.error, display: 'flex', marginLeft: 'auto' }}><Trash2 size={16} /></button>
                        </>
                      )}
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
            {/* Upload dialog */}
            <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Upload Documents</DialogTitle>
              <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv"
                  onChange={(ev) => {
                    const selected = Array.from(ev.target.files || []);
                    if (selected.length) setUploadFiles((prev) => { const existing = new Set(prev.map((f) => f.name + f.size)); return [...prev, ...selected.filter((f) => !existing.has(f.name + f.size))]; });
                    ev.target.value = '';
                  }}
                />
                <button onClick={() => fileInputRef.current?.click()} style={{ border: `2px dashed ${uploadFiles.length ? AG.primary : AG.n200}`, borderRadius: 8, padding: '18px 12px', textAlign: 'center', cursor: 'pointer', background: uploadFiles.length ? AG.primaryLightest : 'transparent', color: uploadFiles.length ? AG.primary : AG.textSec, fontSize: 13 }}>
                  {uploadFiles.length ? `${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''} selected — click to add more` : 'Click to select files (up to 10)'}
                </button>
                {uploadFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {uploadFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: AG.n50, borderRadius: 6, fontSize: 12 }}>
                        <FileText size={13} style={{ color: AG.primary, flexShrink: 0 }} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: AG.text }}>{f.name}</span>
                        <span style={{ color: AG.n400, flexShrink: 0 }}>{fmtSize(f.size)}</span>
                        <button onClick={() => setUploadFiles((prev) => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: AG.n400, display: 'flex', flexShrink: 0 }}>
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <TextField label="Type (applies to all)" size="small" select value={uploadType} onChange={(ev) => setUploadType(ev.target.value)} fullWidth SelectProps={{ native: true }}>
                  {['Contract', 'ID Document', 'Certificate', 'Education', 'Payslip', 'Other'].map((t) => (<option key={t} value={t}>{t}</option>))}
                </TextField>
                {uploadErr && <p style={{ color: AG.error, fontSize: 12, margin: 0 }}>{uploadErr}</p>}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading || !uploadFiles.length} variant="contained">
                  {uploading ? 'Uploading…' : `Upload${uploadFiles.length > 1 ? ` ${uploadFiles.length} files` : ''}`}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Visibility & expiry dialog (X.4) */}
            <Dialog open={!!docMeta} onClose={() => !docBusy && setDocMeta(null)} maxWidth="xs" fullWidth>
              <DialogTitle>Document visibility & expiry</DialogTitle>
              <DialogContent>
                {docMeta && (
                  <div style={{ paddingTop: 6 }}>
                    <p style={{ fontSize: 13, color: AG.n500, marginBottom: 12 }}>{docMeta.doc.name}</p>
                    <label style={{ display: 'block', marginBottom: 12 }}>
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Who can see this</span>
                      <select className="ag-input" value={docMeta.visibility} onChange={(e) => setDocMeta({ ...docMeta, visibility: e.target.value })}>
                        <option value="Employee">Employee + HR</option>
                        <option value="Shared">Shared (employee, manager & HR)</option>
                        <option value="HR">HR only (hidden from the employee)</option>
                      </select>
                    </label>
                    <TextField fullWidth size="small" type="date" label="Expiry (optional)" InputLabelProps={{ shrink: true }} value={docMeta.expiresAt} onChange={(e) => setDocMeta({ ...docMeta, expiresAt: e.target.value })} />
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <OutlineBtn onClick={() => setDocMeta(null)} disabled={docBusy}>Cancel</OutlineBtn>
                <PrimaryBtn onClick={saveDocMeta} disabled={docBusy}>Save</PrimaryBtn>
              </DialogActions>
            </Dialog>

            {/* Version history dialog (X.4) */}
            <Dialog open={!!docHistory} onClose={() => setDocHistory(null)} maxWidth="xs" fullWidth>
              <DialogTitle>Version history</DialogTitle>
              <DialogContent>
                {docHistory && (
                  <div style={{ paddingTop: 6 }}>
                    <p style={{ fontSize: 13, color: AG.n500, marginBottom: 12 }}>{docHistory.doc.name}</p>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {docHistory.versions.map((v) => (
                        <div key={v.id} className="flex items-center gap-2" style={{ padding: '8px 10px', border: `1px solid ${AG.divider}`, borderRadius: 8 }}>
                          <Pill tone={v.supersededAt ? 'neutral' : 'success'}>v{v.version}</Pill>
                          <span style={{ fontSize: 12.5, color: AG.n600, flex: 1, minWidth: 0 }} className="truncate">{v.supersededAt ? 'superseded' : 'current'} · {new Date(v.uploadedAt).toLocaleDateString()}{v.size ? ` · ${fmtSize(v.size)}` : ''}</span>
                          <button onClick={() => handleDownloadDoc(v)} title="Download this version" style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, display: 'flex' }}><Download size={15} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <OutlineBtn onClick={() => setDocHistory(null)}>Close</OutlineBtn>
              </DialogActions>
            </Dialog>
          </div>
        )}
        {tab === 'Attendance' && (
          <DataGridCard title="Recent attendance">
            <AppDataGrid
              rows={attRows}
              columns={historyColumns}
              hideFooter={attRows.length <= 10}
              cursor="default"
            />
          </DataGridCard>
        )}
        {tab === 'Leave' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {balances.map((b) => (
                <Card key={b.type} style={{ padding: 16 }}>
                  <p
                    style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}
                  >
                    {b.type}
                  </p>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: AG.text,
                      marginTop: 4,
                    }}
                  >
                    {b.entitled - b.used}
                    <span
                      style={{ fontSize: 13, color: AG.n400, fontWeight: 500 }}
                    >
                      {' '}
                      / {b.entitled}
                    </span>
                  </p>
                  <div
                    style={{
                      marginTop: 8,
                      height: 6,
                      background: AG.n100,
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 4,
                        width: b.entitled
                          ? `${(b.used / b.entitled) * 100}%`
                          : '0%',
                        background: AG.primary,
                      }}
                    />
                  </div>
                </Card>
              ))}
            </div>
            <Card style={{ padding: 24 }}>
              <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>
                Leave history
              </h3>
              {mine.length === 0 ? (
                <p
                  style={{
                    color: AG.n400,
                    fontSize: 14,
                    textAlign: 'center',
                    padding: '24px 0',
                  }}
                >
                  No leave records to show for this employee.
                </p>
              ) : (
                <div className="space-y-2">
                  {mine.map((l) => (
                    <div
                      key={l.id}
                      className="flex items-center gap-3"
                      style={{
                        padding: 12,
                        background: AG.n50,
                        borderRadius: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          background: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: AG.n500,
                        }}
                      >
                        <CalendarOff size={16} />
                      </div>
                      <div className="flex-1">
                        <p
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: AG.text,
                          }}
                        >
                          {l.type}
                        </p>
                        <p style={{ fontSize: 12, color: AG.n400 }}>
                          {l.from} → {l.to} · {l.days} day
                          {l.days > 1 ? 's' : ''}
                        </p>
                      </div>
                      <Pill tone={statusTone(l.status)}>{l.status}</Pill>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
        {tab === 'Performance' && (
          <Card style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 16 }}>
              H1 2026 goals
            </h3>
            {goals.length === 0 ? (
              <p style={{ color: AG.n400, fontSize: 14 }}>No goals set.</p>
            ) : (
              <div className="space-y-4">
                {goals.map((g) => (
                  <div key={g.id}>
                    <div
                      className="flex justify-between items-center"
                      style={{ marginBottom: 6 }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: AG.text,
                        }}
                      >
                        {g.title}
                      </span>
                      <span style={{ fontSize: 12, color: AG.n400 }}>
                        Weight {g.weight}%
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        style={{
                          flex: 1,
                          height: 8,
                          background: AG.n100,
                          borderRadius: 4,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            background: AG.primary,
                            borderRadius: 4,
                            width: `${g.progress}%`,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: AG.text,
                          width: 40,
                          textAlign: 'right',
                        }}
                      >
                        {g.progress}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function CandidateDetail({
  id,
  candidates,
  jobs,
  interviews,
  offers,
  moveCandidate,
  rejectCandidate,
  restoreCandidate,
  createOffer,
  openJob,
  onBack,
}) {
  const c = candidates.find((x) => x.id === id);
  if (!c) return null;
  const si = STAGES.indexOf(c.stage);
  const sTone =
    c.score >= 88 ? 'success' : c.score >= 78 ? 'warning' : 'neutral';
  const sCol = PILL[sTone];
  const myInterviews = (interviews || []).filter((v) => v.candId === id);
  const myOffer = (offers || []).find((o) => o.candId === id);
  const recTone = (r) =>
    r === 'Advance' ? 'success' : r === 'Reject' ? 'error' : 'warning';
  return (
    <div className="fade-up">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 navlift"
        style={{
          color: AG.textSec,
          fontSize: 14,
          fontWeight: 500,
          marginBottom: 16,
        }}
      >
        <ArrowLeft size={16} />
        Back to ATS
      </button>
      <Card style={{ padding: 24, marginBottom: 16 }}>
        <div className="flex items-start gap-5 flex-wrap">
          <Avatar name={c.name} size="xl" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: AG.text,
                  letterSpacing: '-0.01em',
                }}
              >
                {c.name}
              </h2>
              {c.rejected && (
                <Pill tone="error" dot>
                  Rejected
                </Pill>
              )}
            </div>
            <p style={{ color: AG.textSec, marginTop: 2 }}>
              Applied for{' '}
              <button
                onClick={() => openJob && openJob(c.jobId)}
                style={{
                  color: AG.primary,
                  fontWeight: 600,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 14,
                }}
              >
                {jobName(jobs || [], c.jobId)}
              </button>
            </p>
            <div
              className="flex items-center gap-4 flex-wrap"
              style={{ marginTop: 8, color: AG.n400, fontSize: 13 }}
            >
              <span className="flex items-center gap-1">
                <Briefcase size={13} />
                {c.exp} experience
              </span>
              <span className="flex items-center gap-1">
                <Hash size={13} />
                {c.source}
              </span>
              <span className="flex items-center gap-1">
                <Mail size={13} />
                {empEmail(c.name)}
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {c.rejected ? (
              <PrimaryBtn onClick={() => restoreCandidate(c.id)}>
                Restore to pipeline
              </PrimaryBtn>
            ) : (
              <>
                <button
                  onClick={() => moveCandidate(c.id, -1)}
                  disabled={si === 0}
                  className="ag-btn ag-btn-soft"
                >
                  ← Move back
                </button>
                {c.stage === 'Offer' && !myOffer && (
                  <button
                    onClick={() => createOffer(c.id, 1000000, '2026-07-01')}
                    className="ag-btn ag-btn-soft flex items-center gap-1"
                  >
                    <Send size={15} />
                    Create offer
                  </button>
                )}
                <button
                  onClick={() => rejectCandidate(c.id)}
                  className="ag-btn flex items-center gap-1"
                  style={{
                    color: AG.errorDark,
                    background: 'rgba(240,68,56,.12)',
                    border: 'none',
                  }}
                >
                  Reject
                </button>
                <PrimaryBtn
                  icon={ArrowRight}
                  onClick={() => moveCandidate(c.id, 1)}
                >
                  Advance stage
                </PrimaryBtn>
              </>
            )}
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card style={{ padding: 24 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: AG.n400,
                marginBottom: 12,
              }}
            >
              Pipeline stage
            </p>
            <div className="flex items-center">
              {STAGES.map((s, i) => (
                <div
                  key={s}
                  className="flex items-center"
                  style={{ flex: i < STAGES.length - 1 ? 1 : 'none' }}
                >
                  <div className="flex flex-col items-center">
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 99,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 600,
                        background: i <= si ? AG.primary : AG.n100,
                        color: i <= si ? '#fff' : AG.n400,
                        boxShadow:
                          i === si ? `0 0 0 4px ${AG.primaryLightest}` : 'none',
                      }}
                    >
                      {i < si ? <Check size={15} /> : i + 1}
                    </div>
                    <span
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        fontWeight: 500,
                        color: i <= si ? AG.text : AG.n400,
                      }}
                    >
                      {s}
                    </span>
                  </div>
                  {i < STAGES.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: 2,
                        margin: '0 4px 20px',
                        borderRadius: 2,
                        background: i < si ? AG.primary : AG.n200,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </Card>
          <Card style={{ padding: 24 }}>
            <div
              className="flex items-center gap-2"
              style={{ marginBottom: 12 }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: AG.purpleLightest,
                  color: AG.purple,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sparkles size={15} />
              </div>
              <h3 style={{ fontWeight: 600, color: AG.text }}>
                AI resume summary
              </h3>
            </div>
            <p style={{ color: AG.n600, fontSize: 14, lineHeight: 1.6 }}>
              {c.name.split(' ')[0]} is a {c.exp}-experienced candidate for the{' '}
              {c.role} role with a strong match across core requirements. The AI
              parser extracted the skills below and rated alignment to the job
              description at{' '}
              <span style={{ fontWeight: 600, color: AG.text }}>
                {c.score}/100
              </span>
              .
            </p>
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: AG.n400,
                margin: '16px 0 8px',
              }}
            >
              Extracted skills
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(c.skills || []).map((s) => (
                <span
                  key={s}
                  style={{
                    background: AG.n100,
                    color: AG.n600,
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </Card>
          <Card style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>
              Interviews & scorecards
            </h3>
            {myInterviews.length > 0 ? (
              <div className="space-y-3">
                {myInterviews.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center gap-3"
                    style={{
                      padding: 12,
                      background: AG.n50,
                      borderRadius: 12,
                    }}
                  >
                    <Avatar name={v.interviewer} size="sm" />
                    <div className="flex-1">
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: AG.text,
                        }}
                      >
                        {v.round}
                      </p>
                      <p style={{ fontSize: 12, color: AG.n400 }}>
                        {v.interviewer} · {v.date} {v.time}
                      </p>
                    </div>
                    {v.status === 'Completed' ? (
                      <Pill tone={recTone(v.rec)} dot>
                        {v.rec}
                      </Pill>
                    ) : (
                      <Pill tone="info">Scheduled</Pill>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p
                style={{
                  color: AG.n400,
                  fontSize: 14,
                  textAlign: 'center',
                  padding: '16px 0',
                }}
              >
                No interviews scheduled yet — add one from the Interviews tab.
              </p>
            )}
          </Card>
        </div>
        <div className="space-y-4">
          <Card style={{ padding: 24, textAlign: 'center' }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: AG.n400,
              }}
            >
              AI match score
            </p>
            <div
              style={{
                margin: '12px auto 0',
                width: 96,
                height: 96,
                borderRadius: 99,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: sCol.bg,
              }}
            >
              <span
                style={{
                  fontSize: 34,
                  fontWeight: 700,
                  color: sCol.c,
                  letterSpacing: '-0.02em',
                }}
              >
                {c.score}
              </span>
            </div>
            <p style={{ fontSize: 12, color: AG.n400, marginTop: 12 }}>
              {c.score >= 88
                ? 'Excellent fit for this role'
                : c.score >= 78
                  ? 'Good fit, worth interviewing'
                  : 'Partial match, review needed'}
            </p>
          </Card>
          {myOffer && (
            <Card style={{ padding: 24 }}>
              <div
                className="flex items-center gap-2"
                style={{ marginBottom: 10 }}
              >
                <Send size={16} style={{ color: AG.primary }} />
                <h3 style={{ fontWeight: 600, color: AG.text }}>Offer</h3>
              </div>
              <div className="space-y-2" style={{ fontSize: 14 }}>
                <div className="flex justify-between">
                  <span style={{ color: AG.textSec }}>Status</span>
                  <Pill
                    tone={
                      myOffer.status === 'Accepted'
                        ? 'success'
                        : myOffer.status === 'Declined'
                          ? 'error'
                          : myOffer.status === 'Sent'
                            ? 'info'
                            : 'neutral'
                    }
                  >
                    {myOffer.status}
                  </Pill>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: AG.textSec }}>Salary</span>
                  <span style={{ fontWeight: 500, color: AG.text }}>
                    {fmtPKR(myOffer.salary)}/yr
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: AG.textSec }}>Start date</span>
                  <span style={{ fontWeight: 500, color: AG.text }}>
                    {myOffer.start}
                  </span>
                </div>
              </div>
            </Card>
          )}
          <Card style={{ padding: 24 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: AG.n400,
                marginBottom: 8,
              }}
            >
              Details
            </p>
            <div className="space-y-2" style={{ fontSize: 14 }}>
              {[
                ['Applied for', jobName(jobs || [], c.jobId)],
                ['Stage', c.stage],
                ['Source', c.source],
                ['Experience', c.exp],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <span style={{ color: AG.textSec }}>{k}</span>
                  <span
                    style={{
                      fontWeight: 500,
                      color: AG.text,
                      textAlign: 'right',
                    }}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── Activity Log (HR only) ───────────────────────────────────────────────────
// A single filterable listing of who did what, when — across sessions, leave,
// attendance, OKRs, policies, payroll and people. Times are stored UTC and
// rendered in PKT (the company operates on Pakistan Standard Time, UTC+05:00).
const PKT_TZ = { timeZone: 'Asia/Karachi' };

// Human label for an action code: "MANAGER_APPROVE_LEAVE" → "Manager approve leave".
function activityActionLabel(a) {
  const s = String(a || '').replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Full PKT timestamp, e.g. "24 Jun 2026, 3:42 PM". Used for the CSV export and tooltips.
function fmtPKT(iso) {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      ...PKT_TZ, day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return String(iso ?? '');
  }
}

// PKT date only, e.g. "24 Jun 2026".
function fmtPKTDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      ...PKT_TZ, day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return String(iso ?? '');
  }
}

// PKT time only, e.g. "3:42:08 PM" (seconds included — useful for an audit trail).
function fmtPKTTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      ...PKT_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
  } catch {
    return '';
  }
}

// Short relative time for the secondary line ("3m ago", "2h ago", "4d ago").
// Returns '' once older than a week so the explicit date carries it instead.
function relTime(iso) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days < 7) return `${days}d ago`;
  return '';
}

// A small inline chip (module / action) — avoids portaled popovers that the
// app-wide zoom misaligns; pure inline styling.
function ActivityChip({ label, tone = 'default' }) {
  const tones = {
    info: { bg: AG.primaryLightest, fg: AG.primary },
    success: { bg: '#E7F6EC', fg: AG.success },
    warning: { bg: '#FFF4E5', fg: '#B26A00' },
    error: { bg: '#FDECEC', fg: AG.error || '#C62828' },
    default: { bg: AG.n100 || '#F1F3F5', fg: AG.textSec },
  };
  const c = tones[tone] || tones.default;
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 999,
        background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

const ACTIVITY_MODULE_TONE = {
  Session: 'info', Leave: 'warning', Attendance: 'success',
  OKR: 'info', Policy: 'default', Payroll: 'success',
  People: 'info', Account: 'default', Reports: 'info',
};

function ActivityLogAdmin() {
  const todayStr = new Date().toLocaleDateString('en-CA', PKT_TZ); // YYYY-MM-DD in PKT
  const [filters, setFilters] = useState({ actions: [], modules: [], actors: [] });
  const [actorId, setActorId] = useState('');
  const [role, setRole] = useState('');
  const [module, setModule] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const rangeBad = from && to && from > to;

  // Filter dropdown options (distinct actors / actions / modules in the log).
  useEffect(() => {
    let alive = true;
    api.activityFilters().then((f) => alive && setFilters(f)).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Re-query whenever a filter changes.
  useEffect(() => {
    if (rangeBad) return;
    let alive = true;
    setData(null);
    api
      .activity({ actorId, role, module, action, from, to, limit: 300 })
      .then((d) => alive && setData(d))
      .catch(() => alive && setData({ rows: [], total: 0 }));
    return () => { alive = false; };
  }, [actorId, role, module, action, from, to, rangeBad]);

  const rows = data?.rows || [];
  const loading = data === null;

  const resetFilters = () => {
    setActorId(''); setRole(''); setModule(''); setAction(''); setFrom(''); setTo('');
  };

  const exportCsv = () => {
    const header = ['When (PKT)', 'Actor', 'Role', 'Action', 'Module', 'Details'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map((r) =>
      [
        fmtPKT(r.occurredAt), r.actorName,
        r.isManager ? 'Manager' : r.actorRole,
        activityActionLabel(r.action), r.module, r.targetLabel || '',
      ].map(esc).join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `activity-log-${todayStr}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(
    () => [
      {
        field: 'occurredAt', headerName: 'Date & time (PKT)', flex: 0.9, minWidth: 200,
        renderCell: ({ row }) => {
          const rel = relTime(row.occurredAt);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', whiteSpace: 'nowrap' }}>
              <span style={{ color: AG.text, fontWeight: 500 }}>
                {fmtPKTDate(row.occurredAt)} · {fmtPKTTime(row.occurredAt)}
              </span>
              {rel && <span style={{ fontSize: 11, color: AG.textSec }}>{rel}</span>}
            </div>
          );
        },
      },
      {
        field: 'actorName', headerName: 'Performed by', flex: 1, minWidth: 180,
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <span style={{ fontWeight: 500, color: AG.text }}>{row.actorName}</span>
            <span style={{ fontSize: 11, color: AG.textSec }}>
              {row.isManager ? 'Manager' : row.actorRole === 'HR' ? 'HR' : 'Employee'}
            </span>
          </div>
        ),
      },
      {
        field: 'action', headerName: 'Action', flex: 0.9, minWidth: 170,
        renderCell: ({ row }) => <ActivityChip label={activityActionLabel(row.action)} tone={ACTIVITY_MODULE_TONE[row.module]} />,
      },
      {
        field: 'module', headerName: 'Module', flex: 0.5, minWidth: 110,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'targetLabel', headerName: 'Details', flex: 1.4, minWidth: 200,
        renderCell: ({ value }) => <TextCell value={value || '—'} />,
      },
    ],
    [],
  );

  const selStyle = { width: 'auto', borderRadius: 10, minWidth: 120 };

  return (
    <div className="page-scroll fade-up">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <SectionTitle title="Activity Log" sub="Every action your employees and managers take — sessions, leave, attendance, performance and more" />
        <button
          onClick={exportCsv}
          disabled={!rows.length}
          className="ag-btn ag-btn-soft"
          style={{ padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: rows.length ? 1 : 0.5 }}
        >
          <Download size={15} /> Export CSV
        </button>
      </div>

      {/* What-is-logged banner — sets expectations on coverage + timezone */}
      <div
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          background: AG.primaryLightest, border: `1px solid ${AG.primaryLight}`,
          borderRadius: 12, padding: '12px 16px', margin: '16px 0',
        }}
      >
        <ShieldCheck size={18} style={{ color: AG.primary, flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, lineHeight: 1.55, color: AG.text }}>
          <strong style={{ fontWeight: 600 }}>What gets recorded here.</strong>{' '}
          This is a read-only audit trail of key actions across the workspace:{' '}
          <strong style={{ fontWeight: 600 }}>sign-ins &amp; sign-outs</strong>,{' '}
          <strong style={{ fontWeight: 600 }}>leave</strong> requests and manager/HR approvals,{' '}
          <strong style={{ fontWeight: 600 }}>attendance</strong> clock-in/out,{' '}
          <strong style={{ fontWeight: 600 }}>people</strong> &amp; document changes,{' '}
          profile updates,{' '}
          <strong style={{ fontWeight: 600 }}>OKR</strong> activity,{' '}
          <strong style={{ fontWeight: 600 }}>policy</strong> changes &amp; acknowledgements,{' '}
          <strong style={{ fontWeight: 600 }}>payroll</strong> runs, and{' '}
          <strong style={{ fontWeight: 600 }}>downloads</strong> of documents, policies, payslips, tax certificates &amp; payroll exports.{' '}
          <span style={{ color: AG.textSec }}>
            Times are shown in Pakistan Standard Time (PKT, UTC+05:00).
          </span>
        </div>
      </div>

      {/* Filters — native selects so the app-wide zoom doesn't misalign popovers */}
      <div className="flex items-center gap-2 flex-wrap" style={{ margin: '14px 0' }}>
        <select className="ag-input" style={selStyle} value={actorId} onChange={(e) => setActorId(e.target.value)}>
          <option value="">All people</option>
          {filters.actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select className="ag-input" style={selStyle} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          <option value="EMPLOYEE">Employee</option>
          <option value="MANAGER">Manager</option>
          <option value="HR">HR</option>
        </select>
        <select className="ag-input" style={selStyle} value={module} onChange={(e) => setModule(e.target.value)}>
          <option value="">All modules</option>
          {filters.modules.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="ag-input" style={selStyle} value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          {filters.actions.map((a) => <option key={a} value={a}>{activityActionLabel(a)}</option>)}
        </select>
        <label style={{ fontSize: 12, color: AG.textSec }}>From</label>
        <input type="date" value={from} max={todayStr} onChange={(e) => setFrom(e.target.value)} className="ag-input" style={{ width: 'auto', borderRadius: 10 }} />
        <label style={{ fontSize: 12, color: AG.textSec }}>To</label>
        <input type="date" value={to} max={todayStr} onChange={(e) => setTo(e.target.value)} className="ag-input" style={{ width: 'auto', borderRadius: 10 }} />
        {(actorId || role || module || action || from || to) && (
          <button onClick={resetFilters} className="ag-btn ag-btn-soft" style={{ padding: '8px 14px', fontSize: 13 }}>
            Reset
          </button>
        )}
      </div>

      {rangeBad ? (
        <Card>
          <EmptyState icon={Calendar} title="Invalid date range" desc="The 'From' date must be on or before the 'To' date." />
        </Card>
      ) : loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><CenterSpinner /></div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState icon={Activity} title="No activity found" desc="No recorded activity matches the current filters." />
        </Card>
      ) : (
        <DataGridCard fillHeight={false} title={`${data.total} event${data.total === 1 ? '' : 's'}${data.total > rows.length ? ` · showing latest ${rows.length}` : ''}`}>
          <AppDataGrid rows={rows} columns={columns} autoHeight fillHeight={false} hideFooter={rows.length <= 12} />
        </DataGridCard>
      )}
    </div>
  );
}

function AdminViews(p) {
  const {
    view,
    navSignal,
    employees,
    candidates,
    leaves,
    decideLeave,
    openEmployee,
    navTo,
    askAI,
    addEmployee,
    disableEmployee,
    restoreEmployee,
    showToast,
    orgTree,
    activeEntity,
    refreshOrg,
  } = p;
  if (view === 'Dashboard')
    return (
      <PageScroll>
        <AdminDashboard leaves={leaves} navTo={navTo} askAI={askAI} />
      </PageScroll>
    );
  if (view === 'Employees')
    return (
      <EmployeesView
        navSignal={navSignal}
        employees={employees}
        openEmployee={openEmployee}
        addEmployee={addEmployee}
        disableEmployee={disableEmployee}
        restoreEmployee={restoreEmployee}
        orgTree={orgTree}
        activeEntity={activeEntity}
      />
    );
  if (view === 'Attendance')
    return <AttendanceAdmin openEmployee={openEmployee} />;
  if (view === 'Leave')
    return (
      <PageScroll>
        <LeaveAdmin leaves={leaves} decideLeave={decideLeave} />
      </PageScroll>
    );
  if (view === 'Recruitment') return <ATS p={p} />;
  if (view === 'JD Library') return <PageScroll><JdLibrary showToast={showToast} /></PageScroll>;
  if (view === 'Performance')
    return (
      <PageScroll>
        <PerformanceAdmin employees={employees} />
      </PageScroll>
    );
  if (view === 'Payroll') return <PayrollAdmin showToast={showToast} />;
  if (view === 'Policies')
    return (
      <PageScroll>
        <DocsAdmin kind="Policy" />
      </PageScroll>
    );
  if (view === 'Organization Docs')
    return (
      <PageScroll>
        <DocsAdmin kind="Form" />
      </PageScroll>
    );
  if (view === 'Reports')
    return (
      <PageScroll>
        <ReportsAdmin />
      </PageScroll>
    );
  if (view === 'Activity Log')
    return (
      <PageScroll>
        <ActivityLogAdmin />
      </PageScroll>
    );
  if (view === 'Approvals')
    return (
      <PageScroll>
        <ApprovalsInbox showToast={showToast} onNavigate={() => navTo('Performance')} />
      </PageScroll>
    );
  if (view === 'Entities')
    return (
      <PageScroll>
        <OrgAdmin showToast={showToast} refreshOrg={refreshOrg} />
      </PageScroll>
    );
  if (view === 'Configuration')
    return (
      <PageScroll>
        <ConfigAdmin showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Letters')
    return (
      <PageScroll>
        <Letters showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Org Chart')
    return (
      <PageScroll>
        <OrgChart showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Training')
    return (
      <PageScroll>
        <TrainingAdmin showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Competencies')
    return (
      <PageScroll>
        <CompetencyAdmin showToast={showToast} />
      </PageScroll>
    );
  if (view === '360 Feedback')
    return (
      <PageScroll>
        <Feedback360Admin showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Recognition')
    return (
      <PageScroll>
        <Recognition showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Awards')
    return (
      <PageScroll>
        <Awards showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Pulse')
    return (
      <PageScroll>
        <Pulse showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Employee Relations')
    return (
      <PageScroll>
        <Cases showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Insights')
    return (
      <PageScroll>
        <Insights />
      </PageScroll>
    );
  if (view === 'Announcements')
    return (
      <PageScroll>
        <Announcements showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Health & Safety')
    return (
      <PageScroll>
        <Safety showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Benefits')
    return (
      <PageScroll>
        <Benefits showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Offboarding')
    return (
      <PageScroll>
        <Offboarding showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Disciplinary')
    return (
      <PageScroll>
        <Disciplinary showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Succession')
    return (
      <PageScroll>
        <Succession showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Loans')
    return (
      <PageScroll>
        <Loans showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Performance Cycles')
    return (
      <PageScroll>
        <PerformanceCycles showToast={showToast} />
      </PageScroll>
    );
  if (view === 'Settings')
    return (
      <PageScroll>
        <SettingsAdmin />
      </PageScroll>
    );
  return null;
}

// A label/value row inside an entity card's metrics block (right-aligned value).
function StatRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ fontSize: 13, color: AG.n500 }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: color || AG.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

// Per-card ⋮ actions menu (Edit · Activate/Deactivate). A custom popover, not a
// portaled MUI Menu, so it positions correctly under the app's body zoom.
function EntityCardMenu({ isActive, onEdit, onToggleStatus }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
    border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
    fontSize: 13, fontWeight: 500, color: AG.text, borderRadius: 8, whiteSpace: 'nowrap',
  };
  const hoverOn = (e) => (e.currentTarget.style.background = AG.n50);
  const hoverOff = (e) => (e.currentTarget.style.background = 'transparent');
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label="Entity actions"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: 'none', background: open ? AG.n100 : 'transparent', color: AG.n600, cursor: 'pointer' }}
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, minWidth: 168, background: AG.paper, border: `1px solid ${AG.n200}`, borderRadius: 12, boxShadow: '0 14px 30px rgba(16,24,40,0.16)', zIndex: 1300, padding: 5 }}>
          <button type="button" style={itemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff} onClick={() => { setOpen(false); onEdit(); }}>
            <Pencil size={14} /> Edit
          </button>
          <button type="button" style={itemStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff} onClick={() => { setOpen(false); onToggleStatus(); }}>
            <Power size={14} /> {isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      )}
    </div>
  );
}

// Square logo uploader for the entity dialogs. Client-side downscale to a small
// data URL (reuses the avatar image pipeline); the monogram is the fallback.
function LogoPicker({ value, onChange, entity }) {
  const fileRef = useRef(null);
  const [err, setErr] = useState('');
  const pick = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    if (!IMAGE_ACCEPT.includes(file.type)) { setErr('Logo must be a JPEG, PNG, or WebP image.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { setErr('Logo must be 5 MB or smaller.'); return; }
    try {
      onChange(await downscaleSquareDataUrl(file, 256));
      setErr('');
    } catch {
      setErr("Couldn't read that image.");
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div style={{ width: 56, height: 56, borderRadius: 14, overflow: 'hidden', flexShrink: 0 }}>
        {value ? (
          <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <EntityGlyph entity={entity} size={56} />
        )}
      </div>
      <div>
        <input ref={fileRef} type="file" accept={IMAGE_ACCEPT.join(',')} hidden onChange={pick} />
        <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()}>
          {value ? 'Change logo' : 'Upload logo'}
        </Button>
        {value && (
          <Button size="small" color="inherit" sx={{ ml: 1 }} onClick={() => onChange(null)}>
            Remove
          </Button>
        )}
        <div style={{ fontSize: 12, color: AG.n500, marginTop: 6 }}>JPEG, PNG, or WebP — max 5 MB.</div>
        {err && <div style={{ fontSize: 12, color: AG.error, marginTop: 4 }}>{err}</div>}
      </div>
    </div>
  );
}

// Organization — HR-only management of the Company → Group → Entity hierarchy.
// Create groups and entities; rename / activate / deactivate entities.
const ENTITY_TYPES = ['Branch', 'Subsidiary', 'Division'];
function OrgAdmin({ showToast, refreshOrg }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupForm, setGroupForm] = useState(null); // { name, code }
  const [entityForm, setEntityForm] = useState(null); // { groupId, groupName, name, code, type }
  const [editEntity, setEditEntity] = useState(null); // { id, name, type, status }
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .orgTree()
      .then((t) => setTree(t))
      .catch((e) => showToast(e.message))
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const company = tree && tree[0];
  // Reload this view AND the app shell's org tree (header switcher + add-employee picker).
  const afterChange = async () => {
    await load();
    refreshOrg?.();
  };

  // Quick activate / deactivate from the card's ⋮ menu.
  const toggleEntityStatus = async (ent) => {
    if (busy) return;
    setBusy(true);
    const next = ent.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await api.updateEntity(ent.id, { status: next });
      showToast(`${ent.name} ${next === 'Active' ? 'activated' : 'deactivated'}`);
      await afterChange();
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const addGroup = async () => {
    const name = (groupForm.name || '').trim();
    const code = (groupForm.code || '').trim();
    if (!name || !code || busy) return;
    setBusy(true);
    try {
      await api.createGroup({ companyId: company.id, name, code });
      showToast(`Group "${name}" created`);
      setGroupForm(null);
      await afterChange();
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const addEntity = async () => {
    const name = (entityForm.name || '').trim();
    const code = (entityForm.code || '').trim();
    if (!name || !code || busy) return;
    setBusy(true);
    try {
      await api.createEntity({ groupId: entityForm.groupId, name, code, type: entityForm.type, logoUrl: entityForm.logoUrl || null });
      showToast(`Entity "${name}" created`);
      setEntityForm(null);
      await afterChange();
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEntity = async () => {
    const name = (editEntity.name || '').trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await api.updateEntity(editEntity.id, { name, type: editEntity.type, status: editEntity.status, attendanceSource: editEntity.attendanceSource, logoUrl: editEntity.logoUrl ?? null });
      showToast('Entity updated');
      setEditEntity(null);
      await afterChange();
    } catch (e) {
      showToast(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <CenterSpinner />;
  const groups = (company && company.groups) || [];
  const entities = groups.flatMap((g) => g.entities || []);
  const totalEmployees = entities.reduce((s, e) => s + (e.stats?.employees || 0), 0);
  const activeEntities = entities.filter((e) => e.status === 'Active').length;

  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle
          title="Organization"
          sub={company ? `${company.name} — manage your groups and entities` : 'Company → Groups → Entities'}
        />
        {company && <PrimaryBtn onClick={() => setGroupForm({ name: '', code: '' })}>+ New group</PrimaryBtn>}
      </div>

      <div style={{ marginBottom: 24 }}>
        <WidgetGrid>
          <KPI label="Groups" value={groups.length} icon={Briefcase} iconTone="primary" />
          <KPI label="Entities" value={entities.length} icon={Building2} iconTone="info" />
          <KPI label="Employees" value={totalEmployees} icon={Users} iconTone="success" />
          <KPI label="Active entities" value={activeEntities} icon={ShieldCheck} iconTone="purple" />
        </WidgetGrid>
      </div>

      {groups.length === 0 ? (
        <Card>
          <EmptyState icon={Building2} title="No groups yet" desc="Create a group, then add entities under it." />
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 28 }}>
          {groups.map((g) => {
            const ents = g.entities || [];
            return (
              <div key={g.id}>
                <div className="flex items-center justify-between" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                  <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
                    <Briefcase size={16} style={{ color: AG.n500, flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{g.name}</span>
                    <Pill tone="neutral">{g.code}</Pill>
                    <span style={{ fontSize: 13, color: AG.n500 }}>
                      {ents.length} entit{ents.length === 1 ? 'y' : 'ies'}
                    </span>
                  </div>
                  <OutlineBtn onClick={() => setEntityForm({ groupId: g.id, groupName: g.name, name: '', code: '', type: 'Branch', logoUrl: null })}>
                    + New entity
                  </OutlineBtn>
                </div>
                {ents.length === 0 ? (
                  <Card>
                    <EmptyState icon={Building2} title="No entities in this group" desc="Add the first entity to start scoping data to it." />
                  </Card>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                    {ents.map((ent) => (
                      <div
                        key={ent.id}
                        style={{
                          position: 'relative',
                          background: '#fff',
                          border: `1px solid ${AG.n200}`,
                          borderRadius: 16,
                          padding: 18,
                          display: 'flex',
                          flexDirection: 'column',
                          opacity: ent.status === 'Active' ? 1 : 0.7,
                        }}
                      >
                        {/* Top: glyph + type chip + actions menu */}
                        <div className="flex items-start justify-between" style={{ gap: 8 }}>
                          <EntityGlyph entity={ent} size={48} />
                          <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: AG.n600,
                                border: `1px solid ${AG.n300}`,
                                borderRadius: 8,
                                padding: '3px 8px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {ent.type}
                            </span>
                            <EntityCardMenu
                              isActive={ent.status === 'Active'}
                              onEdit={() => setEditEntity({ id: ent.id, name: ent.name, code: ent.code, type: ent.type, status: ent.status, attendanceSource: ent.attendanceSource, logoUrl: ent.logoUrl })}
                              onToggleStatus={() => toggleEntityStatus(ent)}
                            />
                          </div>
                        </div>

                        {/* Identity */}
                        <div style={{ marginTop: 14, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 16, color: AG.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ent.name}
                          </div>
                          <div style={{ marginTop: 3, fontSize: 12.5, color: AG.n500 }}>
                            {ent.code}
                            {ent.status !== 'Active' && (
                              <span style={{ color: AG.warning, fontWeight: 600 }}> · {ent.status}</span>
                            )}
                          </div>
                        </div>

                        {/* Workforce metrics */}
                        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${AG.divider}`, display: 'grid', gap: 9 }}>
                          <StatRow label="Employees" value={ent.stats?.employees ?? 0} />
                          <StatRow label="Active" value={ent.stats?.active ?? 0} color={AG.successDark} />
                          <StatRow label="Departments" value={ent.stats?.departments ?? 0} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New group */}
      <Dialog open={!!groupForm} onClose={() => !busy && setGroupForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New group</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Group name" fullWidth autoFocus value={groupForm?.name || ''} onChange={(e) => setGroupForm((s) => ({ ...s, name: e.target.value }))} />
          <TextField label="Code" fullWidth value={groupForm?.code || ''} onChange={(e) => setGroupForm((s) => ({ ...s, code: e.target.value }))} helperText="Short unique code, e.g. TECH" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupForm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={addGroup} disabled={busy || !groupForm?.name?.trim() || !groupForm?.code?.trim()}>Create group</Button>
        </DialogActions>
      </Dialog>

      {/* New entity */}
      <Dialog open={!!entityForm} onClose={() => !busy && setEntityForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New entity{entityForm?.groupName ? ` · ${entityForm.groupName}` : ''}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <LogoPicker value={entityForm?.logoUrl || null} onChange={(v) => setEntityForm((s) => ({ ...s, logoUrl: v }))} entity={{ name: entityForm?.name, code: entityForm?.code }} />
          <TextField label="Entity name" fullWidth autoFocus value={entityForm?.name || ''} onChange={(e) => setEntityForm((s) => ({ ...s, name: e.target.value }))} />
          <TextField label="Code" fullWidth value={entityForm?.code || ''} onChange={(e) => setEntityForm((s) => ({ ...s, code: e.target.value }))} helperText="Short unique code, e.g. HQ" />
          <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Type" fullWidth value={entityForm?.type || 'Branch'} onChange={(e) => setEntityForm((s) => ({ ...s, type: e.target.value }))}>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntityForm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={addEntity} disabled={busy || !entityForm?.name?.trim() || !entityForm?.code?.trim()}>Create entity</Button>
        </DialogActions>
      </Dialog>

      {/* Edit entity */}
      <Dialog open={!!editEntity} onClose={() => !busy && setEditEntity(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit entity</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <LogoPicker value={editEntity?.logoUrl || null} onChange={(v) => setEditEntity((s) => ({ ...s, logoUrl: v }))} entity={{ name: editEntity?.name, code: editEntity?.code }} />
          <TextField label="Entity name" fullWidth autoFocus value={editEntity?.name || ''} onChange={(e) => setEditEntity((s) => ({ ...s, name: e.target.value }))} />
          <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Type" fullWidth value={editEntity?.type || 'Branch'} onChange={(e) => setEditEntity((s) => ({ ...s, type: e.target.value }))}>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </TextField>
          <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Status" fullWidth value={editEntity?.status || 'Active'} onChange={(e) => setEditEntity((s) => ({ ...s, status: e.target.value }))}>
            {['Active', 'Inactive'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </TextField>
          <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Attendance source" helperText="Device/Login disable manual clock-in for this OpCo" fullWidth value={editEntity?.attendanceSource || 'Manual'} onChange={(e) => setEditEntity((s) => ({ ...s, attendanceSource: e.target.value }))}>
            {['Manual', 'Device', 'Login'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEntity(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={saveEntity} disabled={busy || !editEntity?.name?.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function AdminDashboard({ leaves, navTo, askAI }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let a = true;
    api
      .dashboard()
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, []);
  const pending = leaves.filter((l) => l.status === 'Pending HR').length;
  const insights = [
    {
      tone: AG.warning,
      icon: AlertTriangle,
      t: 'Review attendance for late arrivals this week',
      s: 'Possible burnout signal — check the Attendance board',
      go: () => navTo('Attendance'),
    },
    {
      tone: AG.purple,
      icon: TrendingUp,
      t: 'Keep your recruitment pipeline moving',
      s: 'Review candidates waiting in each stage',
      go: () => navTo('Recruitment'),
    },
    {
      tone: AG.success,
      icon: CalendarOff,
      t: `${pending} leave request${pending === 1 ? '' : 's'} pending your approval`,
      s: 'Review and approve to keep teams unblocked',
      go: () => navTo('Leave'),
    },
  ];
  const k = data?.kpis;
  const dateStr = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <Stack spacing={2}>
      <div className="fade-up">
        <SectionTitle title="Dashboard" sub={`Overview for ${dateStr}`} />
      </div>
      <Card
        className="fade-up"
        style={{
          padding: 20,
          background: `linear-gradient(120deg, ${AG.primaryDarkest} 0%, ${AG.primaryDark} 52%, ${AG.purpleDark} 135%)`,
          border: 'none',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 44px rgba(90,0,42,0.28)',
        }}
      >
        <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: AG.purple,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={15} className="text-white" />
          </div>
          <h3 style={{ color: '#fff', fontWeight: 600 }}>Your AI briefing</h3>
          <button
            onClick={() => askAI('Summarize what needs my attention today')}
            className="ml-auto flex items-center gap-1"
            style={{ color: AG.primaryLight, fontSize: 14, fontWeight: 500 }}
          >
            Ask follow-up <ChevronRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {insights.map((ins, i) => (
            <button
              key={i}
              onClick={ins.go}
              className="text-left navlift"
              style={{
                background: 'rgba(255,255,255,0.06)',
                borderRadius: 16,
                padding: 16,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')
              }
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: ins.tone,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 8,
                }}
              >
                <ins.icon size={15} className="text-white" />
              </div>
              <p
                style={{
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {ins.t}
              </p>
              <p style={{ color: AG.primaryLight, fontSize: 12, marginTop: 4 }}>
                {ins.s}
              </p>
            </button>
          ))}
        </div>
      </Card>

      <WidgetGrid>
        <KPI
          label="Total employees"
          value={k ? String(k.totalEmployees) : '—'}
          icon={Users}
          iconTone="primary"
          delay={0}
        />
        <KPI
          label="Attendance rate"
          value={k ? `${k.attendanceRate}%` : '—'}
          icon={Clock}
          iconTone="success"
          delay={60}
        />
        <KPI
          label="On leave today"
          value={k ? String(k.onLeaveToday) : '—'}
          icon={CalendarOff}
          iconTone="warning"
          delay={120}
        />
        <KPI
          label="Open positions"
          value={k ? String(k.openPositions) : '—'}
          icon={Briefcase}
          iconTone="neutral"
          delay={180}
        />
      </WidgetGrid>

      <Grid container spacing={2}>
        <Grid xs={12} lg={8}>
          <ChartCard
            className="fade-up"
            sx={{ animationDelay: '120ms' }}
            title="Headcount growth"
            sub="Active employees over the last 6 months"
          >
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data?.headcount || []}
                  margin={{ left: -20, right: 8, top: 8 }}
                >
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={AG.primary}
                        stopOpacity={0.22}
                      />
                      <stop
                        offset="100%"
                        stopColor={AG.primary}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke={AG.divider}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="m"
                    tick={{ fontSize: 12, fill: AG.n400 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: AG.n400 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={<ChartTip />}
                    cursor={{ stroke: AG.n300, strokeDasharray: '4 4' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="v"
                    name="Headcount"
                    stroke={AG.primary}
                    strokeWidth={2.5}
                    fill="url(#g1)"
                    dot={{ r: 0 }}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>
        <Grid xs={12} lg={4}>
          <ChartCard
            className="fade-up"
            sx={{ animationDelay: '180ms' }}
            title="Leave Entitlement"
            sub="Days allotted per employee"
          >
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data?.leavePie || []}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {(data?.leavePie || []).map((e, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {(data?.leavePie || []).map((e, i) => (
                <Grid key={i} xs={6}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: '100%' }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: PIE_COLORS[i % PIE_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {e.name}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </ChartCard>
        </Grid>
        <Grid xs={12} lg={6}>
          <ChartCard
            className="fade-up"
            sx={{ animationDelay: '120ms' }}
            title="Weekly attendance"
            sub="Present / late / absent"
          >
            <Box sx={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.weekAtt || []}
                  margin={{ left: -20, right: 8, top: 8 }}
                  barCategoryGap="28%"
                >
                  <defs>
                    <linearGradient id="barP" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={AG.success}
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor={AG.success}
                        stopOpacity={0.5}
                      />
                    </linearGradient>
                    <linearGradient id="barL" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={AG.warning}
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor={AG.warning}
                        stopOpacity={0.5}
                      />
                    </linearGradient>
                    <linearGradient id="barA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={AG.error} stopOpacity={1} />
                      <stop
                        offset="100%"
                        stopColor={AG.error}
                        stopOpacity={0.5}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke={AG.divider}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="d"
                    tick={{ fontSize: 12, fill: AG.n400 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: AG.n400 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ChartTip />} cursor={{ fill: AG.n50 }} />
                  <Bar dataKey="present" stackId="a" fill="url(#barP)" />
                  <Bar dataKey="late" stackId="a" fill="url(#barL)" />
                  <Bar
                    dataKey="absent"
                    stackId="a"
                    fill="url(#barA)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>
        <Grid xs={12} lg={6}>
          <ChartCard
            className="fade-up"
            sx={{ animationDelay: '180ms' }}
            title="Recruitment funnel"
            sub="Active pipeline this cycle"
          >
            <Stack spacing={1.5}>
              {(data?.funnel || []).map((f, i) => {
                const max = data?.funnel?.[0]?.v || 1;
                return (
                  <Box key={f.stage}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="text.secondary"
                      >
                        {f.stage}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {f.v}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(f.v / max) * 100}
                      sx={{
                        height: 10,
                        borderRadius: 1,
                        bgcolor: AG.n100,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 1,
                          bgcolor: PIE_COLORS[i % PIE_COLORS.length],
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </ChartCard>
        </Grid>
        <Grid xs={12}>
          <ChartCard
            className="fade-up"
            sx={{ animationDelay: '240ms' }}
            title="Employees by status"
            sub="Live headcount by employment status"
          >
            <StatusDistribution items={data?.statusBreakdown} />
          </ChartCard>
        </Grid>
      </Grid>
    </Stack>
  );
}

// Employee status as a modern distribution widget: a segmented brand bar + stat tiles.
function StatusDistribution({ items }) {
  const sb = items || [];
  const total = sb.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          height: 18,
          borderRadius: 99,
          overflow: 'hidden',
          bgcolor: AG.n100,
          mb: 3,
        }}
      >
        {sb
          .filter((s) => s.value > 0)
          .map((s, i) => (
            <Box
              key={i}
              title={`${s.name}: ${s.value}`}
              sx={{
                width: `${(s.value / total) * 100}%`,
                bgcolor: STATUS_COLOR[s.name] || AG.n400,
                transition: 'width .4s ease',
              }}
            />
          ))}
      </Box>
      <Grid container spacing={2}>
        {sb.map((s, i) => {
          const pct = Math.round((s.value / total) * 100);
          const color = STATUS_COLOR[s.name] || AG.n400;
          return (
            <Grid key={i} xs={6} md={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${AG.divider}`,
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    bgcolor: color,
                  }}
                />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: 99,
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {s.name}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontSize: 30,
                    fontWeight: 700,
                    color: 'text.primary',
                    mt: 0.75,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pct}% of workforce
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function EmployeesView({
  navSignal,
  employees,
  openEmployee,
  addEmployee,
  disableEmployee,
  restoreEmployee,
  orgTree = [],
  activeEntity = 'all',
}) {
  const [adding, setAdding] = useState(false);
  const [disabling, setDisabling] = useState(null);
  const [reactivating, setReactivating] = useState(null);
  const [tab, setTab] = useState('Active');
  // Clicking the "Employees" item in the sidebar should always land on the
  // Active tab, even when the directory is already open on Invited/Inactive.
  useEffect(() => {
    setTab('Active');
  }, [navSignal]);
  // Don't list the logged-in user (HR) in their own directory — they manage
  // their own record via "My Workspace", not here. The full `employees` array is
  // left untouched everywhere else (manager dropdowns, org tree, search).
  const { user } = useAuth();
  const directory = employees.filter((e) => e.id !== user?.id);
  const invitedList = directory.filter((e) => e.status === 'Invited');
  const activeList = directory.filter((e) => e.status !== 'Inactive' && e.status !== 'Invited');
  const inactiveList = directory.filter((e) => e.status === 'Inactive');
  const list = tab === 'Active' ? activeList : tab === 'Invited' ? invitedList : inactiveList;
  const tabs = [
    ['Active', activeList.length],
    ['Invited', invitedList.length],
    ['Inactive', inactiveList.length],
  ];
  return (
    <DataGridPage>
      <SectionTitle
        title="Employees"
        sub={`${activeList.length} active · ${invitedList.length} invited · ${inactiveList.length} inactive`}
      />
      <div className="flex gap-2" style={{ marginBottom: 4 }}>
        {tabs.map(([t, n]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="navlift"
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              background: tab === t ? AG.primary : '#fff',
              color: tab === t ? '#fff' : AG.n600,
              border: `1px solid ${tab === t ? AG.primary : AG.n200}`,
            }}
          >
            {t} <span style={{ opacity: 0.75 }}>({n})</span>
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <Card>
          <EmptyState
            icon={Users}
            title={tab === 'Active' ? 'No active employees' : tab === 'Invited' ? 'No pending invites' : 'No inactive employees'}
            desc={tab === 'Active' ? 'Add your first employee to get started.' : tab === 'Invited' ? 'New employees appear here until they accept their invite and set a password.' : 'Deactivated employees appear here and can be reactivated.'}
          />
        </Card>
      ) : (
        <EmployeesDataGrid
          key={tab}
          employees={list}
          onRowClick={openEmployee}
          onAdd={() => setAdding(true)}
          onDisable={tab === 'Active' ? (emp) => setDisabling(emp) : undefined}
          onRestore={
            tab === 'Inactive' ? (emp) => setReactivating(emp) : undefined
          }
        />
      )}
      {adding && (
        <EmployeeModal
          mode="add"
          employees={employees}
          orgTree={orgTree}
          activeEntity={activeEntity}
          onClose={() => setAdding(false)}
          onSubmit={addEmployee}
        />
      )}
      {disabling && (
        <DisableEmployeeModal
          employee={disabling}
          onClose={() => setDisabling(null)}
          onConfirm={disableEmployee}
        />
      )}
      {reactivating && (
        <ReactivateEmployeeModal
          employee={reactivating}
          onClose={() => setReactivating(null)}
          onConfirm={restoreEmployee}
        />
      )}
    </DataGridPage>
  );
}

// Promote an employee to manager. A manager reports to no one, so if they're on
// a team today, confirm that promoting will remove them from it.
function PromoteManagerModal({ employee, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const hasMgr =
    employee.managerId && employee.manager && employee.manager !== '—';
  const go = async () => {
    setBusy(true);
    setErr('');
    try {
      await onConfirm();
    } catch (e) {
      setErr(e.message || 'Could not promote');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          Make {employee.name} a manager?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          They'll be able to lead a team and review their reports.{' '}
          {hasMgr ? (
            <>
              <b>{employee.name}</b> will be removed from{' '}
              <b>{employee.manager}</b>'s team.
            </>
          ) : (
            'They currently report to no one, so nothing else changes.'
          )}
        </Typography>
        {err && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: 'error.light',
              color: 'error.dark',
              fontSize: 13,
              p: 1,
              borderRadius: 2,
            }}
          >
            {err}
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 2.5 }}
        >
          <Button variant="text" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={busy}
            onClick={go}
          >
            {busy ? 'Saving…' : 'Make manager'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

// Remove a manager. Their direct reports must be reassigned to another manager.
function RemoveManagerModal({
  employee,
  reports = [],
  managers = [],
  onClose,
  onConfirm,
}) {
  const [newManagerId, setNewManagerId] = useState(managers[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const needsReassign = reports.length > 0;
  const noTarget = needsReassign && managers.length === 0;
  const valid = !needsReassign || !!newManagerId;
  const go = async () => {
    if (!valid || noTarget) return;
    setBusy(true);
    setErr('');
    try {
      await onConfirm(needsReassign ? newManagerId : undefined);
    } catch (e) {
      setErr(e.message || 'Could not remove');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          Remove {employee.name} as manager?
        </Typography>
        {needsReassign ? (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, mb: 2 }}
            >
              <b>{employee.name}</b> has <b>{reports.length}</b> team member
              {reports.length > 1 ? 's' : ''}. Choose who they should report to
              from now on:
            </Typography>
            <label style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}>
              Move their team to
            </label>
            {noTarget ? (
              <Box
                sx={{
                  mt: 1,
                  bgcolor: 'warning.light',
                  color: 'warning.dark',
                  fontSize: 13,
                  p: 1,
                  borderRadius: 2,
                }}
              >
                No other manager is available. Make someone else a manager
                first.
              </Box>
            ) : (
              <select
                className="ag-input"
                style={{ marginTop: 4, width: '100%' }}
                value={newManagerId}
                onChange={(ev) => setNewManagerId(ev.target.value)}
              >
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} · {m.dept}
                  </option>
                ))}
              </select>
            )}
            <Box sx={{ mt: 1.5, fontSize: 12.5, color: AG.n400 }}>
              Affected: {reports.map((r) => r.name).join(', ')}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            They have no direct reports, so they'll simply stop being a manager.
          </Typography>
        )}
        {err && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: 'error.light',
              color: 'error.dark',
              fontSize: 13,
              p: 1,
              borderRadius: 2,
            }}
          >
            {err}
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 2.5 }}
        >
          <Button variant="text" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disableElevation
            disabled={busy || !valid || noTarget}
            onClick={go}
          >
            {busy ? 'Saving…' : 'Remove as manager'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

// Disable an employee: capture the last working day, confirm, then set Inactive.
function DisableEmployeeModal({ employee, onClose, onConfirm }) {
  const [lastDay, setLastDay] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const apply = async () => {
    setBusy(true);
    setErr('');
    try {
      await onConfirm(employee.id, lastDay);
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not disable');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          {confirming ? 'Deactivate this employee?' : `Deactivate ${employee.name}`}
        </Typography>
        {!confirming ? (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 2 }}
            >
              Enter the employee's last working day to continue.
            </Typography>
            <label style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}>
              Last working day
            </label>
            <input
              type="date"
              value={lastDay}
              onChange={(e) => setLastDay(e.target.value)}
              className="ag-input"
              style={{ marginTop: 4, width: '100%' }}
              autoFocus
            />
          </>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 1 }}
          >
            This will set <b>{employee.name}</b> to <b>Inactive</b> with a last
            day of <b>{lastDay}</b>. They will no longer be active in the
            system.
          </Typography>
        )}
        {err && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: 'error.light',
              color: 'error.dark',
              fontSize: 13,
              p: 1,
              borderRadius: 2,
            }}
          >
            {err}
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 2.5 }}
        >
          <Button
            variant="text"
            color="inherit"
            onClick={confirming ? () => setConfirming(false) : onClose}
          >
            {confirming ? 'Back' : 'Cancel'}
          </Button>
          {confirming ? (
            <Button
              variant="contained"
              color="error"
              disableElevation
              disabled={busy}
              onClick={apply}
            >
              {busy ? 'Deactivating…' : 'Confirm'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              disableElevation
              disabled={!lastDay}
              onClick={() => setConfirming(true)}
            >
              Deactivate
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
}

// Reactivate an inactive employee. HR must give a reason before confirming so
// there's a record of why the account was brought back.
function ReactivateEmployeeModal({ employee, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const trimmed = reason.trim();
  const apply = async () => {
    setBusy(true);
    setErr('');
    try {
      await onConfirm(employee.id, trimmed);
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not reactivate');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          Reactivate {employee.name}?
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2 }}
        >
          This will set <b>{employee.name}</b> back to <b>Active</b>. Add a
          reason for reactivating to continue.
        </Typography>
        <label style={{ fontSize: 12, fontWeight: 500, color: AG.textSec }}>
          Reason for reactivation
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="ag-input"
          rows={3}
          placeholder="e.g. Rejoined after a sabbatical"
          style={{ marginTop: 4, width: '100%', resize: 'vertical' }}
          autoFocus
        />
        {err && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: 'error.light',
              color: 'error.dark',
              fontSize: 13,
              p: 1,
              borderRadius: 2,
            }}
          >
            {err}
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 2.5 }}
        >
          <Button variant="text" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!trimmed || busy}
            onClick={apply}
          >
            {busy ? 'Reactivating…' : 'Reactivate'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

function AttendanceAdmin({ openEmployee }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 86400000)
    .toISOString()
    .slice(0, 10);
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(todayStr);
  const [data, setData] = useState(null);
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Employee',
        flex: 1.1,
        minWidth: 190,
        renderCell: ({ row }) => <PersonCell name={row.name} />,
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value }) => <TextCell value={value} fontWeight={500} />,
      },
      {
        field: 'ci',
        headerName: 'Clock in',
        flex: 0.55,
        minWidth: 95,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'co',
        headerName: 'Clock out',
        flex: 0.55,
        minWidth: 95,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'dept',
        headerName: 'Department',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.6,
        minWidth: 110,
        renderCell: ({ value }) => <GridStatusPill status={value} />,
      },
    ],
    [],
  );
  const rangeBad = from && to && from > to;
  useEffect(() => {
    if (!from || !to || from > to) return;
    let a = true;
    setData(null);
    api
      .attendanceRange(from, to)
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, [from, to]);
  const rows = data?.rows || [];
  const s = data?.summary || { present: 0, late: 0, absent: 0 };
  const noData = !!data && rows.length === 0;
  return (
    <div className="page-scroll fade-up">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <SectionTitle
          title="Attendance"
          sub="Search attendance by date range"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <label style={{ fontSize: 12, color: AG.textSec }}>From</label>
          <input
            type="date"
            value={from}
            max={todayStr}
            onChange={(e) => setFrom(e.target.value)}
            className="ag-input"
            style={{ width: 'auto', borderRadius: 10 }}
          />
          <label style={{ fontSize: 12, color: AG.textSec }}>To</label>
          <input
            type="date"
            value={to}
            max={todayStr}
            onChange={(e) => setTo(e.target.value)}
            className="ag-input"
            style={{ width: 'auto', borderRadius: 10 }}
          />
          <button
            onClick={() => {
              setFrom(todayStr);
              setTo(todayStr);
            }}
            className="ag-btn ag-btn-soft"
            style={{ padding: '8px 14px', fontSize: 13 }}
          >
            Today
          </button>
        </div>
      </div>
      {rangeBad ? (
        <Card>
          <EmptyState
            icon={Calendar}
            title="Invalid date range"
            desc="The 'From' date must be on or before the 'To' date."
          />
        </Card>
      ) : (
        <>
          <WidgetGrid>
            <KPI
              label="Present"
              value={noData ? '—' : String(s.present)}
              icon={Check}
              iconTone="success"
              delay={0}
            />
            <KPI
              label="Late arrivals"
              value={noData ? '—' : String(s.late)}
              icon={Clock}
              iconTone="warning"
              delay={60}
            />
            <KPI
              label="Absent"
              value={noData ? '—' : String(s.absent)}
              icon={X}
              iconTone="error"
              delay={120}
            />
            <KPI
              label="Records"
              value={noData ? '0' : String(s.total ?? rows.length)}
              icon={Users}
              iconTone="info"
              delay={180}
            />
          </WidgetGrid>
          {noData ? (
            <Card>
              <EmptyState
                icon={Calendar}
                title="No attendance found"
                desc={`No attendance was recorded between ${from} and ${to}.`}
              />
            </Card>
          ) : (
            <DataGridCard
              fillHeight={false}
              title={`Attendance · ${from} → ${to}`}
            >
              <AppDataGrid
                rows={rows}
                columns={columns}
                autoHeight
                fillHeight={false}
                onRowClick={(row) => openEmployee(row.employeeId)}
                hideFooter={rows.length <= 10}
              />
            </DataGridCard>
          )}
        </>
      )}
    </div>
  );
}

function LeaveAdmin({ leaves, decideLeave }) {
  // HR acts on "Pending HR" (manager-approved). "With managers" is read-only visibility.
  const tabs = [
    ['Awaiting HR', 'Pending HR'],
    ['With managers', 'Pending Manager'],
    ['Approved', 'Approved'],
    ['Rejected', 'Rejected'],
  ];
  const [tab, setTab] = useState('Pending HR');
  const rows = leaves.filter((l) => l.status === tab);
  const tabLabel = Object.fromEntries(tabs.map(([l, v]) => [v, l]));
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Leave management"
        sub="Final approval — requests already approved by the employee's manager"
      />
      <div className="flex gap-2 flex-wrap">
        {tabs.map(([labelText, value]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className="navlift"
            style={{
              padding: '8px 16px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 500,
              background: tab === value ? AG.primary : '#fff',
              color: tab === value ? '#fff' : AG.n600,
              border: `1px solid ${tab === value ? AG.primary : AG.n200}`,
            }}
          >
            {labelText}{' '}
            <span style={{ opacity: 0.7 }}>
              ({leaves.filter((l) => l.status === value).length})
            </span>
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={CalendarOff}
            title="Nothing here"
            desc={
              tab === 'Pending HR'
                ? "You're all caught up — no requests waiting for your approval."
                : `Nothing in "${tabLabel[tab]}" right now.`
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((l) => (
            <Card key={l.id} style={{ padding: 16 }}>
              <div className="flex items-center gap-4 flex-wrap">
                <Avatar name={l.emp} />
                <div className="flex-1" style={{ minWidth: 200 }}>
                  <div className="flex items-center gap-2">
                    <p style={{ fontWeight: 600, color: AG.text }}>{l.emp}</p>
                    <Pill tone="neutral">{l.dept}</Pill>
                  </div>
                  <p style={{ color: AG.textSec, marginTop: 2, fontSize: 14 }}>
                    {l.type} · {l.from} → {l.to} ·{' '}
                    <span style={{ fontWeight: 500, color: AG.n700 }}>
                      {l.days} day{l.days > 1 ? 's' : ''}
                    </span>
                  </p>
                  <p style={{ color: AG.n400, marginTop: 4, fontSize: 12 }}>
                    "{l.reason}"
                  </p>
                </div>
                {l.status === 'Pending HR' ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => decideLeave(l.id, 'Rejected')}
                      className="ag-btn flex items-center gap-1"
                      style={{
                        color: AG.errorDark,
                        background: 'rgba(240,68,56,.12)',
                        border: 'none',
                      }}
                    >
                      <X size={15} />
                      Reject
                    </button>
                    <button
                      onClick={() => decideLeave(l.id, 'Approved')}
                      className="ag-btn ag-btn-primary flex items-center gap-1"
                    >
                      <Check size={15} />
                      Approve
                    </button>
                  </div>
                ) : (
                  <Pill tone={statusTone(l.status)} dot>
                    {l.status}
                  </Pill>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ATSTabs({ tab, setTab, counts }) {
  const tabs = [
    ['Overview', null],
    ['Talent Pool', null],
    ['Jobs', counts.jobs],
    ['Pipeline', counts.pipeline],
    ['Interviews', counts.interviews],
    ['Offers', counts.offers],
    ['Onboarding', counts.onboarding],
    ['Tests', null],
  ];
  return (
    <div className="tab-strip page-shrink">
      {tabs.map(([t, n]) => {
        const active = tab === t;
        return (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`navlift tab-strip-btn${active ? ' tab-strip-btn--active' : ''}`}
          >
            <span>{t}</span>
            <span
              className={`tab-strip-count${active ? ' tab-strip-count--active' : ''}${n == null ? ' tab-strip-count--placeholder' : ''}`}
              aria-hidden={n == null}
            >
              {n ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ATS({ p }) {
  const {
    jobs,
    candidates,
    interviews,
    offers,
    onboarding,
    atsTab,
    setAtsTab,
  } = p;
  const counts = {
    jobs: jobs.filter((j) => j.status !== 'Closed').length,
    pipeline: candidates.filter((c) => !c.rejected && c.stage !== 'Hired')
      .length,
    interviews: interviews.filter((i) => i.status === 'Scheduled').length,
    offers: offers.filter((o) =>
      ['Draft', 'Approved', 'Sent'].includes(o.status),
    ).length,
    onboarding: onboarding.filter((o) => o.status !== 'Completed').length,
  };
  const gridTab = atsTab === 'Jobs';
  return (
    <div className="page-fill fade-up">
      <SectionTitle title="Recruitment · Applicant Tracking System" />
      <ATSTabs tab={atsTab} setTab={setAtsTab} counts={counts} />
      <div className={gridTab ? 'page-fill' : 'page-scroll'}>
        {atsTab === 'Overview' && <ATSOverview p={p} />}
        {atsTab === 'Talent Pool' && <TalentPool showToast={p.showToast} />}
        {atsTab === 'Jobs' && <ATSJobs p={p} />}
        {atsTab === 'Pipeline' && <ATSPipeline p={p} />}
        {atsTab === 'Interviews' && <ATSInterviews p={p} />}
        {atsTab === 'Offers' && <ATSOffers p={p} />}
        {atsTab === 'Onboarding' && <ATSOnboarding p={p} />}
        {atsTab === 'Tests' && <ATSTests p={p} />}
      </div>
    </div>
  );
}

// Recruitment online tests (4.1): HR builds multiple-choice tests, assigns them
// to a candidate (mints a link), and reviews auto-scored results.
function ATSTests({ p }) {
  const showToast = p.showToast;
  const [tests, setTests] = useState(null);
  const [editing, setEditing] = useState(null); // a test object being edited (questions) or 'new'
  const [assign, setAssign] = useState(null); // { candidateId, testId }
  const [results, setResults] = useState({}); // candidateId → assignments
  const candidates = (p.candidates || []).filter((c) => !c.rejected);
  const loadTests = () => api.recruitmentTests().then(setTests).catch(() => setTests([]));
  useEffect(() => { loadTests(); }, []);

  const createTest = async () => {
    try { const t = await api.createRecruitmentTest({ title: 'New test', passScore: 60, durationMins: 20 }); await loadTests(); setEditing(await api.recruitmentTest(t.id)); }
    catch (e) { showToast?.(e.message); }
  };
  const openTest = async (id) => { try { setEditing(await api.recruitmentTest(id)); } catch (e) { showToast?.(e.message); } };
  const removeTest = async (t) => { try { await api.deleteRecruitmentTest(t.id); loadTests(); } catch (e) { showToast?.(e.message); } };
  const doAssign = async () => {
    if (!assign?.candidateId || !assign?.testId) return;
    try { const a = await api.assignTest(assign.candidateId, assign.testId); setAssign(null); loadResults(assign.candidateId); navigator.clipboard?.writeText(window.location.origin + a.link); showToast?.('Assigned — link copied to clipboard'); }
    catch (e) { showToast?.(e.message); }
  };
  const loadResults = (candidateId) => api.candidateAssignments(candidateId).then((r) => setResults((m) => ({ ...m, [candidateId]: r }))).catch(() => {});

  if (!tests) return <CenterSpinner />;
  if (editing) return <TestEditor test={editing} onClose={() => { setEditing(null); loadTests(); }} showToast={showToast} />;

  const origin = window.location.origin;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 12 }}>
        <p style={{ fontSize: 13, color: AG.n500, margin: 0, maxWidth: 560 }}>Author multiple-choice tests, assign one to a candidate to generate a private link, and review the auto-scored result.</p>
        <PrimaryBtn onClick={createTest}><Plus size={14} style={{ marginRight: 6 }} /> New test</PrimaryBtn>
      </div>

      {/* Tests */}
      {tests.length === 0 ? <Card><EmptyState icon={FileText} title="No tests yet" desc="Create a test and add questions to start assessing candidates." /></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {tests.map((t) => (
            <Card key={t.id} style={{ padding: 16 }}>
              <div className="flex items-center justify-between" style={{ gap: 8 }}>
                <p style={{ fontWeight: 600, color: AG.text }}>{t.title}</p>
                <Pill tone={t.active ? 'success' : 'neutral'}>{t.active ? 'Active' : 'Off'}</Pill>
              </div>
              <p style={{ fontSize: 12.5, color: AG.n500, marginTop: 4 }}>{t.questionCount} question(s) · pass {t.passScore}% · {t.durationMins} min · {t.assignmentCount} assigned</p>
              <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
                <OutlineBtn onClick={() => openTest(t.id)} style={{ padding: '5px 12px', fontSize: 12.5 }}>Edit questions</OutlineBtn>
                <button onClick={() => removeTest(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, marginLeft: 'auto' }}><Trash2 size={15} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Assign + results */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10, marginBottom: 12 }}>
          <p style={{ fontWeight: 600, color: AG.text }}>Assign a test to a candidate</p>
          <PrimaryBtn onClick={() => setAssign({ candidateId: '', testId: tests.find((t) => t.questionCount > 0)?.id || '' })} disabled={!tests.some((t) => t.questionCount > 0)}><Plus size={14} style={{ marginRight: 6 }} /> Assign</PrimaryBtn>
        </div>
        <div className="flex flex-wrap gap-2">
          {candidates.slice(0, 30).map((c) => (
            <OutlineBtn key={c.id} onClick={() => loadResults(c.id)} style={{ padding: '5px 12px', fontSize: 12.5 }}>{c.name} · results</OutlineBtn>
          ))}
        </div>
        {Object.entries(results).map(([cid, list]) => (
          <div key={cid} style={{ marginTop: 14 }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: AG.n600, marginBottom: 6 }}>{candidates.find((c) => c.id === cid)?.name || cid}</p>
            {list.length === 0 ? <p style={{ fontSize: 12.5, color: AG.n400 }}>No tests assigned.</p> : (
              <div style={{ display: 'grid', gap: 6 }}>
                {list.map((a) => (
                  <div key={a.id} className="flex items-center gap-2" style={{ padding: '8px 10px', border: `1px solid ${AG.divider}`, borderRadius: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>{a.testTitle}</span>
                    <Pill tone={a.status === 'Submitted' ? (a.passed ? 'success' : 'error') : 'warning'}>{a.status === 'Submitted' ? `${a.percent}% · ${a.passed ? 'Pass' : 'Fail'}` : a.status}</Pill>
                    {a.status !== 'Submitted' && <button onClick={() => { navigator.clipboard?.writeText(origin + a.link); showToast?.('Link copied'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, fontSize: 12.5 }}>Copy link</button>}
                    <button onClick={async () => { await api.deleteTestAssignment(a.id); loadResults(cid); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, marginLeft: 'auto' }}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>

      <Dialog open={!!assign} onClose={() => setAssign(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign a test</DialogTitle>
        <DialogContent>
          {assign && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <label><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Candidate</span>
                <select className="ag-input" value={assign.candidateId} onChange={(e) => setAssign({ ...assign, candidateId: e.target.value })}>
                  <option value="">Select…</option>
                  {candidates.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.role}</option>)}
                </select>
              </label>
              <label><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Test</span>
                <select className="ag-input" value={assign.testId} onChange={(e) => setAssign({ ...assign, testId: e.target.value })}>
                  {tests.filter((t) => t.questionCount > 0).map((t) => <option key={t.id} value={t.id}>{t.title} ({t.questionCount}q)</option>)}
                </select>
              </label>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setAssign(null)}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={doAssign} disabled={!assign?.candidateId || !assign?.testId}>Assign & get link</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Per-test editor: title/pass settings + add/remove multiple-choice questions.
function TestEditor({ test, onClose, showToast }) {
  const [t, setT] = useState(test);
  const blankQ = { prompt: '', options: ['', ''], correctIndex: 0, points: 1 };
  const [qForm, setQForm] = useState(null);
  const reload = async () => { try { setT(await api.recruitmentTest(test.id)); } catch { /* keep */ } };

  const saveMeta = async (patch) => { try { const u = await api.updateRecruitmentTest(test.id, patch); setT((cur) => ({ ...cur, ...u })); } catch (e) { showToast?.(e.message); } };
  const saveQuestion = async () => {
    const opts = qForm.options.map((o) => o.trim()).filter(Boolean);
    if (!qForm.prompt.trim() || opts.length < 2) { showToast?.('Add a prompt and at least two options'); return; }
    if (qForm.correctIndex >= opts.length) { showToast?.('Pick which option is correct'); return; }
    try {
      const payload = { prompt: qForm.prompt, options: opts, correctIndex: qForm.correctIndex, points: Number(qForm.points) || 1 };
      if (qForm.id) await api.updateTestQuestion(test.id, qForm.id, payload); else await api.addTestQuestion(test.id, payload);
      setQForm(null); reload();
    } catch (e) { showToast?.(e.message); }
  };
  const removeQuestion = async (q) => { try { await api.deleteTestQuestion(test.id, q.id); reload(); } catch (e) { showToast?.(e.message); } };

  return (
    <div className="space-y-4">
      <button onClick={onClose} className="flex items-center gap-1.5 navlift" style={{ color: AG.textSec, fontSize: 14, fontWeight: 500 }}><ArrowLeft size={16} /> Back to tests</button>
      <Card style={{ padding: 18 }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField size="small" label="Title" value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} onBlur={() => saveMeta({ title: t.title })} />
          <div className="flex gap-2">
            <TextField size="small" type="number" label="Pass %" value={t.passScore} onChange={(e) => setT({ ...t, passScore: e.target.value })} onBlur={() => saveMeta({ passScore: Number(t.passScore) || 0 })} sx={{ width: 110 }} inputProps={{ min: 0, max: 100 }} />
            <TextField size="small" type="number" label="Minutes" value={t.durationMins} onChange={(e) => setT({ ...t, durationMins: e.target.value })} onBlur={() => saveMeta({ durationMins: Number(t.durationMins) || 1 })} sx={{ width: 110 }} inputProps={{ min: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: AG.n600 }}>
              <input type="checkbox" checked={t.active} onChange={(e) => { setT({ ...t, active: e.target.checked }); saveMeta({ active: e.target.checked }); }} /> Active
            </label>
          </div>
        </div>
        <TextField size="small" fullWidth label="Description (optional)" value={t.description || ''} onChange={(e) => setT({ ...t, description: e.target.value })} onBlur={() => saveMeta({ description: t.description })} sx={{ mt: 1.5 }} />
      </Card>

      <div className="flex items-center justify-between">
        <p style={{ fontWeight: 600, color: AG.text }}>Questions ({t.questions?.length || 0})</p>
        <OutlineBtn onClick={() => setQForm({ ...blankQ })}><Plus size={14} style={{ marginRight: 4 }} /> Add question</OutlineBtn>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {(t.questions || []).map((q, i) => (
          <Card key={q.id} style={{ padding: 14 }}>
            <div className="flex items-start justify-between gap-2">
              <div style={{ minWidth: 0 }}>
                <p style={{ fontWeight: 600, color: AG.text, fontSize: 14 }}>{i + 1}. {q.prompt} <span style={{ fontSize: 12, color: AG.n400 }}>({q.points} pt)</span></p>
                <div style={{ marginTop: 6, display: 'grid', gap: 3 }}>
                  {q.options.map((o, idx) => <span key={idx} style={{ fontSize: 12.5, color: idx === q.correctIndex ? AG.successDark : AG.n500 }}>{idx === q.correctIndex ? '✓ ' : '○ '}{o}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setQForm({ id: q.id, prompt: q.prompt, options: [...q.options], correctIndex: q.correctIndex, points: q.points })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Pencil size={14} /></button>
                <button onClick={() => removeQuestion(q)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={14} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!qForm} onClose={() => setQForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{qForm?.id ? 'Edit question' : 'Add question'}</DialogTitle>
        <DialogContent>
          {qForm && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Prompt" value={qForm.prompt} onChange={(e) => setQForm({ ...qForm, prompt: e.target.value })} sx={{ mb: 1.5 }} />
              <p style={{ fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 6 }}>Options (select the correct one)</p>
              {qForm.options.map((o, idx) => (
                <div key={idx} className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                  <input type="radio" name="correct" checked={qForm.correctIndex === idx} onChange={() => setQForm({ ...qForm, correctIndex: idx })} style={{ accentColor: AG.primary }} />
                  <TextField size="small" fullWidth placeholder={`Option ${idx + 1}`} value={o} onChange={(e) => setQForm({ ...qForm, options: qForm.options.map((x, j) => (j === idx ? e.target.value : x)) })} />
                  {qForm.options.length > 2 && <button onClick={() => {
                    const opts = qForm.options.filter((_, j) => j !== idx);
                    // Keep the SAME option marked correct when an earlier one is removed.
                    let ci = qForm.correctIndex;
                    if (idx < ci) ci -= 1; else if (idx === ci) ci = 0;
                    setQForm({ ...qForm, options: opts, correctIndex: Math.min(ci, opts.length - 1) });
                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><X size={14} /></button>}
                </div>
              ))}
              <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
                <button onClick={() => setQForm({ ...qForm, options: [...qForm.options, ''] })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, fontSize: 12.5, fontWeight: 600 }}>+ Add option</button>
                <TextField size="small" type="number" label="Points" value={qForm.points} onChange={(e) => setQForm({ ...qForm, points: e.target.value })} sx={{ width: 90 }} inputProps={{ min: 1 }} />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setQForm(null)}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={saveQuestion}>Save question</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function ATSOverview({ p }) {
  const { jobs, candidates } = p;
  const open = jobs.filter((j) => j.status === 'Published').length;
  const activeC = candidates.filter(
    (c) => !c.rejected && c.stage !== 'Hired',
  ).length;
  const toAction = jobs.filter((j) =>
    ['Draft', 'Pending Approval', 'Approved'].includes(j.status),
  );
  const STAGE_FUNNEL = STAGES.map((stage) => ({
    stage,
    v: candidates.filter((c) => c.stage === stage && !c.rejected).length,
  }));
  return (
    <div className="space-y-4">
      <WidgetGrid>
        <KPI
          label="Open positions"
          value={String(open)}
          icon={Briefcase}
          iconTone="primary"
          delay={0}
        />
        <KPI
          label="Active candidates"
          value={String(activeC)}
          icon={Users}
          iconTone="info"
          delay={60}
        />
        <KPI
          label="Avg time-to-fill"
          value="28d"
          icon={Clock}
          iconTone="warning"
          delay={120}
        />
        <KPI
          label="Requisitions"
          value={String(jobs.length)}
          icon={Check}
          iconTone="success"
          delay={180}
        />
      </WidgetGrid>
      <Grid container spacing={2}>
        <Grid xs={12} lg={6}>
          <ChartCard title="Recruitment funnel" sub="Across all open roles">
            <Stack spacing={1.5}>
              {STAGE_FUNNEL.map((f, i) => {
                const max = STAGE_FUNNEL[0].v || 1;
                return (
                  <Box key={f.stage}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="text.secondary"
                      >
                        {f.stage}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {f.v}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(f.v / max) * 100}
                      sx={{
                        height: 10,
                        borderRadius: 1,
                        bgcolor: AG.n100,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 1,
                          bgcolor: PIE_COLORS[i % PIE_COLORS.length],
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </ChartCard>
        </Grid>
        <Grid xs={12} lg={6}>
          <ChartCard title="Requisitions to action" sub="Not yet live">
            {toAction.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 2 }}
              >
                All requisitions are live.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {toAction.map((j) => (
                  <Stack
                    key={j.id}
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ p: 1.5, bgcolor: AG.n50, borderRadius: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: AG.n500,
                        flexShrink: 0,
                      }}
                    >
                      <Briefcase size={16} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {j.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {j.dept} · {j.openings} opening
                        {j.openings > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Pill tone={jobTone(j.status)}>{j.status}</Pill>
                  </Stack>
                ))}
              </Stack>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </div>
  );
}

function ATSJobs({ p }) {
  const { jobs, candidates, advanceJob, closeJob, addJob, openJob } = p;
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    dept: 'Engineering',
    manager: '',
    openings: 1,
  });
  // JD library (Phase 2) — optional prefill for a new requisition.
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.jdTemplates().then(setTemplates).catch(() => setTemplates([])); }, []);
  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) { setForm((s) => ({ ...s, jdText: undefined, jdSkills: undefined, minYears: undefined })); return; }
    setForm((s) => ({ ...s, title: s.title || t.title, dept: t.dept || s.dept, jdText: t.jdText, jdSkills: t.jdSkills, minYears: t.minYears }));
  };
  const count = (jid) =>
    candidates.filter((c) => c.jobId === jid && !c.rejected).length;
  const nextLabel = {
    Draft: 'Submit for approval',
    'Pending Approval': 'Approve',
    Approved: 'Publish',
  };
  const jobColumns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Job',
        flex: 1.2,
        minWidth: 220,
        renderCell: ({ row }) => (
          <PersonCell
            name={row.title}
            subtitle={`${row.openings} opening${row.openings > 1 ? 's' : ''} · ${row.location}`}
          />
        ),
      },
      {
        field: 'dept',
        headerName: 'Department',
        flex: 0.75,
        minWidth: 120,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'manager',
        headerName: 'Hiring manager',
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'applicants',
        headerName: 'Applicants',
        flex: 0.5,
        minWidth: 90,
        renderCell: ({ row }) => <TextCell value={String(count(row.id))} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.75,
        minWidth: 130,
        renderCell: ({ row }) => (
          <GridStatusPill
            status={row.status}
            tone={
              jobTone(row.status) === 'neutral'
                ? 'secondary'
                : jobTone(row.status)
            }
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Box
            onClick={(ev) => ev.stopPropagation()}
            sx={{ width: '100%', textAlign: 'right' }}
          >
            {nextLabel[row.status] ? (
              <button
                className="ag-btn ag-btn-soft"
                style={{ padding: '5px 12px', fontSize: 12 }}
                onClick={() => advanceJob(row.id)}
              >
                {nextLabel[row.status]}
              </button>
            ) : row.status === 'Published' ? (
              <button
                className="ag-btn ag-btn-soft"
                style={{ padding: '5px 12px', fontSize: 12 }}
                onClick={() => closeJob(row.id)}
              >
                Close
              </button>
            ) : (
              <ChevronRight
                size={16}
                style={{ color: AG.n300, display: 'inline' }}
              />
            )}
          </Box>
        ),
      },
    ],
    [candidates],
  );
  return (
    <>
      <div className="page-shrink flex justify-end">
        <PrimaryBtn icon={Plus} onClick={() => setShowForm((s) => !s)}>
          New requisition
        </PrimaryBtn>
      </div>
      {showForm && (
        <Card className="page-shrink" style={{ padding: 20 }}>
          <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>
            New job requisition
          </h3>
          {templates.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600, marginRight: 8 }}>Prefill from JD template</label>
              <select className="ag-input" style={{ maxWidth: 280 }} defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">— None —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
              {form.jdText ? <span style={{ fontSize: 12, color: AG.success || '#12B76A', marginLeft: 10 }}>JD applied</span> : null}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              className="ag-input"
              placeholder="Job title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <input
              className="ag-input"
              placeholder="Department"
              value={form.dept}
              onChange={(e) => setForm({ ...form, dept: e.target.value })}
            />
            <input
              className="ag-input"
              placeholder="Hiring manager"
              value={form.manager}
              onChange={(e) => setForm({ ...form, manager: e.target.value })}
            />
            <input
              className="ag-input"
              type="number"
              min="1"
              placeholder="Openings"
              value={form.openings}
              onChange={(e) =>
                setForm({ ...form, openings: Number(e.target.value) })
              }
            />
          </div>
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button
              className="ag-btn ag-btn-primary"
              disabled={!form.title}
              onClick={() => {
                addJob({
                  title: form.title,
                  dept: form.dept || '—',
                  manager: form.manager || '—',
                  location: 'Lahore',
                  type: 'Full-time',
                  openings: form.openings || 1,
                  ...(form.jdText ? { jdText: form.jdText, jdSkills: form.jdSkills, minYears: form.minYears } : {}),
                });
                setForm({
                  title: '',
                  dept: 'Engineering',
                  manager: '',
                  openings: 1,
                });
                setShowForm(false);
              }}
            >
              Create requisition
            </button>
            <button
              className="ag-btn ag-btn-soft"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </Card>
      )}
      <DataGridCard>
        <AppDataGrid
          rows={jobs}
          columns={jobColumns}
          onRowClick={(row) => openJob(row.id)}
          hideFooter={jobs.length <= 10}
        />
      </DataGridCard>
    </>
  );
}

function ATSPipeline({ p }) {
  const { candidates, jobs, moveCandidate, rejectCandidate, openCandidate } = p;
  const [jobFilter, setJobFilter] = useState('all');
  const pool = candidates.filter(
    (c) => !c.rejected && (jobFilter === 'all' || c.jobId === jobFilter),
  );
  const rejectedCount = candidates.filter((c) => c.rejected).length;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ fontSize: 13, color: AG.textSec, fontWeight: 500 }}>
          Filter by job:
        </span>
        <select
          className="ag-input"
          style={{ width: 'auto', minWidth: 200 }}
          value={jobFilter}
          onChange={(e) => setJobFilter(e.target.value)}
        >
          <option value="all">All open roles</option>
          {jobs
            .filter((j) => j.status === 'Published')
            .map((j) => (
              <option key={j.id} value={j.id}>
                {j.title}
              </option>
            ))}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: AG.n400 }}>
          {rejectedCount} rejected (hidden)
        </span>
      </div>
      <div className="grid grid-cols-5 gap-3" style={{ minWidth: 980 }}>
        {STAGES.map((stage, si) => {
          const col = pool.filter((c) => c.stage === stage);
          return (
            <div
              key={stage}
              style={{ background: AG.n100, borderRadius: 16, padding: 10 }}
              className="flex flex-col"
            >
              <div
                className="flex items-center justify-between"
                style={{ padding: '6px 6px' }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: AG.n700 }}>
                  {stage}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: AG.n400,
                    background: '#fff',
                    borderRadius: 99,
                    padding: '2px 8px',
                  }}
                >
                  {col.length}
                </span>
              </div>
              <div className="space-y-2" style={{ marginTop: 4 }}>
                {col.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => openCandidate(c.id)}
                    className="ag-card ag-card-h fade-up"
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      boxShadow: '0 1px 2px rgba(0,0,0,.04)',
                    }}
                  >
                    <div
                      className="flex items-center gap-2"
                      style={{ marginBottom: 6 }}
                    >
                      <Avatar name={c.name} size="sm" />
                      <div className="min-w-0">
                        <p
                          className="truncate"
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: AG.text,
                          }}
                        >
                          {c.name}
                        </p>
                        <p
                          className="truncate"
                          style={{ fontSize: 11, color: AG.n400 }}
                        >
                          {jobName(jobs, c.jobId)}
                        </p>
                      </div>
                    </div>
                    <div
                      className="flex items-center justify-between"
                      style={{ marginBottom: 8 }}
                    >
                      <Pill
                        tone={
                          c.score >= 88
                            ? 'success'
                            : c.score >= 78
                              ? 'warning'
                              : 'neutral'
                        }
                      >
                        <Sparkles size={11} />
                        {c.score}
                      </Pill>
                      <span style={{ fontSize: 11, color: AG.n400 }}>
                        {c.exp}
                      </span>
                    </div>
                    <div
                      className="flex gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => moveCandidate(c.id, -1)}
                        disabled={si === 0}
                        className="ag-btn"
                        style={{
                          fontSize: 12,
                          padding: '5px 8px',
                          background: AG.n100,
                          color: AG.n500,
                          border: 'none',
                          borderRadius: 8,
                        }}
                      >
                        ←
                      </button>
                      <button
                        onClick={() => moveCandidate(c.id, 1)}
                        disabled={si === STAGES.length - 1}
                        className="ag-btn flex-1"
                        style={{
                          fontSize: 12,
                          padding: '5px 0',
                          background: AG.primaryLightest,
                          color: AG.primary,
                          border: 'none',
                          borderRadius: 8,
                        }}
                      >
                        Advance →
                      </button>
                      <button
                        onClick={() => rejectCandidate(c.id)}
                        title="Reject"
                        className="ag-btn"
                        style={{
                          fontSize: 12,
                          padding: '5px 8px',
                          background: 'rgba(240,68,56,.12)',
                          color: AG.errorDark,
                          border: 'none',
                          borderRadius: 8,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ATSInterviews({ p }) {
  const {
    interviews,
    candidates,
    jobs,
    scheduleInterview,
    completeInterview,
    moveCandidate,
    showToast,
  } = p;
  const [evalIv, setEvalIv] = useState(null); // { id, name } being evaluated
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    candId: '',
    round: 'Technical Round',
    interviewer: '',
    date: '',
  });
  const interviewCands = candidates.filter(
    (c) => !c.rejected && (c.stage === 'Interview' || c.stage === 'Screening'),
  );
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <PrimaryBtn icon={Calendar} onClick={() => setShowForm((s) => !s)}>
          Schedule interview
        </PrimaryBtn>
      </div>
      {showForm && (
        <Card style={{ padding: 20 }}>
          <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>
            Schedule an interview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              className="ag-input"
              value={form.candId}
              onChange={(e) => setForm({ ...form, candId: e.target.value })}
            >
              <option value="">Select candidate…</option>
              {interviewCands.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {jobName(jobs, c.jobId)}
                </option>
              ))}
            </select>
            <input
              className="ag-input"
              placeholder="Round (e.g. Technical)"
              value={form.round}
              onChange={(e) => setForm({ ...form, round: e.target.value })}
            />
            <input
              className="ag-input"
              placeholder="Interviewer"
              value={form.interviewer}
              onChange={(e) =>
                setForm({ ...form, interviewer: e.target.value })
              }
            />
            <input
              className="ag-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button
              className="ag-btn ag-btn-primary"
              disabled={!form.candId || !form.date}
              onClick={() => {
                scheduleInterview({
                  candId: form.candId,
                  round: form.round || 'Interview',
                  interviewer: form.interviewer || 'TBD',
                  date: form.date,
                  time: '10:00',
                });
                setForm({
                  candId: '',
                  round: 'Technical Round',
                  interviewer: '',
                  date: '',
                });
                setShowForm(false);
              }}
            >
              Schedule
            </button>
            <button
              className="ag-btn ag-btn-soft"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </Card>
      )}
      {interviews.length === 0 ? (
        <Card>
          <EmptyState
            icon={Calendar}
            title="No interviews yet"
            desc="Schedule interviews for candidates in the screening or interview stage."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {interviews.map((iv) => {
            const c = candidates.find((x) => x.id === iv.candId);
            return (
              <Card key={iv.id} style={{ padding: 16 }}>
                <div className="flex items-center gap-4 flex-wrap">
                  <Avatar name={c ? c.name : '?'} />
                  <div className="flex-1" style={{ minWidth: 200 }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p style={{ fontWeight: 600, color: AG.text }}>
                        {c ? c.name : '—'}
                      </p>
                      <Pill tone="neutral">{iv.round}</Pill>
                    </div>
                    <p
                      style={{ color: AG.textSec, marginTop: 2, fontSize: 14 }}
                    >
                      {c ? jobName(jobs, c.jobId) : ''} · {iv.interviewer} ·{' '}
                      {iv.date} {iv.time}
                    </p>
                  </div>
                  <button
                    className="ag-btn ag-btn-soft"
                    style={{ fontSize: 13 }}
                    onClick={() => setEvalIv({ id: iv.id, name: c ? c.name : '' })}
                  >
                    Evaluate
                  </button>
                  {iv.status === 'Scheduled' ? (
                    <div className="flex gap-2">
                      <button
                        className="ag-btn ag-btn-soft"
                        style={{ fontSize: 13 }}
                        onClick={() => completeInterview(iv.id, 'Hold')}
                      >
                        Hold
                      </button>
                      <button
                        className="ag-btn flex items-center gap-1"
                        style={{
                          color: AG.errorDark,
                          background: 'rgba(240,68,56,.12)',
                          border: 'none',
                          fontSize: 13,
                        }}
                        onClick={() => completeInterview(iv.id, 'Reject')}
                      >
                        Reject
                      </button>
                      <button
                        className="ag-btn ag-btn-primary flex items-center gap-1"
                        style={{ fontSize: 13 }}
                        onClick={() => {
                          completeInterview(iv.id, 'Advance');
                          if (c) moveCandidate(c.id, 1);
                        }}
                      >
                        <Check size={15} />
                        Advance
                      </button>
                    </div>
                  ) : (
                    <Pill
                      tone={
                        iv.rec === 'Advance'
                          ? 'success'
                          : iv.rec === 'Reject'
                            ? 'error'
                            : 'warning'
                      }
                      dot
                    >
                      Completed · {iv.rec}
                    </Pill>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <InterviewEval interviewId={evalIv?.id} candName={evalIv?.name} onClose={() => setEvalIv(null)} showToast={showToast} />
    </div>
  );
}

function ATSOffers({ p }) {
  const { offers, candidates, jobs, createOffer, advanceOffer, decideOffer, showToast } =
    p;
  const copyOfferLink = async (o) => {
    try {
      const u = await api.sendOfferLink(o.id);
      await navigator.clipboard.writeText(window.location.origin + u.link);
      showToast?.('Candidate offer link copied to clipboard');
    } catch (e) { showToast?.(e.message); }
  };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ candId: '', salary: 1000000, start: '' });
  const offerCands = candidates.filter(
    (c) =>
      !c.rejected &&
      c.stage === 'Offer' &&
      !offers.some((o) => o.candId === c.id),
  );
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <PrimaryBtn icon={Plus} onClick={() => setShowForm((s) => !s)}>
          Create offer
        </PrimaryBtn>
      </div>
      {showForm && (
        <Card style={{ padding: 20 }}>
          <h3 style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>
            Create an offer
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              className="ag-input"
              value={form.candId}
              onChange={(e) => setForm({ ...form, candId: e.target.value })}
            >
              <option value="">Candidate in offer stage…</option>
              {offerCands.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {jobName(jobs, c.jobId)}
                </option>
              ))}
            </select>
            <input
              className="ag-input"
              type="number"
              placeholder="Annual salary (USD)"
              value={form.salary}
              onChange={(e) =>
                setForm({ ...form, salary: Number(e.target.value) })
              }
            />
            <input
              className="ag-input"
              type="date"
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </div>
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button
              className="ag-btn ag-btn-primary"
              disabled={!form.candId || !form.start}
              onClick={() => {
                createOffer(form.candId, form.salary, form.start);
                setForm({ candId: '', salary: 1000000, start: '' });
                setShowForm(false);
              }}
            >
              Draft offer
            </button>
            <button
              className="ag-btn ag-btn-soft"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
          {offerCands.length === 0 && (
            <p style={{ fontSize: 12, color: AG.n400, marginTop: 8 }}>
              Move a candidate to the Offer stage first (Pipeline tab).
            </p>
          )}
        </Card>
      )}
      {offers.length === 0 ? (
        <Card>
          <EmptyState
            icon={Send}
            title="No offers yet"
            desc="Create an offer for a candidate who has reached the offer stage."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {offers.map((o) => {
            const c = candidates.find((x) => x.id === o.candId);
            return (
              <Card key={o.id} style={{ padding: 16 }}>
                <div className="flex items-center gap-4 flex-wrap">
                  <Avatar name={c ? c.name : '?'} />
                  <div className="flex-1" style={{ minWidth: 200 }}>
                    <p style={{ fontWeight: 600, color: AG.text }}>
                      {c ? c.name : '—'}
                    </p>
                    <p
                      style={{ color: AG.textSec, marginTop: 2, fontSize: 14 }}
                    >
                      {c ? jobName(jobs, c.jobId) : ''} · {fmtPKR(o.salary)}/yr
                      · starts {o.start}
                    </p>
                  </div>
                  {o.status === 'Sent' ? (
                    <div className="flex gap-2">
                      <button
                        className="ag-btn ag-btn-soft"
                        style={{ fontSize: 13 }}
                        onClick={() => copyOfferLink(o)}
                      >
                        Copy candidate link
                      </button>
                      <button
                        className="ag-btn ag-btn-soft"
                        style={{ fontSize: 13 }}
                        onClick={() => decideOffer(o.id, 'Declined')}
                      >
                        Mark declined
                      </button>
                      <button
                        className="ag-btn ag-btn-primary flex items-center gap-1"
                        style={{ fontSize: 13 }}
                        onClick={() => decideOffer(o.id, 'Accepted')}
                      >
                        <Check size={15} />
                        Mark accepted
                      </button>
                    </div>
                  ) : o.status === 'Accepted' || o.status === 'Declined' ? (
                    <Pill
                      tone={o.status === 'Accepted' ? 'success' : 'error'}
                      dot
                    >
                      {o.status}
                    </Pill>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Pill tone={o.status === 'Approved' ? 'info' : 'neutral'}>
                        {o.status}
                      </Pill>
                      <button
                        className="ag-btn ag-btn-primary flex items-center gap-1"
                        style={{ fontSize: 13 }}
                        onClick={() => advanceOffer(o.id)}
                      >
                        {o.status === 'Draft' ? 'Approve' : 'Send'}
                        {o.status === 'Draft' ? (
                          <Check size={14} />
                        ) : (
                          <Send size={14} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ATSOnboarding({ p }) {
  const { onboarding, candidates, jobs, toggleOnboardTask, convertToEmployee, showToast } =
    p;
  const copyProfileLink = async (o) => {
    try {
      const u = await api.onboardingProfileLink(o.id);
      await navigator.clipboard.writeText(window.location.origin + u.profileLink);
      showToast?.('Onboarding profile link copied to clipboard');
    } catch (e) { showToast?.(e.message); }
  };
  return (
    <div className="space-y-4">
      {onboarding.length === 0 ? (
        <Card>
          <EmptyState
            icon={UserPlus}
            title="No onboarding in progress"
            desc="When an offer is accepted, the new hire's onboarding handoff appears here."
          />
        </Card>
      ) : (
        onboarding.map((o) => {
          const c = candidates.find((x) => x.id === o.candId);
          const done = o.tasks.filter((t) => t.done).length;
          const all = done === o.tasks.length;
          return (
            <Card key={o.id} style={{ padding: 20 }}>
              <div
                className="flex items-center gap-4 flex-wrap"
                style={{ marginBottom: 12 }}
              >
                <Avatar name={c ? c.name : '?'} size="lg" />
                <div className="flex-1" style={{ minWidth: 200 }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p
                      style={{ fontWeight: 700, color: AG.text, fontSize: 18 }}
                    >
                      {c ? c.name : '—'}
                    </p>
                    <Pill
                      tone={o.status === 'Completed' ? 'success' : 'info'}
                      dot
                    >
                      {o.status}
                    </Pill>
                  </div>
                  <p style={{ color: AG.textSec, marginTop: 2, fontSize: 14 }}>
                    {c ? jobName(jobs, c.jobId) : ''} · start date {o.start} ·{' '}
                    {done}/{o.tasks.length} tasks done
                  </p>
                </div>
                <button
                  className="ag-btn ag-btn-soft"
                  style={{ fontSize: 13 }}
                  onClick={() => copyProfileLink(o)}
                >
                  Copy profile link
                </button>
                <button
                  className="ag-btn ag-btn-primary flex items-center gap-1"
                  disabled={!all || o.status === 'Completed'}
                  onClick={() => convertToEmployee(o.id)}
                >
                  <UserPlus size={15} />
                  Convert to employee
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {o.tasks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => toggleOnboardTask(o.id, t.id)}
                    className="flex items-center gap-3 text-left"
                    style={{
                      padding: 12,
                      background: AG.n50,
                      borderRadius: 12,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: t.done ? AG.success : '#fff',
                        border: `1px solid ${t.done ? AG.success : AG.n300}`,
                      }}
                    >
                      {t.done && <Check size={14} className="text-white" />}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: 14,
                          color: t.done ? AG.n400 : AG.n700,
                          textDecoration: t.done ? 'line-through' : 'none',
                        }}
                      >
                        {t.t}
                      </span>
                      {(t.owner || t.dueDate) && (
                        <span style={{ display: 'block', fontSize: 12, color: AG.n400 }}>
                          {t.owner || ''}{t.owner && t.dueDate ? ' · ' : ''}{t.dueDate ? `by ${t.dueDate}` : ''}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
              <OnboardingSurveys onboardingId={o.id} showToast={showToast} />
              {all && o.status !== 'Completed' && (
                <p
                  style={{
                    fontSize: 12,
                    color: AG.successDark,
                    marginTop: 10,
                    fontWeight: 500,
                  }}
                >
                  All tasks complete — ready to hand off to Core HR.
                </p>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}

function JobDetail({
  id,
  jobs,
  candidates,
  advanceJob,
  closeJob,
  openCandidate,
  onBack,
}) {
  const j = jobs.find((x) => x.id === id);
  const [tab, setTab] = useState('Description');
  const [jd, setJd] = useState('');
  if (!j) return null;
  const apps = candidates.filter((c) => c.jobId === id && !c.rejected);
  const nextLabel = {
    Draft: 'Submit for approval',
    'Pending Approval': 'Approve',
    Approved: 'Publish',
  };
  const genJd = () =>
    setJd(
      `We are hiring a ${j.title} to join our ${j.dept} team in ${j.location}.\n\nResponsibilities\n• Own and deliver key ${j.dept.toLowerCase()} initiatives end to end\n• Collaborate cross-functionally with stakeholders and leadership\n• Raise the bar on quality, speed and craft\n\nRequirements\n• Proven experience in a similar ${j.title} role\n• Strong communication and ownership\n• Alignment with Augment's values\n\nWhat we offer\n• Competitive salary, health cover and a learning budget\n• Hybrid working from ${j.location}`,
    );
  const tabs = ['Description', 'Screening', 'Pipeline', 'Details'];
  return (
    <div className="page-fill fade-up">
      <button
        onClick={onBack}
        className="page-shrink flex items-center gap-1.5 navlift"
        style={{ color: AG.textSec, fontSize: 14, fontWeight: 500 }}
      >
        <ArrowLeft size={16} />
        Back to ATS
      </button>
      <Card className="page-shrink" style={{ padding: 24 }}>
        <div className="flex items-start gap-5 flex-wrap">
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: AG.primaryLightest,
              color: AG.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Briefcase size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: AG.text,
                  letterSpacing: '-0.01em',
                }}
              >
                {j.title}
              </h2>
              <Pill tone={jobTone(j.status)} dot>
                {j.status}
              </Pill>
            </div>
            <p style={{ color: AG.textSec, marginTop: 2 }}>
              {j.dept} · {j.location} · {j.type}
            </p>
            <div
              className="flex items-center gap-4 flex-wrap"
              style={{ marginTop: 8, color: AG.n400, fontSize: 13 }}
            >
              <span className="flex items-center gap-1">
                <Hash size={13} />
                {j.id}
              </span>
              <span className="flex items-center gap-1">
                <Users size={13} />
                {j.manager}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={13} />
                {j.posted === '—' ? 'Not posted' : 'Posted ' + j.posted}
              </span>
              <span className="flex items-center gap-1">
                <UserPlus size={13} />
                {j.openings} opening{j.openings > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {nextLabel[j.status] ? (
              <PrimaryBtn icon={ArrowRight} onClick={() => advanceJob(j.id)}>
                {nextLabel[j.status]}
              </PrimaryBtn>
            ) : null}
            {j.status === 'Published' ? (
              <button
                className="ag-btn ag-btn-soft"
                onClick={() => closeJob(j.id)}
              >
                Close requisition
              </button>
            ) : null}
          </div>
        </div>
      </Card>
      <div className="tab-strip page-shrink">
        {tabs.map((t) => {
          const active = tab === t;
          const count = t === 'Pipeline' ? apps.length : null;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`navlift tab-strip-btn${active ? ' tab-strip-btn--active' : ''}`}
            >
              <span>{t}</span>
              <span
                className={`tab-strip-count${active ? ' tab-strip-count--active' : ''}${count == null ? ' tab-strip-count--placeholder' : ''}`}
                aria-hidden={count == null}
              >
                {count ?? 0}
              </span>
            </button>
          );
        })}
      </div>
      <div className="page-scroll">
        {tab === 'Description' && (
          <Card style={{ padding: 24 }}>
            <div
              className="flex items-center justify-between flex-wrap gap-2"
              style={{ marginBottom: 12 }}
            >
              <h3 style={{ fontWeight: 600, color: AG.text }}>
                Job description
              </h3>
              <button
                className="ag-btn ag-btn-ai flex items-center gap-2"
                onClick={genJd}
              >
                <Sparkles size={15} />
                Generate with AI
              </button>
            </div>
            {jd ? (
              <p
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: 14,
                  color: AG.n700,
                  lineHeight: 1.6,
                }}
              >
                {jd}
              </p>
            ) : (
              <p style={{ color: AG.n400, fontSize: 14 }}>
                No description yet. Click "Generate with AI" to draft one from
                the role, department and location.
              </p>
            )}
          </Card>
        )}
        {tab === 'Screening' && <JobScreening jobId={j.id} openCandidate={openCandidate} />}
        {tab === 'Pipeline' &&
          (apps.length === 0 ? (
            <Card>
              <EmptyState
                icon={Users}
                title="No applicants yet"
                desc="Once this role is published, applicants will appear here by stage."
              />
            </Card>
          ) : (
            <div className="space-y-2">
              {apps.map((c) => (
                <Card
                  key={c.id}
                  hover
                  style={{ padding: 14 }}
                  onClick={() => openCandidate(c.id)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar name={c.name} size="sm" />
                    <div className="flex-1">
                      <p
                        style={{
                          fontWeight: 500,
                          color: AG.text,
                          fontSize: 14,
                        }}
                      >
                        {c.name}
                      </p>
                      <p style={{ fontSize: 12, color: AG.n400 }}>
                        {c.exp} · {c.source}
                      </p>
                    </div>
                    <Pill tone="neutral">{c.stage}</Pill>
                    <Pill
                      tone={
                        c.score >= 88
                          ? 'success'
                          : c.score >= 78
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      <Sparkles size={11} />
                      {c.score}
                    </Pill>
                    <ChevronRight size={16} style={{ color: AG.n300 }} />
                  </div>
                </Card>
              ))}
            </div>
          ))}
        {tab === 'Details' && (
          <Card style={{ padding: 24 }}>
            <div className="space-y-2" style={{ fontSize: 14 }}>
              {[
                ['Requisition ID', j.id],
                ['Department', j.dept],
                ['Hiring manager', j.manager],
                ['Location', j.location],
                ['Employment type', j.type],
                ['Openings', String(j.openings)],
                ['Status', j.status],
                ['Posted', j.posted === '—' ? 'Not posted' : j.posted],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between"
                  style={{
                    padding: '8px 0',
                    borderBottom: `1px solid ${AG.divider}`,
                  }}
                >
                  <span style={{ color: AG.textSec }}>{k}</span>
                  <span style={{ fontWeight: 500, color: AG.text }}>{v}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// Lightweight styled table cells for the Performance module.
function PerformanceAdmin() {
  const [allOkrs, setAllOkrs] = useState(null);
  const [employees, setEmployees] = useState([]);
  const tabs = ['Overview', 'Objectives', 'Assessments', 'Calibration', 'Compensation', 'Reports'];
  const [tab, setTab] = useState(() => tabFromHash(window.location.hash, tabs));
  const changeTab = (t) => { setTab(t); window.history.replaceState(null, '', window.location.pathname + (t === tabs[0] ? '' : '#' + tabSlug(t))); };
  const [empFilter, setEmpFilter] = useState('all');
  const [cycleFilter, setCycleFilter] = useState('all');
  const [reloadKey, setReloadKey] = useState(0);
  const [resolve, setResolve] = useState(null); // { okr, outcome, managerRating, hrComment }
  const [resolving, setResolving] = useState(false);
  useEffect(() => {
    let a = true;
    // Fetch OKRs AND the full directory, so employees with no objectives still
    // appear (otherwise HR can't see who is missing OKRs).
    Promise.all([api.okrsAll(), api.employees().catch(() => [])])
      .then(([o, e]) => {
        if (a) {
          setAllOkrs(o);
          setEmployees(e);
        }
      })
      .catch(() => a && setAllOkrs([]));
    // Re-fetch when the tab/window regains focus so reviews added in another
    // session (employee self-rating, manager review) appear without a reload.
    const onFocus = () => setReloadKey((k) => k + 1);
    window.addEventListener('focus', onFocus);
    return () => {
      a = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [reloadKey]);
  if (!allOkrs)
    return (
      <div
        className="flex items-center justify-center"
        style={{ minHeight: 240 }}
      >
        <CenterSpinner />
      </div>
    );

  // Cycle filter — distinct periods present in the data (newest-ish first).
  const cycleOptions = Array.from(
    new Set(allOkrs.map((o) => o.cycle).filter(Boolean)),
  ).sort();
  const okrs =
    cycleFilter === 'all'
      ? allOkrs
      : allOkrs.filter((o) => o.cycle === cycleFilter);

  const finalize = async (emp) => {
    const toSign = okrs.filter(
      (o) => o.employeeId === emp.id && o.status === 'Reviewed',
    );
    await Promise.all(
      toSign.map((o) => api.finalizeOkr(o.id, {}).catch(() => {})),
    );
    setReloadKey((k) => k + 1);
  };

  // Resolve an employee's appeal: uphold (optionally revising the rating) or reject.
  const doResolve = async () => {
    if (!resolve || resolving) return; setResolving(true);
    const body = { outcome: resolve.outcome, hrComment: resolve.hrComment };
    if (resolve.outcome === 'Upheld' && resolve.managerRating) body.managerRating = resolve.managerRating;
    try { await api.resolveOkrObjection(resolve.okr.id, body); setResolve(null); setReloadKey((k) => k + 1); }
    catch (e) { window.alert(e.message); } finally { setResolving(false); }
  };

  const reviewed = okrs.filter(
    (o) => o.status === 'Reviewed' || o.status === 'Finalized',
  ).length;
  const submitted = okrs.filter((o) => o.status === 'Submitted').length;
  const byEmp = okrsByEmployee(okrs, employees);
  // employee filter (Objectives / Assessments / Calibration tabs) — full directory
  const empOptions = [...employees].sort((a, b) =>
    String(a.name).localeCompare(String(b.name)),
  );
  const fOkrs =
    empFilter === 'all' ? okrs : okrs.filter((o) => o.employeeId === empFilter);
  const fByEmp =
    empFilter === 'all' ? byEmp : byEmp.filter((e) => e.id === empFilter);
  const RBUCKETS = [
    'Exceptional',
    'Exceeds',
    'Meets',
    'Below',
    'Unsatisfactory',
  ];
  const dist = RBUCKETS.map((r) => ({
    r: r === 'Unsatisfactory' ? 'Unsat.' : r,
    v: okrs.filter((o) => o.managerRating === r).length,
  }));
  const ratedNums = okrs
    .map((o) => RATING_NUM[o.managerRating])
    .filter(Boolean);
  const avgRating = ratedNums.length
    ? (ratedNums.reduce((a, b) => a + b, 0) / ratedNums.length).toFixed(1)
    : '—';
  const highPerformers = byEmp.filter(
    (e) => e.manager && RATING_NUM[e.manager] >= 4,
  ).length;
  const completion = okrs.length
    ? Math.round((reviewed / okrs.length) * 100)
    : 0;
  const overallProgress = weightedProgress(okrs);
  const awaitingSignoff = byEmp.reduce((a, e) => a + (e.finalizable || 0), 0);

  const RatingChart = () => (
    <ChartCard
      title="Manager rating distribution"
      sub="Across reviewed objectives"
    >
      <Box sx={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dist}
            margin={{ left: -20, right: 8, top: 8 }}
            barCategoryGap="30%"
          >
            <defs>
              <linearGradient id="perfBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={AG.primary} stopOpacity={1} />
                <stop offset="100%" stopColor={AG.primary} stopOpacity={0.5} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="2 4"
              stroke={AG.divider}
              vertical={false}
            />
            <XAxis
              dataKey="r"
              tick={{ fontSize: 11, fill: AG.n400 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: AG.n400 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<ChartTip />} cursor={{ fill: AG.n50 }} />
            <Bar
              dataKey="v"
              name="reviewed"
              fill="url(#perfBar)"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </ChartCard>
  );

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Performance management"
        sub="OKRs · H1 2026 cycle · live across the company"
        action={
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="ag-btn ag-btn-soft"
            style={{ padding: '6px 14px', fontSize: 13 }}
          >
            ↻ Refresh
          </button>
        }
      />
      <div
        className="flex gap-6 overflow-x-auto"
        style={{ borderBottom: `1px solid ${AG.divider}` }}
      >
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => changeTab(t)}
            className="navlift"
            style={{
              padding: '10px 0',
              fontSize: 14,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              color: tab === t ? AG.primary : AG.n500,
              borderBottom: `2px solid ${tab === t ? AG.primary : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ fontSize: 13, color: AG.textSec, fontWeight: 500 }}>
          Cycle:
        </span>
        <select
          className="ag-input"
          style={{ width: 'auto', minWidth: 150 }}
          value={cycleFilter}
          onChange={(e) => setCycleFilter(e.target.value)}
        >
          <option value="all">All cycles</option>
          {cycleOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {['Objectives', 'Assessments', 'Calibration'].includes(tab) && (
          <>
            <span
              style={{
                fontSize: 13,
                color: AG.textSec,
                fontWeight: 500,
                marginLeft: 8,
              }}
            >
              Employee:
            </span>
            <select
              className="ag-input"
              style={{ width: 'auto', minWidth: 220 }}
              value={empFilter}
              onChange={(e) => setEmpFilter(e.target.value)}
            >
              <option value="all">All employees</option>
              {empOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </>
        )}
        {(empFilter !== 'all' || cycleFilter !== 'all') && (
          <button
            onClick={() => {
              setEmpFilter('all');
              setCycleFilter('all');
            }}
            className="ag-btn ag-btn-soft"
            style={{ padding: '6px 12px', fontSize: 13 }}
          >
            Clear
          </button>
        )}
      </div>

      {tab === 'Overview' && (
        <>
          <WidgetGrid>
            <KPI
              label="Total objectives"
              value={String(okrs.length)}
              icon={Star}
              iconTone="primary"
              delay={0}
            />
            <KPI
              label="Reviewed"
              value={String(reviewed)}
              hint={`${completion}% complete`}
              icon={Check}
              iconTone="success"
              delay={60}
            />
            <KPI
              label="Awaiting manager review"
              value={String(submitted)}
              icon={Clock}
              iconTone="warning"
              delay={120}
            />
            <KPI
              label="Awaiting HR sign-off"
              value={String(awaitingSignoff)}
              icon={Check}
              iconTone="primary"
              delay={180}
            />
            <KPI
              label="Overall progress"
              value={`${overallProgress}%`}
              icon={TrendingUp}
              iconTone="info"
              delay={240}
            />
          </WidgetGrid>
          <RatingChart />
        </>
      )}

      {tab === 'Objectives' && (
        <PerfTable
          head={[
            'Employee',
            'Objective',
            'Weight',
            'Progress',
            'Status',
            'Manager rating',
            'Proof',
            'Appeal',
          ]}
        >
          {fOkrs.map((o) => (
            <tr key={o.id} className="row-h">
              <PTd>
                <PersonCell name={o.employeeName} />
              </PTd>
              <PTd style={{ fontWeight: 500, color: AG.text }}>
                {o.objective}
              </PTd>
              <PTd>{o.weight}%</PTd>
              <PTd>
                <div className="flex items-center gap-2">
                  <div
                    style={{
                      flex: 1,
                      minWidth: 70,
                      height: 8,
                      background: AG.n100,
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        background: AG.primary,
                        borderRadius: 4,
                        width: `${o.progress}%`,
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 12, color: AG.n600 }}>
                    {o.progress}%
                  </span>
                </div>
              </PTd>
              <PTd>
                <Pill tone={okrStatusTone(o.status)} dot>
                  {o.status}
                </Pill>
              </PTd>
              <PTd>
                {o.managerRating ? (
                  <Pill tone={ratingTone(o.managerRating)}>
                    {o.managerRating}
                  </Pill>
                ) : (
                  <span style={{ color: AG.n400 }}>—</span>
                )}
              </PTd>
              <PTd>
                {o.evidence?.length ? (
                  <div className="flex flex-col gap-1" style={{ alignItems: 'flex-start' }}>
                    {o.evidence.map((ev) => (
                      <button
                        key={ev.id}
                        onClick={() => api.downloadOkrEvidence(o.id, ev.id, ev.name)}
                        title={ev.name}
                        className="flex items-center gap-1"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, fontSize: 12, maxWidth: 160 }}
                      >
                        <Download size={13} style={{ flexShrink: 0 }} />
                        <span className="truncate">{ev.name}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: AG.n400 }}>—</span>
                )}
              </PTd>
              <PTd>
                {o.status === 'Objected' ? (
                  <button onClick={() => setResolve({ okr: o, outcome: 'Upheld', managerRating: o.managerRating || '', hrComment: '' })} className="ag-btn ag-btn-outline" style={{ padding: '3px 10px', fontSize: 12 }}>Resolve</button>
                ) : o.objectionOutcome ? (
                  <Pill tone={o.objectionOutcome === 'Upheld' ? 'success' : 'neutral'}>{o.objectionOutcome}</Pill>
                ) : (
                  <span style={{ color: AG.n400 }}>—</span>
                )}
              </PTd>
            </tr>
          ))}
        </PerfTable>
      )}

      {tab === 'Assessments' && (
        <PerfTable
          head={[
            'Employee',
            'Objectives',
            'Self-rating',
            'Manager rating',
            'Status',
          ]}
          note="Per-employee roll-up across the cycle. Employees with no objectives are shown so HR can spot gaps."
        >
          {fByEmp.map((e) => (
            <tr key={e.id} className="row-h">
              <PTd>
                <PersonCell name={e.name} subtitle={e.dept} />
              </PTd>
              <PTd>
                {e.count === 0 ? (
                  <span style={{ color: AG.n400 }}>None</span>
                ) : (
                  `${e.count} · ${e.progress}%`
                )}
              </PTd>
              <PTd>
                {e.self ? (
                  <Pill tone={ratingTone(e.self)}>{e.self}</Pill>
                ) : (
                  <span style={{ color: AG.n400 }}>—</span>
                )}
              </PTd>
              <PTd>
                <span className="flex items-center gap-2">
                  {e.manager ? (
                    <Pill tone={ratingTone(e.manager)}>{e.manager}</Pill>
                  ) : (
                    <span style={{ color: AG.n400 }}>—</span>
                  )}
                  {e.gap && <Pill tone="warning">△ gap</Pill>}
                </span>
              </PTd>
              <PTd>
                <Pill tone={rollupStatusTone(e.status)} dot>
                  {e.status}
                </Pill>
              </PTd>
            </tr>
          ))}
        </PerfTable>
      )}

      {tab === 'Calibration' && (
        <PerfTable
          head={['Employee', 'Self', 'Manager', 'Progress', 'Flag', 'Sign-off']}
          note="HR-only — compare self vs manager ratings, resolve outliers, then sign off reviewed objectives to lock the cycle."
        >
          {fByEmp.map((e) => (
            <tr key={e.id} className="row-h">
              <PTd style={{ fontWeight: 500, color: AG.text }}>{e.name}</PTd>
              <PTd>{e.self || '—'}</PTd>
              <PTd>{e.manager || '—'}</PTd>
              <PTd>{e.progress}%</PTd>
              <PTd>
                {e.gap ? (
                  <Pill tone="warning">△ review</Pill>
                ) : (
                  <span style={{ color: AG.n400 }}>—</span>
                )}
              </PTd>
              <PTd>
                {e.count === 0 ? (
                  <span style={{ color: AG.n400 }}>—</span>
                ) : e.finalizable > 0 ? (
                  <button
                    onClick={() => finalize(e)}
                    className="ag-btn ag-btn-primary"
                    style={{ padding: '4px 12px', fontSize: 12 }}
                  >
                    Sign off ({e.finalizable})
                  </button>
                ) : e.status === 'Finalized' ? (
                  <Pill tone="primary" dot>
                    Signed off
                  </Pill>
                ) : (
                  <span style={{ color: AG.n400 }}>—</span>
                )}
              </PTd>
            </tr>
          ))}
        </PerfTable>
      )}

      {tab === 'Compensation' && <Compensation employees={empOptions} cycleOptions={cycleOptions} />}

      {tab === 'Reports' && (
        <>
          <WidgetGrid>
            <KPI
              label="High performers"
              value={String(highPerformers)}
              hint="Exceeds & above"
              icon={Star}
              iconTone="success"
              delay={0}
            />
            <KPI
              label="Avg manager rating"
              value={`${avgRating} / 5`}
              icon={TrendingUp}
              iconTone="info"
              delay={60}
            />
            <KPI
              label="Completion"
              value={`${completion}%`}
              icon={Check}
              iconTone="primary"
              delay={120}
            />
            <KPI
              label="Awaiting review"
              value={String(submitted)}
              icon={Clock}
              iconTone="warning"
              delay={180}
            />
          </WidgetGrid>
          <RatingChart />
        </>
      )}

      <Dialog open={!!resolve} onClose={() => !resolving && setResolve(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve appeal</DialogTitle>
        <DialogContent>
          {resolve && (
            <div style={{ paddingTop: 6 }}>
              <p style={{ fontSize: 13, color: AG.n500, marginBottom: 4 }}>{resolve.okr.employeeName} appealed “{resolve.okr.objective}”.</p>
              {resolve.okr.objectionReason && <p style={{ fontSize: 12.5, color: AG.n600, marginBottom: 12, fontStyle: 'italic' }}>“{resolve.okr.objectionReason}”</p>}
              <div className="flex gap-2" style={{ marginBottom: 12 }}>
                <button onClick={() => setResolve({ ...resolve, outcome: 'Upheld' })} className={`ag-btn ${resolve.outcome === 'Upheld' ? 'ag-btn-primary' : 'ag-btn-outline'}`} style={{ flex: 1 }}>Uphold</button>
                <button onClick={() => setResolve({ ...resolve, outcome: 'Rejected' })} className={`ag-btn ${resolve.outcome === 'Rejected' ? 'ag-btn-primary' : 'ag-btn-outline'}`} style={{ flex: 1 }}>Reject</button>
              </div>
              {resolve.outcome === 'Upheld' && (
                <label style={{ display: 'block', marginBottom: 12 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Revised rating (optional)</span>
                  <select className="ag-input" value={resolve.managerRating} onChange={(e) => setResolve({ ...resolve, managerRating: e.target.value })}>
                    <option value="">Keep current ({resolve.okr.managerRating || '—'})</option>
                    {OKR_RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
              )}
              <TextField fullWidth size="small" multiline minRows={2} label="HR note" value={resolve.hrComment} onChange={(e) => setResolve({ ...resolve, hrComment: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setResolve(null)} disabled={resolving}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={doResolve} disabled={resolving}>Resolve</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Performance → compensation linkage (Phase 1.2): HR derives a merit increment +
// bonus from an employee's finalized appraisals; approving applies the raise to
// base salary and books the bonus as a one-time payroll incentive.
const COMP_TONE = { Draft: 'neutral', Approved: 'success', Rejected: 'warning' };
function Compensation({ employees, cycleOptions }) {
  const [recs, setRecs] = useState(null);
  const [form, setForm] = useState(null); // { employeeId, cycle, rating, currentSalary, incrementPct, bonus, note, currency, appraisals }
  const [busy, setBusy] = useState(false);
  const load = () => api.compRecommendations().then(setRecs).catch(() => setRecs([]));
  useEffect(() => { load(); }, []);

  const money = (n, c = 'PKR') => `${c === 'PKR' ? 'Rs' : c} ${Number(n || 0).toLocaleString('en-IN')}`;
  const openNew = () => setForm({ employeeId: '', cycle: cycleOptions[0] || 'H1 2026', rating: '', currentSalary: 0, incrementPct: 0, bonus: 0, note: '', currency: 'PKR', appraisals: null });
  const suggest = async (employeeId, cycle) => {
    if (!employeeId) return;
    try { const s = await api.suggestComp(employeeId, cycle); setForm((f) => ({ ...f, employeeId, cycle, rating: s.rating || '', currentSalary: s.currentSalary, incrementPct: s.incrementPct, currency: s.currency, appraisals: s.appraisals })); }
    catch { /* leave form as-is */ }
  };
  const save = async () => {
    if (!form.employeeId || !form.cycle || busy) return; setBusy(true);
    try { await api.createComp({ employeeId: form.employeeId, cycle: form.cycle, rating: form.rating || null, currentSalary: form.currentSalary, incrementPct: Number(form.incrementPct) || 0, bonus: Number(form.bonus) || 0, note: form.note }); setForm(null); load(); }
    catch (e) { window.alert(e.message); } finally { setBusy(false); }
  };
  const act = async (fn, id) => { try { await fn(id); load(); } catch (e) { window.alert(e.message); } };
  const approve = async (r) => {
    try { const u = await api.approveComp(r.id); if (u?.bonusMonth) window.alert(`Approved. The bonus is booked into the ${u.bonusMonth} payroll run.`); load(); }
    catch (e) { window.alert(e.message); }
  };

  if (!recs) return <CenterSpinner />;
  // Mirror the server: clamp + round the percent before projecting the new salary.
  const previewPct = form ? Math.max(0, Math.min(100, Math.round(Number(form.incrementPct) || 0))) : 0;
  const projected = form ? Math.round((form.currentSalary || 0) * (1 + previewPct / 100)) : 0;
  return (
    <>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 13, color: AG.n500, margin: 0, maxWidth: 600 }}>Merit recommendations from appraisal ratings. Approving a recommendation applies the increment to base salary and books any bonus as a one-time incentive in the next payroll run.</p>
        <PrimaryBtn onClick={openNew}><Plus size={14} style={{ marginRight: 6 }} /> New recommendation</PrimaryBtn>
      </div>
      {recs.length === 0 ? (
        <Card><EmptyState icon={TrendingUp} title="No recommendations" desc="Create a merit recommendation from an employee's finalized appraisals." /></Card>
      ) : (
        <PerfTable head={['Employee', 'Cycle', 'Rating', 'Current → New', 'Increment', 'Bonus', 'Status', '']}>
          {recs.map((r) => (
            <tr key={r.id} className="row-h">
              <PTd><PersonCell name={r.employeeName} subtitle={r.dept} /></PTd>
              <PTd>{r.cycle}</PTd>
              <PTd>{r.rating ? <Pill tone={ratingTone(r.rating)}>{r.rating}</Pill> : <span style={{ color: AG.n400 }}>—</span>}</PTd>
              <PTd>{money(r.currentSalary, r.currency)} → <b style={{ color: AG.text }}>{money(r.status === 'Approved' ? r.newSalary : r.projectedSalary, r.currency)}</b></PTd>
              <PTd>{r.incrementPct}%</PTd>
              <PTd>{r.bonus > 0 ? money(r.bonus, r.currency) : '—'}</PTd>
              <PTd><Pill tone={COMP_TONE[r.status] || 'neutral'} dot>{r.status}</Pill></PTd>
              <PTd style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {r.status === 'Draft' ? (
                  <div className="flex items-center gap-1" style={{ justifyContent: 'flex-end' }}>
                    <button onClick={() => approve(r)} className="ag-btn ag-btn-primary" style={{ padding: '3px 10px', fontSize: 12 }}>Approve</button>
                    <button onClick={() => act(api.rejectComp, r.id)} className="ag-btn ag-btn-outline" style={{ padding: '3px 10px', fontSize: 12 }}>Reject</button>
                    <button onClick={() => act(api.deleteComp, r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={14} /></button>
                  </div>
                ) : <span style={{ color: AG.n400 }}>—</span>}
              </PTd>
            </tr>
          ))}
        </PerfTable>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New compensation recommendation</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Employee</span>
                <select className="ag-input" value={form.employeeId} onChange={(e) => suggest(e.target.value, form.cycle)}>
                  <option value="">Select…</option>
                  {employees.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.dept}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Cycle</span>
                <select className="ag-input" value={form.cycle} onChange={(e) => { const cy = e.target.value; setForm((f) => ({ ...f, cycle: cy })); if (form.employeeId) suggest(form.employeeId, cy); }}>
                  {(cycleOptions.length ? cycleOptions : ['H1 2026']).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              {form.employeeId && (
                <p style={{ fontSize: 12.5, color: AG.n500, marginBottom: 12 }}>
                  {form.appraisals != null ? `${form.appraisals} finalized appraisal(s)` : 'No appraisals'}{form.rating ? ` · suggested rating ${form.rating}` : ''} · current {money(form.currentSalary, form.currency)}
                </p>
              )}
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><label style={{ display: 'block', marginBottom: 12 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>Increment %</span><TextField fullWidth size="small" type="number" value={form.incrementPct} onChange={(e) => setForm({ ...form, incrementPct: e.target.value })} inputProps={{ min: 0, max: 100 }} /></label></div>
                <div style={{ flex: 1 }}><label style={{ display: 'block', marginBottom: 12 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>One-time bonus</span><TextField fullWidth size="small" type="number" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} inputProps={{ min: 0 }} /></label></div>
              </div>
              <p style={{ fontSize: 13, color: AG.n600, marginBottom: 12 }}>New salary: <b style={{ color: AG.primary }}>{money(projected, form.currency)}</b></p>
              <TextField fullWidth size="small" multiline minRows={2} label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={save} disabled={busy || !form?.employeeId || !form?.cycle}>Save draft</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}

function tabSlug(t) { return t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
function tabFromHash(hash, tabs) { const h = (hash || '').replace(/^#/, ''); return tabs.find(t => tabSlug(t) === h) || tabs[0]; }

function PayrollAdmin({ showToast }) {
  const tabs = ['Overview', 'Runs', 'Income Tax', 'Benefits & Contributions'];
  const [tab, setTab] = useState(() => tabFromHash(window.location.hash, tabs));
  const [runKey, setRunKey] = useState(0);
  const [running, setRunning] = useState(false);
  const changeTab = (t) => { setTab(t); window.history.replaceState(null, '', window.location.pathname + (t === tabs[0] ? '' : '#' + tabSlug(t))); };
  const runPayroll = async () => {
    if (running) return;
    setRunning(true);
    try {
      const r = await api.runPayroll();
      showToast?.(
        `Payroll for ${r.month} ran — ${r.employeesPaid} payslips drafted, pending approval`,
      );
      setRunKey((k) => k + 1);
      changeTab('Runs');
    } catch (e) {
      showToast?.(e.message);
    } finally {
      setRunning(false);
    }
  };
  const downloadCsv = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const exportBank = async () => {
    try {
      const r = await api.bankExport();
      if (!r.rows.length) { showToast?.('No payable accounts to export'); return; }
      downloadCsv(r.csv, r.filename);
      showToast?.(`Bank file: ${r.summary.count} payee(s)${r.missing.length ? ` · ${r.missing.length} missing an account` : ''}`);
    } catch (e) {
      showToast?.(e.message);
    }
  };
  const exportStatutory = async () => {
    try {
      const r = await api.statutoryReport();
      downloadCsv(r.pf.csv, r.pf.filename);
      downloadCsv(r.tax.csv, r.tax.filename);
      showToast?.(`Statutory registers for ${r.month} downloaded`);
    } catch (e) {
      showToast?.(e.message);
    }
  };
  return (
    <DataGridPage>
      <SectionTitle
        title="Payroll"
        sub="Live payroll — salaries, income tax & benefits"
        action={
          <div className="flex items-center gap-2">
            <OutlineBtn icon={Download} onClick={exportStatutory}>
              Registers
            </OutlineBtn>
            <OutlineBtn icon={Download} onClick={exportBank}>
              Bank export
            </OutlineBtn>
            <PrimaryBtn icon={Wallet} onClick={runPayroll}>
              {running ? 'Running…' : 'Run payroll'}
            </PrimaryBtn>
          </div>
        }
      />
      <div className="tab-strip page-shrink">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => changeTab(t)}
            className={`navlift tab-strip-btn${tab === t ? ' tab-strip-btn--active' : ''}`}
          >
            <span>{t}</span>
          </button>
        ))}
      </div>
      <div
        className={
          tab === 'Benefits & Contributions' ? 'page-scroll' : 'page-fill'
        }
      >
        {tab === 'Overview' && <PayrollOverview runKey={runKey} showToast={showToast} />}
        {tab === 'Runs' && <PayrollRunsPanel runKey={runKey} showToast={showToast} />}
        {tab === 'Income Tax' && <PayrollTax runKey={runKey} />}
        {tab === 'Benefits & Contributions' && (
          <PayrollBenefits runKey={runKey} />
        )}
      </div>
    </DataGridPage>
  );
}

// Payroll runs (Phase 3) — each run needs approval before its payslips are
// finalized; the approver must differ from whoever ran it (segregation of duties).
function PayrollRunsPanel({ runKey, showToast }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.payrollRuns().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [runKey]);
  const act = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === 'approve') { const r = await api.approvePayrollRun(id); showToast?.(`Approved — ${r.payslipsFinalized} payslips finalized`); }
      else { const r = await api.rejectPayrollRun(id); showToast?.(`Rejected — ${r.payslipsReversed} draft payslips reversed`); }
      load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(''); }
  };
  const tone = (s) => (s === 'Approved' ? 'success' : s === 'Rejected' ? 'error' : 'warning');
  if (!rows) return <CenterSpinner />;
  if (!rows.length) return <div style={{ padding: 8 }}><Card style={{ padding: 24 }}><EmptyState icon={Wallet} title="No payroll runs yet" desc="Run payroll to draft this month's payslips for approval." /></Card></div>;
  return (
    <div className="space-y-3 fade-up" style={{ padding: 4 }}>
      {rows.map((r) => (
        <Card key={r.id} style={{ padding: 16 }}>
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
            <div>
              <div style={{ fontWeight: 600, color: AG.text }}>{r.month} <Pill tone={tone(r.status)}>{r.status}</Pill></div>
              <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{r.employeeCount} employee(s) · Net Rs {r.netTotal.toLocaleString('en-IN')} · ran by {r.createdBy || '—'}{r.approvedBy ? ` · approved by ${r.approvedBy}` : ''}</div>
            </div>
            {r.status === 'Pending Approval' && (
              <div className="flex items-center gap-2">
                {r.canApprove
                  ? <PrimaryBtn onClick={() => act(r.id, 'approve')} disabled={busy === r.id}>Approve</PrimaryBtn>
                  : <span style={{ fontSize: 12, color: AG.n400 }}>Awaiting another approver</span>}
                <OutlineBtn onClick={() => act(r.id, 'reject')} disabled={busy === r.id}>Reject</OutlineBtn>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

const FX_TARGETS = ['', 'USD', 'PKR', 'EUR', 'GBP', 'AED', 'SAR'];

function PayrollOverview({ runKey, showToast }) {
  const [data, setData] = useState(null);
  const [convertTo, setConvertTo] = useState('');
  const [structFor, setStructFor] = useState(null);
  const displayCurrency = data?.displayCurrency || null;
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Employee',
        flex: 1.1,
        minWidth: 190,
        renderCell: ({ row }) => <PersonCell name={row.name} />,
      },
      {
        field: 'dept',
        headerName: 'Department',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value }) => <TextCell value={value} />,
      },
      {
        field: 'gross',
        headerName: 'Gross',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value, row }) => <TextCell value={displayCurrency ? fmtMoney(value, row?.converted === false ? row.currency : displayCurrency) : fmtPKR(value)} />,
      },
      {
        field: 'pf',
        headerName: 'PF (6.5%)',
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ value, row }) => <TextCell value={displayCurrency ? fmtMoney(value, row?.converted === false ? row.currency : displayCurrency) : fmtPKR(value)} />,
      },
      {
        field: 'tax',
        headerName: 'Income tax',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value, row }) => <TextCell value={displayCurrency ? fmtMoney(value, row?.converted === false ? row.currency : displayCurrency) : fmtPKR(value)} />,
      },
      {
        field: 'deductions',
        headerName: 'Deductions',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value, row }) => <TextCell value={displayCurrency ? fmtMoney(value, row?.converted === false ? row.currency : displayCurrency) : fmtPKR(value)} />,
      },
      {
        field: 'net',
        headerName: 'Net pay',
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ value, row }) => (
          <TextCell value={displayCurrency ? fmtMoney(value, row?.converted === false ? row.currency : displayCurrency) : fmtPKR(value)} fontWeight={600} />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
        minWidth: 90,
        renderCell: ({ value }) => (
          <GridStatusPill status={value} tone="success" />
        ),
      },
      {
        field: 'structure',
        headerName: '',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <button
            onClick={() => setStructFor({ id: row.id, name: row.name })}
            className="inline-flex items-center gap-1"
            style={{ color: AG.primary, fontWeight: 500, fontSize: 13 }}
          >
            <SlidersHorizontal size={14} />
            Structure
          </button>
        ),
      },
    ],
    [displayCurrency],
  );
  useEffect(() => {
    let a = true;
    setData(null);
    api
      .payroll(convertTo || undefined)
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, [runKey, convertTo]);
  const rows = data?.rows || [];
  const s = data?.summary;
  // Currency-aware formatters: native ($/Rs default) unless a reporting currency
  // is active. Unconverted rows (no rate path) keep their own native currency.
  const money = (v, row) => (displayCurrency ? fmtMoney(v, row?.converted === false ? row.currency : displayCurrency) : fmtPKR(v));
  const big = (v) => (displayCurrency ? fmtMoney(v, displayCurrency) : fmtM(v));
  const exportAll = () => {
    api.trackDownload('DOWNLOAD_PAYROLL_EXPORT', `Payroll summary · ${rows.length} employees`);
    const th = (t, right) =>
      `<td style="padding:9px 0 9px 12px;font-weight:600;color:#6C737F;${right ? 'text-align:right' : ''}">${t}</td>`;
    const td = (t, right) =>
      `<td style="padding:8px 0 8px 12px;color:#111927;${right ? 'text-align:right' : ''}">${t}</td>`;
    const head = `<tr>${th('Employee')}${th('Dept')}${th('Gross', 1)}${th('PF', 1)}${th('Income tax', 1)}${th('Deductions', 1)}${th('Net', 1)}</tr>`;
    const body = rows
      .map(
        (r) =>
          `<tr>${td(r.name)}${td(r.dept)}${td(money(r.gross, r), 1)}${td(money(r.pf, r), 1)}${td(money(r.tax, r), 1)}${td(money(r.deductions, r), 1)}${td(money(r.net, r), 1)}</tr>`,
      )
      .join('');
    const totals = `<tr class="tot"><td class="k" style="padding-left:12px">Totals</td><td></td><td class="v">${money(s?.totalGross || 0)}</td><td></td><td class="v">${money(s?.totalTax || 0)}</td><td class="v">${money(s?.totalDeductions || 0)}</td><td class="v">${money(s?.totalNet || 0)}</td></tr>`;
    printPayDoc(
      'Payroll Summary',
      `<div class="hdr"><h1>Augment HR</h1><p>Payroll Summary · ${rows.length} employees</p></div>
       <h2>This month's payroll</h2>
       <table>${head}${body}${totals}</table>`,
      'This document is system-generated by Augment HR and does not require any signature or verification.',
    );
  };
  return (
    <div className="page-scroll fade-up">
      <WidgetGrid>
        <KPI
          label="Total payroll (gross)"
          value={s ? big(s.totalGross) : '—'}
          icon={Wallet}
          iconTone="primary"
          delay={0}
        />
        <KPI
          label="Total deductions"
          value={s ? big(s.totalDeductions) : '—'}
          icon={ArrowRight}
          iconTone="warning"
          delay={60}
        />
        <KPI
          label="Income tax"
          value={s ? big(s.totalTax) : '—'}
          icon={FileText}
          iconTone="error"
          delay={120}
        />
        <KPI
          label="Net payout"
          value={s ? big(s.totalNet) : '—'}
          icon={Check}
          iconTone="success"
          delay={180}
        />
      </WidgetGrid>
      {data?.rateWarnings?.length > 0 && (
        <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: AG.warningLight || '#FEF7EC', color: AG.warning || '#B54708', fontSize: 12.5 }}>
          No exchange rate to {displayCurrency} for: {data.rateWarnings.join(', ')} — those rows are shown in their native currency and are excluded from the totals above.
        </div>
      )}
      <DataGridCard
        fillHeight={false}
        title="Payroll — this month"
        headerRight={
          <div className="flex items-center gap-2">
            <select
              className="ag-input"
              style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
              value={convertTo}
              onChange={(e) => setConvertTo(e.target.value)}
              title="Consolidate payroll into a single reporting currency"
            >
              {FX_TARGETS.map((c) => <option key={c || 'native'} value={c}>{c ? `View in ${c}` : 'Native currency'}</option>)}
            </select>
            <OutlineBtn icon={Download} onClick={exportAll}>
              Export
            </OutlineBtn>
          </div>
        }
      >
        <AppDataGrid
          rows={rows}
          columns={columns}
          autoHeight
          fillHeight={false}
          hideFooter={rows.length <= 10}
          cursor="default"
        />
      </DataGridCard>
      {structFor && (
        <Dialog open onClose={() => setStructFor(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
              Salary structure · {structFor.name}
            </Typography>
            <SalaryStructure employeeId={structFor.id} editable showToast={showToast} />
            <Box sx={{ mt: 2 }}>
              <AdjustmentsCard employeeId={structFor.id} showToast={showToast} />
            </Box>
            <Box sx={{ mt: 2 }}>
              <OvertimeCard employeeId={structFor.id} />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SettlementCard employeeId={structFor.id} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <OutlineBtn onClick={() => setStructFor(null)}>Close</OutlineBtn>
            </Box>
          </Box>
        </Dialog>
      )}
    </div>
  );
}

// View-only popup listing the progressive income-tax slabs.
function TaxSlabsDialog({ slabs = [], open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          Income tax slabs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Progressive annual salaried slabs · view only
        </Typography>
        <Stack spacing={1}>
          {slabs.map((sl, i) => {
            const [range, rate] = sl.split('—').map((x) => x.trim());
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1.1,
                  px: 1.75,
                  borderRadius: 2,
                  bgcolor: AG.n50,
                  border: `1px solid ${AG.divider}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', fontWeight: 500 }}
                >
                  {range}
                </Typography>
                <Pill tone="primary">{rate}</Pill>
              </Box>
            );
          })}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.5 }}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={onClose}
          >
            Close
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

function PayrollTax({ runKey }) {
  const [data, setData] = useState(null);
  const [slabsOpen, setSlabsOpen] = useState(false);
  const downloadOne = (r) => {
    api.trackDownload('DOWNLOAD_TAX_CERTIFICATE', r.name);
    printPayDoc(
      `Income Tax Certificate — ${r.name}`,
      `<div class="hdr"><h1>Augment HR</h1><p>Income Tax Deduction Certificate · Tax Year ${new Date().getFullYear()}</p></div>
       <h2>Employee</h2>
       <table>
         ${docRow('Name', r.name)}
         ${docRow('Department', r.dept)}
       </table>
       <h2>Income tax summary</h2>
       <table>
         ${docRow('Monthly gross salary', fmtPKR(r.monthlyGross))}
         ${docRow('Annual gross salary', fmtPKR(r.annualGross))}
         ${docRow('Annual income tax', fmtPKR(r.annualTax))}
         ${docRow('Monthly income tax', fmtPKR(r.monthlyTax))}
         <tr class="tot"><td class="k">Effective tax rate</td><td class="v">${r.effectiveRate}%</td></tr>
       </table>`,
      `This income tax deduction certificate is system-generated by Augment HR for the tax year ${new Date().getFullYear()}.`,
    );
  };
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Employee',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => <PersonCell name={row.name} />,
      },
      {
        field: 'monthlyGross',
        headerName: 'Monthly gross',
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ value }) => <TextCell value={fmtPKR(value)} />,
      },
      {
        field: 'annualGross',
        headerName: 'Annual gross',
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ value }) => <TextCell value={fmtPKR(value)} />,
      },
      {
        field: 'annualTax',
        headerName: 'Annual tax',
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ value }) => <TextCell value={fmtPKR(value)} />,
      },
      {
        field: 'monthlyTax',
        headerName: 'Monthly tax',
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ value }) => (
          <TextCell value={fmtPKR(value)} fontWeight={600} />
        ),
      },
      {
        field: 'effectiveRate',
        headerName: 'Eff. rate',
        flex: 0.5,
        minWidth: 90,
        renderCell: ({ value }) => <TextCell value={value + '%'} />,
      },
      {
        field: 'report',
        headerName: 'Report',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <button
            onClick={() => downloadOne(row)}
            className="inline-flex items-center gap-1"
            style={{ color: AG.primary, fontWeight: 500, fontSize: 14 }}
          >
            <Download size={14} />
            Download
          </button>
        ),
      },
    ],
    [],
  );
  useEffect(() => {
    let a = true;
    setData(null);
    api
      .payrollTax()
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, [runKey]);
  if (!data)
    return (
      <div className="page-fill flex items-center justify-center">
        <CenterSpinner />
      </div>
    );
  const { rows = [], slabs = [], summary } = data;
  const exportAll = () => {
    api.trackDownload('DOWNLOAD_PAYROLL_EXPORT', `Income tax summary · ${rows.length} employees`);
    const th = (t, right) =>
      `<td style="padding:9px 0 9px 16px;font-weight:600;color:#6C737F;${right ? 'text-align:right' : ''}">${t}</td>`;
    const td = (t, right) =>
      `<td style="padding:8px 0 8px 16px;${right ? 'text-align:right;font-weight:600;color:#111927' : 'color:#111927'}">${t}</td>`;
    const head = `<tr>${th('Employee')}${th('Department')}${th('Annual gross', 1)}${th('Annual tax', 1)}${th('Monthly tax', 1)}${th('Eff. rate', 1)}</tr>`;
    const body = rows
      .map(
        (r) =>
          `<tr>${td(r.name)}${td(r.dept)}${td(fmtPKR(r.annualGross), 1)}${td(fmtPKR(r.annualTax), 1)}${td(fmtPKR(r.monthlyTax), 1)}${td(r.effectiveRate + '%', 1)}</tr>`,
      )
      .join('');
    const totals = `<tr class="tot"><td class="k" style="padding-left:16px">Totals</td><td></td><td></td><td class="v">${fmtPKR(summary?.totalAnnualTax || 0)}</td><td class="v">${fmtPKR(summary?.totalMonthlyTax || 0)}</td><td></td></tr>`;
    printPayDoc(
      'Income Tax Report',
      `<div class="hdr"><h1>Augment HR</h1><p>Income Tax Report · ${rows.length} employees · Tax Year ${new Date().getFullYear()}</p></div>
       <h2>Per-employee income tax</h2>
       <table>${head}${body}${totals}</table>`,
      'This document is system-generated by Augment HR and does not require any signature or verification.',
    );
  };
  return (
    <div className="page-scroll fade-up">
      <WidgetGrid>
        <KPI
          label="Income tax / month"
          value={summary ? fmtM(summary.totalMonthlyTax) : '—'}
          icon={FileText}
          iconTone="error"
          delay={0}
        />
        <KPI
          label="Income tax / year"
          value={summary ? fmtM(summary.totalAnnualTax) : '—'}
          icon={FileText}
          iconTone="warning"
          delay={60}
        />
        <KPI
          label="Taxpayers"
          value={summary ? String(summary.taxpayers) : '—'}
          icon={Users}
          iconTone="info"
          delay={120}
        />
        <KPI
          label="Tax slabs"
          value={String(slabs.length)}
          icon={BarChart3}
          iconTone="primary"
          delay={180}
        />
      </WidgetGrid>
      <div className="page-shrink">
        <OutlineBtn icon={FileText} onClick={() => setSlabsOpen(true)}>
          View tax slabs
        </OutlineBtn>
      </div>
      <TaxSlabsDialog
        slabs={slabs}
        open={slabsOpen}
        onClose={() => setSlabsOpen(false)}
      />
      <DataGridCard
        fillHeight={false}
        title="Income tax — per employee"
        headerRight={
          <OutlineBtn icon={Download} onClick={exportAll}>
            Export all
          </OutlineBtn>
        }
      >
        <AppDataGrid
          rows={rows}
          columns={columns}
          autoHeight
          fillHeight={false}
          hideFooter={rows.length <= 10}
          cursor="default"
        />
      </DataGridCard>
    </div>
  );
}

// Maps the scheme.icon name from the API to a Lucide component.
const SCHEME_ICONS = { Wallet, ShieldCheck, Plus };

function SchemeCard({ scheme }) {
  const Icon = SCHEME_ICONS[scheme.icon] || Wallet;
  const Row = ({ label, value, accent }) => (
    <div
      className="flex items-center justify-between"
      style={{ fontSize: 13, marginTop: 8 }}
    >
      <span style={{ color: AG.n500 }}>{label}</span>
      <span
        style={{
          color: accent ? AG.primary : AG.text,
          fontWeight: accent ? 600 : 500,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
  return (
    <Card style={{ padding: 18, height: '100%' }}>
      <div className="flex items-center gap-3">
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: AG.primaryLightest,
            color: AG.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </div>
        <div>
          <p style={{ fontWeight: 600, color: AG.text }}>{scheme.name}</p>
          <Pill tone={scheme.type === 'Statutory' ? 'info' : 'primary'}>
            {scheme.type}
          </Pill>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: AG.n500, marginTop: 12 }}>
        {scheme.note}
      </p>
      <div
        style={{
          marginTop: 12,
          borderTop: `1px solid ${AG.divider}`,
          paddingTop: 10,
        }}
      >
        <Row label="Basis" value={scheme.basis} />
        <Row label="Employee share" value={scheme.employeeShare} accent />
        <Row label="Employer share" value={scheme.employerShare} accent />
        <Row label="Cap / note" value={scheme.cap} />
      </div>
      <div style={{ marginTop: 14 }}>
        <OutlineBtn icon={Pencil}>Configure</OutlineBtn>
      </div>
    </Card>
  );
}

function ContributionsTable({ rows }) {
  const exportCsv = () => {
    const th = (t, right) =>
      `<td style="padding:9px 0 9px 14px;font-weight:600;color:#6C737F;${right ? 'text-align:right' : ''}">${t}</td>`;
    const td = (t, right) =>
      `<td style="padding:8px 0 8px 14px;color:#111927;${right ? 'text-align:right' : ''}">${t}</td>`;
    const head = `<tr>${th('Employee')}${th('PF (emp)', 1)}${th('PF (employer)', 1)}${th('EOBI', 1)}${th('OPD', 1)}${th('Emp. deduction', 1)}${th('Employer cost', 1)}</tr>`;
    const body = rows
      .map(
        (r) =>
          `<tr>${td(r.name)}${td(fmtPKR(r.pfEmployee), 1)}${td(fmtPKR(r.pfEmployer), 1)}${td(fmtPKR(r.eobiEmployer), 1)}${td(fmtPKR(r.opdEmployer), 1)}${td(fmtPKR(r.employeeTotal), 1)}${td(fmtPKR(r.employerTotal), 1)}</tr>`,
      )
      .join('');
    const empTot = rows.reduce((a, r) => a + r.employeeTotal, 0);
    const emprTot = rows.reduce((a, r) => a + r.employerTotal, 0);
    const totals = `<tr class="tot"><td class="k" style="padding-left:14px">Totals</td><td></td><td></td><td></td><td></td><td class="v">${fmtPKR(empTot)}</td><td class="v">${fmtPKR(emprTot)}</td></tr>`;
    printPayDoc(
      'Benefits & Contributions',
      `<div class="hdr"><h1>Augment HR</h1><p>Benefits &amp; Contributions · ${rows.length} employees</p></div>
       <h2>Per-employee contributions</h2>
       <table>${head}${body}${totals}</table>`,
      'This document is system-generated by Augment HR and does not require any signature or verification.',
    );
  };
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Employee',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => <PersonCell name={row.name} />,
      },
      {
        field: 'pf',
        headerName: 'PF (emp / employer)',
        flex: 1,
        minWidth: 170,
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap>
            {fmtPKR(row.pfEmployee)}{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              / {fmtPKR(row.pfEmployer)}
            </Box>
          </Typography>
        ),
      },
      {
        field: 'eobi',
        headerName: 'EOBI (emp / employer)',
        flex: 1,
        minWidth: 170,
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap>
            {fmtPKR(row.eobiEmployee)}{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              / {fmtPKR(row.eobiEmployer)}
            </Box>
          </Typography>
        ),
      },
      {
        field: 'opdEmployer',
        headerName: 'OPD (employer)',
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ value }) => <TextCell value={fmtPKR(value)} />,
      },
      {
        field: 'employeeTotal',
        headerName: 'Employee deduction',
        flex: 0.9,
        minWidth: 150,
        renderCell: ({ value }) => (
          <TextCell value={fmtPKR(value)} fontWeight={600} />
        ),
      },
      {
        field: 'employerTotal',
        headerName: 'Employer cost',
        flex: 0.9,
        minWidth: 140,
        renderCell: ({ value }) => (
          <TextCell value={fmtPKR(value)} fontWeight={600} />
        ),
      },
    ],
    [],
  );

  return (
    <DataGridCard
      fillHeight={false}
      title={
        <>
          This month&apos;s contributions{' '}
          <Box
            component="span"
            sx={{ fontWeight: 400, color: 'text.secondary', fontSize: 13 }}
          >
            · auto-calculated per employee
          </Box>
        </>
      }
      headerRight={
        <OutlineBtn icon={Download} onClick={exportCsv}>
          Export
        </OutlineBtn>
      }
    >
      <AppDataGrid
        rows={rows}
        columns={columns}
        fillHeight={false}
        autoHeight
        hideFooter={rows.length <= 10}
        cursor="default"
      />
    </DataGridCard>
  );
}

function PayrollBenefits({ runKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let a = true;
    setData(null);
    api
      .payrollBenefits()
      .then((d) => a && setData(d))
      .catch(() => {});
    return () => {
      a = false;
    };
  }, [runKey]);
  if (!data)
    return (
      <div className="page-scroll flex items-center justify-center">
        <CenterSpinner />
      </div>
    );
  const { schemes = [], rows = [], summary } = data;
  return (
    <>
      <div
        className="page-shrink"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: AG.primaryLightest,
          border: `1px solid ${AG.primaryLight}`,
          color: AG.primaryDark,
          borderRadius: 12,
          padding: '12px 16px',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <Sparkles size={16} /> Configured once per scheme · auto-applied every
        payroll run · override per employee before approval.
      </div>
      <WidgetGrid>
        <KPI
          label="Employee contributions"
          value={summary ? fmtM(summary.employeeContrib) : '—'}
          icon={ArrowRight}
          iconTone="info"
          delay={0}
        />
        <KPI
          label="Employer contributions"
          value={summary ? fmtM(summary.employerContrib) : '—'}
          icon={Building2}
          iconTone="warning"
          delay={60}
        />
        <KPI
          label="Active schemes"
          value={summary ? String(summary.activeSchemes) : '—'}
          icon={ShieldCheck}
          iconTone="primary"
          delay={120}
        />
        <KPI
          label="Application"
          value="Automatic"
          icon={Check}
          iconTone="success"
          delay={180}
        />
      </WidgetGrid>
      <Grid
        container
        spacing={2}
        className="page-shrink"
        sx={{ width: '100%', m: 0 }}
      >
        {schemes.map((s) => (
          <Grid
            key={s.code}
            xs={12}
            lg={4}
            sx={{ minWidth: 0, display: 'flex' }}
          >
            <Box sx={{ width: '100%' }}>
              <SchemeCard scheme={s} />
            </Box>
          </Grid>
        ))}
      </Grid>
      <ContributionsTable rows={rows} />
    </>
  );
}

const ORG_DOC_CATS = ['General', 'HR', 'Finance', 'Legal', 'Operations', 'Compliance', 'Other'];
const POLICY_CATS = ['HR', 'Compliance', 'IT', 'Finance', 'Legal', 'Operations', 'General'];

function DocsAdmin({ kind = 'Policy', readOnly = false }) {
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addCat, setAddCat] = useState('General');
  // Files for bulk upload (multi-file picker) — org docs only
  const [addFiles, setAddFiles] = useState([]);
  // Single-file add — policies only
  const [polName, setPolName] = useState('');
  const [polFile, setPolFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Edit title + category
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCat, setEditCat] = useState('');
  const [editErr, setEditErr] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const polFileRef = useRef(null);

  const reload = () => api.policies().then(setItems).catch(() => {});
  useEffect(() => { reload(); }, []);

  const isPolicies = kind === 'Policy';
  const list = items.filter((p) => isPolicies ? (p.kind || 'Policy') === 'Policy' : p.kind === 'Form');

  const handleDownload = (p) => {
    if (p.hasFile || (p.fileUrl && p.fileUrl.startsWith('gcs:'))) {
      api.downloadPolicy(p.id, p.name).catch(() => {});
    } else if (p.fileUrl) {
      window.open(p.fileUrl, '_blank', 'noopener');
    }
  };

  const handleAdd = async () => {
    if (!addFiles.length) { setAddErr('Please select at least one file.'); return; }
    setAdding(true); setAddErr('');
    try {
      const { created, errors } = await api.bulkCreateOrgDocs(addFiles, addCat);
      if (created.length) setItems((prev) => [...prev, ...created]);
      if (errors.length === 0) {
        setAddOpen(false); setAddFiles([]); setAddCat('General');
      } else if (created.length > 0) {
        // Partial: remove the successful files, show which ones failed
        const failedNames = new Set(errors.map((e) => e.name));
        setAddFiles((prev) => prev.filter((f) => failedNames.has(f.name)));
        setAddErr(`${created.length} uploaded. ${errors.length} failed: ` + errors.map((e) => e.name).join(', '));
      } else {
        setAddErr('All uploads failed: ' + errors.map((e) => `${e.name} — ${e.error}`).join('; '));
      }
    } catch (err) { setAddErr(err.message || 'Upload failed'); }
    finally { setAdding(false); }
  };

  const handleAddPolicy = async () => {
    if (!polName.trim()) { setAddErr('Please enter a policy name.'); return; }
    if (!polFile) { setAddErr('Please attach a document.'); return; }
    setAdding(true); setAddErr('');
    try {
      const created = await api.createPolicy(polName.trim(), addCat, polFile);
      setItems((prev) => [...prev, created]);
      setAddOpen(false); setPolName(''); setPolFile(null); setAddCat('HR');
    } catch (err) { setAddErr(err.message || 'Upload failed'); }
    finally { setAdding(false); }
  };

  const openEdit = (p) => {
    setEditTarget(p); setEditName(p.name || ''); setEditCat(p.cat || (isPolicies ? 'HR' : 'General')); setEditErr('');
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) { setEditErr('Please enter a title.'); return; }
    setSaving(true); setEditErr('');
    try {
      const updated = await api.updatePolicy(editTarget.id, { name: editName.trim(), cat: editCat });
      setItems((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setEditTarget(null);
    } catch (err) { setEditErr(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.deletePolicy(target.id);
      setItems((prev) => prev.filter((p) => p.id !== target.id));
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title={isPolicies ? 'Policies' : 'Organization Docs'}
        sub={isPolicies ? 'Published company policies and acknowledgment rates' : 'Company documents and downloadable forms'}
        action={readOnly ? null : (
          <PrimaryBtn icon={Plus} onClick={() => { setAddFiles([]); setPolName(''); setPolFile(null); setAddCat(isPolicies ? 'HR' : 'General'); setAddErr(''); setAddOpen(true); }}>
            {isPolicies ? 'Add Policy' : 'Upload documents'}
          </PrimaryBtn>
        )}
      />

      {list.length === 0 ? (
        <Card>
          <EmptyState icon={FileText} title={`No ${isPolicies ? 'policies' : 'documents'} yet`}
            desc={readOnly ? 'Nothing has been published here yet.' : `Add your first ${isPolicies ? 'policy' : 'document'} with the button above.`}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((p) => (
            <Card key={p.id} hover style={{ padding: 16 }}>
              <div className="flex items-start gap-3">
                <div style={{ width: 40, height: 40, borderRadius: 12, background: AG.n100, color: AG.n500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p style={{ fontWeight: 600, color: AG.text }}>{p.name}</p>
                    <Pill tone="neutral">{p.cat}</Pill>
                  </div>
                  <p style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>Updated {p.updated}</p>
                  <p style={{ fontSize: 12, color: AG.n400, marginTop: 6 }}>{p.hasFile || p.fileUrl ? 'File attached' : 'No file'}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  <button onClick={() => handleDownload(p)} disabled={!p.fileUrl} title={p.fileUrl ? 'Download' : 'No file attached'}
                    className="inline-flex items-center gap-1"
                    style={{ color: p.fileUrl ? AG.primary : AG.n300, fontWeight: 500, fontSize: 13, background: 'none', border: 'none', cursor: p.fileUrl ? 'pointer' : 'default', padding: 4 }}>
                    <Download size={15} /> PDF
                  </button>
                  {!readOnly && (
                    <button onClick={() => openEdit(p)} title="Edit title & category"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.n400, display: 'flex' }}>
                      <Pencil size={15} />
                    </button>
                  )}
                  {!readOnly && (
                    <button onClick={() => setDeleteTarget(p)} title="Delete"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: AG.n400, display: 'flex' }}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add policy dialog (single file → GCS) */}
      {isPolicies && (
        <Dialog open={addOpen} onClose={() => !adding && setAddOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add Policy</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <TextField label="Policy name" size="small" fullWidth value={polName} onChange={(e) => setPolName(e.target.value)} placeholder="e.g. Remote Work Policy" />
            <TextField label="Category" size="small" select fullWidth value={addCat} onChange={(e) => setAddCat(e.target.value)} SelectProps={{ native: true }}>
              {POLICY_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </TextField>
            <input ref={polFileRef} type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
              onChange={(ev) => { const f = (ev.target.files || [])[0]; if (f) setPolFile(f); ev.target.value = ''; }} />
            <button onClick={() => polFileRef.current?.click()}
              style={{ border: `2px dashed ${polFile ? AG.primary : AG.n200}`, borderRadius: 8, padding: '18px 12px', textAlign: 'center', cursor: 'pointer', background: polFile ? AG.primaryLightest : 'transparent', color: polFile ? AG.primary : AG.textSec, fontSize: 13 }}>
              {polFile ? polFile.name : 'Click to select document (PDF, Word, Excel, Image)'}
            </button>
            {addErr && <p style={{ color: AG.error, fontSize: 12, margin: 0 }}>{addErr}</p>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
            <Button onClick={handleAddPolicy} disabled={adding || !polName.trim() || !polFile} variant="contained">
              {adding ? 'Uploading…' : 'Add Policy'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add org doc dialog */}
      {!isPolicies && (
        <Dialog open={addOpen} onClose={() => !adding && setAddOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Upload Organization Documents</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <TextField label="Category (applies to all)" size="small" select fullWidth value={addCat} onChange={(e) => setAddCat(e.target.value)} SelectProps={{ native: true }}>
              {ORG_DOC_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </TextField>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
              onChange={(ev) => {
                const sel = Array.from(ev.target.files || []);
                if (sel.length) setAddFiles((prev) => { const seen = new Set(prev.map((f) => f.name + f.size)); return [...prev, ...sel.filter((f) => !seen.has(f.name + f.size))]; });
                ev.target.value = '';
              }} />
            <button onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${addFiles.length ? AG.primary : AG.n200}`, borderRadius: 8, padding: '18px 12px', textAlign: 'center', cursor: 'pointer', background: addFiles.length ? AG.primaryLightest : 'transparent', color: addFiles.length ? AG.primary : AG.textSec, fontSize: 13 }}>
              {addFiles.length ? `${addFiles.length} file${addFiles.length > 1 ? 's' : ''} selected — click to add more` : 'Click to select files (PDF, Word, Excel, Image)'}
            </button>
            {addFiles.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                {addFiles.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: AG.n50, borderRadius: 6, fontSize: 12 }}>
                    <FileText size={13} style={{ color: AG.primary, flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: AG.text }}>{f.name}</span>
                    <button onClick={() => setAddFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: AG.n400, display: 'flex', flexShrink: 0 }}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {addErr && <p style={{ color: AG.error, fontSize: 12, margin: 0 }}>{addErr}</p>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
            <Button onClick={handleAdd} disabled={adding || !addFiles.length} variant="contained">
              {adding ? 'Uploading…' : `Upload${addFiles.length > 1 ? ` ${addFiles.length} files` : ''}`}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit title + category */}
      <Dialog open={!!editTarget} onClose={() => !saving && setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {isPolicies ? 'Policy' : 'Document'}</DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
          <TextField label="Title" size="small" fullWidth value={editName} onChange={(e) => setEditName(e.target.value)} />
          <TextField label="Category" size="small" select fullWidth value={editCat} onChange={(e) => setEditCat(e.target.value)} SelectProps={{ native: true }}>
            {(isPolicies ? POLICY_CATS : ORG_DOC_CATS).map((c) => <option key={c} value={c}>{c}</option>)}
          </TextField>
          {editErr && <p style={{ color: AG.error, fontSize: 12, margin: 0 }}>{editErr}</p>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveEdit} disabled={saving || !editName.trim()} variant="contained">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, fontSize: 14, color: AG.textSec }}>
            <b style={{ color: AG.text }}>{deleteTarget?.name}</b> will be permanently deleted.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function ReportsAdmin() {
  const [emps, setEmps] = useState(null);
  const [dash, setDash] = useState(null);
  const [pay, setPay] = useState(null);
  const [okrs, setOkrs] = useState(null);
  const [leaves, setLeaves] = useState(null);
  useEffect(() => {
    let a = true;
    Promise.all([
      api.employees(),
      api.dashboard(),
      api.payroll(),
      api.okrsAll(),
      api.leaves(),
    ])
      .then(([e, d, p, o, l]) => {
        if (a) {
          setEmps(e);
          setDash(d);
          setPay(p);
          setOkrs(o);
          setLeaves(l);
        }
      })
      .catch(() => {});
    return () => {
      a = false;
    };
  }, []);
  const ready = emps && dash && pay && okrs && leaves;

  const groupCount = (arr, key) => {
    const m = {};
    arr.forEach((x) => {
      m[x[key]] = (m[x[key]] || 0) + 1;
    });
    return m;
  };

  const reports = !ready
    ? []
    : [
        {
          name: 'Headcount by department',
          icon: Users,
          desc: 'Active directory by department',
          build: () => {
            const g = groupCount(emps, 'dept');
            const rows = Object.entries(g)
              .sort((a, b) => b[1] - a[1])
              .map(([d, n]) => [d, String(n)]);
            return [
              'Headcount by department',
              `${emps.length} employees`,
              ['Department', 'Headcount'],
              rows,
              ['Total', String(emps.length)],
            ];
          },
        },
        {
          name: 'Employees by status',
          icon: TrendingUp,
          desc: 'Active / probation / notice / inactive',
          build: () => {
            const rows = (dash.statusBreakdown || []).map((s) => [
              s.name,
              String(s.value),
            ]);
            return [
              'Employees by status',
              'Live workforce status',
              ['Status', 'Count'],
              rows,
              ['Total', String(emps.length)],
            ];
          },
        },
        {
          name: 'Attendance summary',
          icon: Clock,
          desc: 'Present / late / absent (this week)',
          build: () => {
            const w = dash.weekAtt || [];
            const s = (k) => w.reduce((a, x) => a + (x[k] || 0), 0);
            const rows = [
              ['Present', String(s('present'))],
              ['Late', String(s('late'))],
              ['Absent', String(s('absent'))],
            ];
            return [
              'Attendance summary',
              'This week',
              ['Status', 'Records'],
              rows,
              ['Total', String(s('present') + s('late') + s('absent'))],
            ];
          },
        },
        {
          name: 'Leave requests by type',
          icon: CalendarOff,
          desc: 'Requests grouped by leave type',
          build: () => {
            const g = groupCount(leaves, 'type');
            const rows = Object.entries(g).map(([t, n]) => [t, String(n)]);
            return [
              'Leave requests by type',
              `${leaves.length} requests`,
              ['Leave type', 'Requests'],
              rows,
              ['Total', String(leaves.length)],
            ];
          },
        },
        {
          name: 'Recruitment funnel',
          icon: Briefcase,
          desc: 'Candidates by stage',
          build: () => {
            const rows = (dash.funnel || []).map((f) => [f.stage, String(f.v)]);
            return [
              'Recruitment funnel',
              'Active pipeline this cycle',
              ['Stage', 'Candidates'],
              rows,
              null,
            ];
          },
        },
        {
          name: 'Payroll cost by department',
          icon: Wallet,
          desc: 'Monthly gross grouped by department',
          build: () => {
            const m = {};
            pay.rows.forEach((r) => {
              m[r.dept] = (m[r.dept] || 0) + r.gross;
            });
            const rows = Object.entries(m)
              .sort((a, b) => b[1] - a[1])
              .map(([d, v]) => [d, fmtPKR(v)]);
            return [
              'Payroll cost by department',
              'Monthly gross payroll',
              ['Department', 'Gross payroll'],
              rows,
              ['Total', fmtPKR(pay.summary.totalGross)],
            ];
          },
        },
        {
          name: 'Performance distribution',
          icon: Star,
          desc: 'Manager rating distribution',
          build: () => {
            const B = [
              'Exceptional',
              'Exceeds',
              'Meets',
              'Below',
              'Unsatisfactory',
            ];
            const rows = B.map((r) => [
              r,
              String(okrs.filter((o) => o.managerRating === r).length),
            ]);
            const reviewed = okrs.filter((o) => o.status === 'Reviewed').length;
            return [
              'Performance distribution',
              `${reviewed} reviewed objectives`,
              ['Rating', 'Count'],
              rows,
              ['Reviewed', String(reviewed)],
            ];
          },
        },
      ];

  const run = (rep) => {
    api.trackDownload('DOWNLOAD_REPORT', rep.name);
    const [title, subtitle, headers, rows, totals] = rep.build();
    printReport(title, subtitle, headers, rows, totals);
  };

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Reports & analytics"
        sub="Live reports from your data — download as PDF"
      />
      {!ready ? (
        <div
          className="flex items-center justify-center"
          style={{ minHeight: 200 }}
        >
          <CenterSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {reports.map((r) => (
            <Card
              key={r.name}
              hover
              style={{ padding: 16, cursor: 'pointer' }}
              onClick={() => run(r)}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: AG.primaryLightest,
                  color: AG.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 12,
                }}
              >
                <r.icon size={18} />
              </div>
              <p style={{ fontWeight: 600, color: AG.text, fontSize: 14 }}>
                {r.name}
              </p>
              <p style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>
                {r.desc}
              </p>
              <div
                className="flex items-center gap-1"
                style={{
                  color: AG.primary,
                  fontWeight: 500,
                  marginTop: 12,
                  fontSize: 12,
                }}
              >
                <Download size={13} />
                Download PDF
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsAdmin() {
  const [ai, setAi] = useState({
    chatbot: true,
    resume: true,
    insights: true,
    bias: false,
  });
  const cards = [
    {
      title: 'Company profile',
      icon: Building2,
      desc: 'Name, logo, timezone, currency, working days',
    },
    {
      title: 'Roles & permissions',
      icon: ShieldCheck,
      desc: '2 roles configured · RBAC enabled',
    },
    {
      title: 'Leave types',
      icon: CalendarOff,
      desc: '4 leave types · accrual & carry-forward rules',
    },
    {
      title: 'Email templates',
      icon: Mail,
      desc: '12 templates for approvals, offers, alerts',
    },
  ];
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle
        title="Settings"
        sub="Configure your Augment HR workspace"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cards.map((c) => (
          <Card
            key={c.title}
            hover
            style={{ padding: 16 }}
            className="flex items-start gap-3"
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: AG.n100,
                color: AG.n500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <c.icon size={18} />
            </div>
            <div className="flex-1">
              <p style={{ fontWeight: 600, color: AG.text }}>{c.title}</p>
              <p style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>
                {c.desc}
              </p>
            </div>
            <ChevronRight size={16} style={{ color: AG.n300, marginTop: 8 }} />
          </Card>
        ))}
      </div>
      <Card style={{ padding: 24 }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
          <Sparkles size={18} style={{ color: AG.purple }} />
          <h3 style={{ fontWeight: 600, color: AG.text }}>AI features</h3>
        </div>
        <div>
          {[
            { k: 'chatbot', l: 'HR AI chatbot (employee self-service)' },
            { k: 'resume', l: 'Resume parser & candidate scoring' },
            { k: 'insights', l: 'Weekly attendance & workforce insights' },
            { k: 'bias', l: 'Performance review bias detection' },
          ].map((o, i) => (
            <div
              key={o.k}
              className="flex items-center justify-between"
              style={{
                padding: '10px 0',
                borderBottom: i < 3 ? `1px solid ${AG.divider}` : 'none',
              }}
            >
              <span style={{ fontSize: 14, color: AG.n700 }}>{o.l}</span>
              <button
                onClick={() => setAi((a) => ({ ...a, [o.k]: !a[o.k] }))}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 99,
                  position: 'relative',
                  background: ai[o.k] ? AG.primary : AG.n400,
                  transition: 'background .15s',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: ai[o.k] ? 22 : 2,
                    width: 20,
                    height: 20,
                    background: '#fff',
                    borderRadius: 99,
                    transition: 'left .15s',
                    boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                  }}
                />
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export {
  AdminViews,
  EmployeeDetail,
  CandidateDetail,
  JobDetail,
  DocsAdmin,
  TaxSlabsDialog,
};
